import React, { useEffect, useState } from 'react';
import './App.css';
import { exportSkillCertPdf, sanitizeFileName } from './pdfExport';
import skillcertLogo from './assets/skillcert-logo.png';

// Configuração
const resolveFunctionsUrl = () => {
  const configured = process.env.REACT_APP_FUNCTIONS_URL;
  const fallback = '/.netlify/functions';

  if (!configured) return fallback;

  try {
    const parsed = new URL(configured, window.location.origin);
    if (parsed.origin !== window.location.origin) return fallback;
    return `${parsed.origin}${parsed.pathname}`.replace(/\/$/, '');
  } catch {
    return fallback;
  }
};

const FUNCTIONS_URL = resolveFunctionsUrl();
const ENABLE_LEGACY_COMPARATIVE_PII = process.env.REACT_APP_ENABLE_LEGACY_COMPARATIVE_PII === 'true';
const PASSWORD_AUTH_ENABLED = process.env.REACT_APP_AUTH_PASSWORD_ENABLED !== 'false';
const INVITES_ENABLED = process.env.REACT_APP_INVITES_ENABLED === 'true';
const CULTURE_DOC_MAX_BYTES_ENV = Number(process.env.REACT_APP_CULTURE_DOC_MAX_BYTES || 4 * 1024 * 1024);
const CULTURE_DOC_MAX_BYTES = Number.isFinite(CULTURE_DOC_MAX_BYTES_ENV) && CULTURE_DOC_MAX_BYTES_ENV > 0
  ? Math.floor(CULTURE_DOC_MAX_BYTES_ENV)
  : 4 * 1024 * 1024;
const CULTURE_DOC_MAX_MB = Number((CULTURE_DOC_MAX_BYTES / (1024 * 1024)).toFixed(1));
const isValidPrincipleLevel = (value) => [1, 2, 3, 4].includes(Number(value));
const LAST_CONTEXT_STORAGE_PREFIX = 'skillcert:last_context:';

// Tenant e usuários são carregados dinamicamente via autenticação por senha/contexto.

// 8 Princípios de Trabalho
// Princípios de Trabalho v2 — 6 PTs
// Ordenação intercalada por bloco para evitar contaminação entre respostas adjacentes:
// PT1 (decisão) → PT2 (ambiente) → PT3 (social) → PT4 (ambiente) → PT5 (social) → PT6 (decisão)
const PRINCIPIOS_TRABALHO = [
  {
    id: 'PT1',
    titulo: 'Ritmo de decisão',
    esquerda: 'Decisões importantes esperam pelo nível de análise e fundamento necessários — mesmo que isso leve tempo.',
    direita: 'Decisões importantes são tomadas com as informações disponíveis no momento — ajustes acontecem no caminho.'
  },
  {
    id: 'PT2',
    titulo: 'Estabilidade versus mudança',
    esquerda: 'Diretrizes e prioridades tendem a ser estáveis — mudanças de direção são exceção e exigem revisão estratégica.',
    direita: 'Diretrizes e prioridades mudam com frequência — adaptar-se sempre a novas direções é parte normal do trabalho.'
  },
  {
    id: 'PT3',
    titulo: 'Tensão interpessoal e conflito',
    esquerda: 'Divergências são resolvidas de forma contida e diplomática — o debate aberto entre pessoas é evitado para prevenir desgastes desnecessários ao ambiente.',
    direita: 'Divergências são trabalhadas de forma explícita — o debate aberto entre pessoas é parte normal do processo.'
  },
  {
    id: 'PT4',
    titulo: 'Intensidade do ambiente',
    esquerda: 'O ritmo de trabalho é pautado pela previsibilidade e sustentabilidade — picos de demanda são exceções planejadas.',
    direita: 'O ritmo de trabalho é de alta intensidade e demanda contínua — a capacidade de entrega sob pressão é um pilar estrutural do ambiente.'
  },
  {
    id: 'PT5',
    titulo: 'Trabalho individual versus coletivo',
    esquerda: 'O foco está na autonomia individual: cada pessoa é avaliada, reconhecida e cobrada principalmente pela sua própria entrega.',
    direita: 'A entrega coletiva tem peso maior: quem contribui para elevar o time e servir de referência é mais valorizado, mesmo sem resultados individuais brilhantes.'
  },
  {
    id: 'PT6',
    titulo: 'Relação com desvio e erro',
    esquerda: 'Seguir processos, mitigar riscos e evitar erros é prioritário — desvios podem ter custo reputacional relevante, independentemente do resultado.',
    direita: 'Resultado e aprendizado prevalecem sobre aderência a processos — atalhos justificáveis e erros são aceitáveis quando geram valor.'
  }
];

// 6 Dimensões da Posição — v3 (posicoes_v3.json)
const DIMENSOES_POSICAO = {
  'Habilidades Analíticas': [
    { id: 'HA1', desc: 'Perfil operacional. Trabalho estruturado, com processos e scripts definidos. Raciocínio analítico simples.' },
    { id: 'HA2', desc: 'Raciocínio crítico requerido desde o início. Problemas moderadamente complexos, sem modelagem matemática avançada.' },
    { id: 'HA3', desc: 'Desafios complexos desde o início, envolvendo modelagens matemáticas avançadas.' }
  ],
  'Tomada de Decisão': [
    { id: 'TD1', desc: 'Decisões baseadas em regras, padrões ou procedimentos conhecidos.' },
    { id: 'TD2', desc: 'Decisões com alguma autonomia sobre o "como", dentro de parâmetros definidos.' },
    { id: 'TD3', desc: 'Decisões em situações novas ou ambíguas, com informação incompleta, de médio impacto.' },
    { id: 'TD4', desc: 'Decisões estratégicas em alta incerteza, sob pressão, com impacto amplo e irreversibilidade relevante.' }
  ],
  'Agilidade e Aprendizado': [
    { id: 'AA1', desc: 'Conhecimento atual é suficiente. Domínio estável, atualizações periódicas e previsíveis.' },
    { id: 'AA2', desc: 'Aprendizado contínuo em domínios em evolução, mas dentro de áreas adjacentes ao que já domina.' },
    { id: 'AA3', desc: 'Demanda por aprendizado de conhecimentos novos e complexos, com alta velocidade e alta consistência.' }
  ],
  'Liderança': [
    { id: 'LD1', desc: 'Posição não envolve liderança no presente e nem no futuro visível.' },
    { id: 'LD2', desc: 'Posição envolve (ou poderá envolver em breve) liderança de time.' },
    { id: 'LD3', desc: 'Posição envolve (ou poderá envolver em breve) liderança de área.' },
    { id: 'LD4', desc: 'Posição envolve (ou poderá envolver em breve) liderança de unidade de negócio ou do negócio.' }
  ],
  'Orientação para Cliente': [
    { id: 'OC1', desc: 'Sem interação direta com clientes ou usuários externos.' },
    { id: 'OC2', desc: 'Contato com clientes é pontual e de baixa complexidade relacional.' },
    { id: 'OC3', desc: 'Interações frequentes com clientes que exigem habilidades relacionais consistentes e capacidade de gerenciar expectativas.' },
    { id: 'OC4', desc: 'Relacionamentos críticos com clientes de alto valor ou alta complexidade, onde o resultado depende fortemente da qualidade da relação.' }
  ],
  'Influência e Gestão de Stakeholders': [
    { id: 'IN1', desc: 'Execução técnica individual, sem necessidade de persuadir ou articular posições junto a outros.' },
    { id: 'IN2', desc: 'Persuasão de pares e negociação de recursos em contextos de baixa resistência.' },
    { id: 'IN3', desc: 'Convencimento de stakeholders com interesses divergentes. Exige articulação política e construção de consenso.' },
    { id: 'IN4', desc: 'Negociação de alianças críticas, representação institucional ou capacidade de mudar visões de mundo em fóruns de alto impacto.' }
  ]
};

const UI_TEXT = {
  'pt-BR': {
    appTitle: 'Ferramenta de Apoio à Decisão em RH',
    loginSubtitle: 'Digite suas credenciais de acesso',
    emailPlaceholder: 'Seu email',
    passwordPlaceholder: 'Sua senha',
    tokenPlaceholder: 'Código de acesso da empresa',
    forgotPassword: 'Esqueci minha senha',
    manageUsersHeaderButton: '👥 Usuários',
    creditsHeaderButton: '💳 Créditos',
    helpHeaderButton: '❓ Ajuda',
    faqTitle: 'FAQ e Orientações',
    faqSubtitle: 'Guia rápido para interpretar resultados dos testes SkillCert no contexto real da vaga e da cultura da empresa.',
    faqBack: '← Voltar ao app',
    faqExpandAll: 'Expandir tudo',
    faqCollapseAll: 'Recolher tudo',
    faqWhatToDoPrefix: 'O que fazer:',
    userManagementTitle: 'Gestão de usuários',
    userManagementSubtitle: 'Gerencie usuários do tenant com atualização de papel e status.',
    userManagementBack: '← Voltar ao app',
    userManagementRefresh: 'Atualizar lista',
    userManagementInviteDisabledBanner: 'Convites por email estão temporariamente desativados nesta tela.',
    userManagementProvisionTitle: 'Cadastrar usuário sem envio de email',
    userManagementProvisionSubmit: 'Cadastrar usuário',
    userManagementProvisionSubmitting: 'Cadastrando usuário…',
    userManagementProvisionSuccess: 'Usuário {{email}} provisionado com sucesso.',
    userManagementInviteTitle: 'Convidar usuário',
    userManagementInviteName: 'Nome (opcional)',
    userManagementInviteEmail: 'Email',
    userManagementInviteRole: 'Papel',
    userManagementInviteSubmit: 'Enviar convite',
    userManagementInviteSubmitting: 'Enviando convite…',
    userManagementListTitle: 'Usuários do tenant',
    userManagementLoading: 'Carregando usuários…',
    userManagementEmpty: 'Nenhum usuário encontrado neste tenant.',
    userManagementRole: 'Papel',
    userManagementStatus: 'Status',
    userManagementResend: 'Reenviar acesso',
    userManagementSave: 'Salvar ajuste',
    userManagementSaving: 'Salvando…',
    userManagementSelfTag: 'Você',
    userManagementNoChanges: 'Sem alterações para salvar.',
    roleLabelMasterAdmin: 'Master admin',
    roleLabelAdminClient: 'Admin do tenant',
    roleLabelUserClient: 'Usuário do tenant',
    statusLabelInvited: 'Convidado',
    statusLabelActive: 'Ativo',
    statusLabelInactive: 'Inativo',
    userManagementInviteSuccess: 'Convite enviado com sucesso para {{email}}.',
    userManagementResendInviteSuccess: 'Acesso reenviado com sucesso para {{email}}.',
    userManagementResendResetSuccess: 'Email de recuperação enviado para {{email}}.',
    userManagementUpdateSuccess: 'Acesso de {{email}} atualizado com sucesso.',
    globalTenantsTitle: 'Gestão de tenants',
    globalTenantsSubtitle: 'Crie e administre tenants ativos sem SQL manual.',
    globalTenantsRefresh: 'Atualizar tenants',
    globalTenantsCreateTitle: 'Criar tenant',
    globalTenantsName: 'Nome do tenant',
    globalTenantsLanguage: 'Idioma',
    globalTenantsStatus: 'Ativo',
    globalTenantsToken: 'Access token',
    globalTenantsGenerateToken: 'Gerar token',
    globalTenantsCreateSubmit: 'Criar tenant',
    globalTenantsCreateSubmitting: 'Criando tenant…',
    globalTenantsListTitle: 'Tenants ativos',
    globalTenantsEmpty: 'Nenhum tenant ativo encontrado.',
    globalTenantsManageUsers: 'Gerenciar usuários',
    globalTenantsEdit: 'Editar tenant',
    globalTenantsSaveEdit: 'Salvar tenant',
    globalTenantsSavingEdit: 'Salvando…',
    globalTenantsActive: 'Ativo',
    globalTenantsInactive: 'Inativo',
    globalTenantsCreatedAt: 'Criado em',
    globalTenantsCreateSuccess: 'Tenant "{{name}}" criado com sucesso.',
    globalTenantsUpdateSuccess: 'Tenant "{{name}}" atualizado com sucesso.',
    globalUsersForTenant: 'Usuários do tenant: {{name}}',
    globalUsersBack: '← Voltar para Tenants',
    creditsTitle: 'Gestão de créditos',
    creditsSubtitle: 'Saldo e histórico de consumo deste tenant.',
    creditsBack: '← Voltar ao app',
    creditsRefresh: 'Atualizar',
    creditsCurrentBalance: 'Saldo disponível',
    creditsUsedTotal: 'Créditos consumidos',
    creditsPolicy: 'Regra atual: individual={{individual}} · comparativa={{comparative}}',
    creditsHistoryTitle: 'Histórico de créditos',
    creditsHistoryEmpty: 'Nenhuma movimentação de crédito encontrada.',
    creditsHistoryDate: 'Data',
    creditsHistoryOperation: 'Operação',
    creditsHistoryAmount: 'Quantidade',
    creditsHistoryBalanceAfter: 'Saldo após',
    creditsHistoryPerformedBy: 'Responsável',
    creditsHistoryAnalysis: 'Análise',
    creditsUnknownUser: 'Usuário não identificado',
    creditsUnknownAnalysis: 'Sem análise vinculada',
    creditsOperationUse: 'Consumo',
    creditsOperationAdd: 'Adição',
    creditsOperationRefund: 'Estorno',
    creditsOperationAdjust: 'Ajuste manual',
    creditsAdjustTitle: 'Ajustar créditos (master_admin)',
    creditsAdjustAmountLabel: 'Delta de créditos (inteiro)',
    creditsAdjustAmountPlaceholder: 'Ex.: 50 ou -10',
    creditsAdjustReasonLabel: 'Motivo do ajuste',
    creditsAdjustReasonPlaceholder: 'Ex.: carga contratada / correção operacional',
    creditsAdjustSubmit: 'Aplicar ajuste',
    creditsAdjustSubmitting: 'Aplicando...',
    creditsAdjustSuccess: 'Ajuste aplicado com sucesso.',
    errCreditsLoad: 'Não foi possível carregar créditos: {{error}}',
    errCreditsAdjust: 'Não foi possível ajustar créditos: {{error}}',
    errCreditsAdjustInvalidAmount: 'Informe um inteiro diferente de zero.',
    selectContextTitle: 'Escolha onde entrar',
    selectContextSubtitle: 'Este usuário tem acesso a mais de um contexto. Selecione o tenant ou a área administrativa.',
    selectContextLabel: 'Contexto de acesso',
    enterSelectedContext: 'Entrar neste contexto',
    backToLogin: 'Voltar ao login',
    globalAdminArea: 'Área Administrativa Global',
    switchTenant: 'Trocar tenant',
    switchTenantConfirm: 'Deseja trocar de tenant agora? Dados não salvos serão perdidos.',
    globalAdminWelcomeTitle: 'Administração Global',
    globalAdminWelcomeBody: 'Você está no modo master admin. Selecione abaixo o tenant que deseja visualizar.',
    enterTenant: 'Entrar no tenant',
    errContextSelection: 'Não foi possível selecionar o contexto: {{error}}',
    passwordResetSent: 'Se o email existir, enviaremos instruções de recuperação.',
    checking: 'Verificando…',
    enter: 'Entrar',
    principlesOrg: 'Princípios de Trabalho da Organização',
    principlesView: 'Visualização dos princípios configurados',
    principlesSetup: 'Configure uma única vez — este é o contexto cultural da empresa',
    principlesPendingAdmin: 'Os Princípios de Trabalho ainda não foram configurados por um admin do tenant. Solicite a configuração para continuar.',
    edit: '✏️ Editar',
    leftPole: '← POLO ESQUERDO',
    rightPole: 'POLO DIREITO →',
    strong: 'Forte',
    moderate: 'Moderado',
    cultureComplementTitle: 'Complementar com documento de cultura',
    cultureComplementBody: 'Envie um manual de valores, código de conduta ou qualquer documento que descreva como a empresa funciona na prática. A IA inferirá os níveis e identificará divergências.',
    analyzingDocument: 'Analisando documento…',
    uploadCultureDoc: '+ Enviar documento de cultura',
    cultureDocDone: '✓ Documento processado. Contexto cultural adicionado às análises.',
    cultureDocNoAlerts: 'Nenhuma divergência forte ou trade-off acionável foi identificado no documento de cultura enviado.',
    cultureDocReviewAlerts: 'Alertas do documento de cultura enviado: {{strong}} divergências fortes e {{tradeoff}} trade-offs.',
    cultureDocSameLevelTradeoffOnly: 'Trade-off identificado em {{count}} princípio{{plural}} sem sugestão de ajuste de nível.',
    continueToPosition: 'Continuar para Posição →',
    positionDefinition: 'Definição da Posição',
    positionView: 'Visualização da posição configurada',
    positionSubtitle: 'Descreva a exigência real da vaga.',
    positionGuidance: 'Marque a menor exigência que ainda sustenta boa performance de forma consistente.',
    positionGuidanceExpandedIntro: 'Use estas regras para calibrar sem inflar a vaga:',
    positionGuidanceTieBreaker: 'Em dúvida entre duas opções, escolha a menor que ainda funcionaria bem nos primeiros 90 dias.',
    positionGuidanceBias: 'A primeira opção não reduz a importância do cargo; ela descreve o tipo de demanda desta dimensão.',
    positionGuidanceExceptions: 'Se algo acontece só em exceções, não use isso para inflar a exigência.',
    positionGuidanceCheckQuestion: 'Pergunta de checagem: sem esse patamar, uma pessoa forte no restante entregaria bem em 90 dias?',
    positionGuidanceReadMore: 'Ver orientação completa',
    positionGuidanceReadLess: 'Ocultar orientação',
    roleName: 'Nome do Cargo *',
    rolePlaceholder: 'Ex: CFO, Analista de Negócios, Gerente Comercial...',
    selectLevel: 'Selecione a opção que melhor representa a rotina real da vaga',
    positionDimensionRequirement: 'Exigência da função nesta dimensão',
    positionOptionHint: 'Selecione uma opção para ver a explicação prática.',
    positionMeaningLabel: 'O que essa escolha significa',
    calibrationAlertLabel: 'Alerta de calibragem:',
    commonMistakeLabel: 'Erro comum:',
    howToCalibrateLabel: 'Como calibrar:',
    whenInDoubtLabel: 'Na dúvida:',
    positionConfirmCalibration: 'Confirmar calibragem desta dimensão',
    positionCalibrationConfirmed: 'Calibragem confirmada.',
    positionEditChoiceTooltip: 'Clique para editar esta dimensão',
    positionEditChoiceTitle: 'Editar escolha da dimensão',
    positionEditChoiceConfirm: 'Deseja editar "{{dimension}}"? Será necessário confirmar novamente a escolha.',
    positionChecklistTitle: 'Pendências para continuar:',
    positionPendingRole: 'Informar o nome do cargo.',
    positionPendingOneDimension: 'Selecionar 1 dimensão.',
    positionPendingManyDimensions: 'Selecionar {{count}} dimensões.',
    positionPendingOneCritical: 'Confirmar escolha em 1 dimensão.',
    positionPendingManyCritical: 'Confirmar escolha em {{count}} dimensões.',
    roleContext: 'Contexto adicional da vaga (opcional)',
    roleContextPlaceholder: 'Descreva responsabilidades específicas, desafios do momento, contexto da equipe ou qualquer informação relevante para a análise...',
    roleContextHint: 'Este campo enriquece a análise — quanto mais contexto, mais precisa a interpretação.',
    chooseFile: 'Escolher arquivo',
    noFileChosen: 'Nenhum arquivo escolhido',
    back: '← Voltar',
    continueToUpload: 'Continuar para Upload →',
    uploadReports: 'Upload de Relatórios',
    uploadSubtitle: 'Adicione de 1 a 5 candidatos — cada upload gera a análise individual automaticamente',
    cognitivePdf: 'PDF — Relatório Cognitivo *',
    candidateNameAuto: 'O nome do candidato será extraído automaticamente do arquivo.',
    socioPdf: 'PDF — Relatório Socioemocional *',
    addCandidate: '+ Adicionar Candidato',
    processingCandidate: 'Processando {{name}}…',
    uploadProcessingHint: 'PDFs processados. A análise está sendo gerada em background — o status será atualizado automaticamente em alguns instantes.',
    candidatesAdded: 'Candidatos adicionados ({{count}}/5)',
    candidateReady: '✓ Análise pronta',
    candidateTimeout: '⚠ Tempo esgotado — recarregue',
    candidateAnalyzing: '⏳ Analisando...',
    deleteCandidateTitle: 'Excluir candidato',
    continueToAnalysis: 'Continuar para Análise →',
    analysisTitle: 'Análise',
    analysisSubtitlePlural: 'Análises individuais prontas — escolha o modo de visualização',
    analysisSubtitleSingle: 'Análise individual pronta',
    individualMode: 'Individual',
    comparativeMode: 'Comparativa',
    generateComparative: 'Gerar Análise Comparativa',
    viewIndividual: 'Ver Análise Individual',
    generatingAnalysis: 'Gerando análise…',
    waitMoments: 'Aguarde alguns instantes',
    generatingComparative: 'Gerando análise comparativa…',
    mayTakeMinutes: 'Isso pode levar alguns minutos',
    comparativeAnalysisTitle: 'Análise Comparativa',
    exportPdf: '📄 Exportar PDF',
    exportingPdf: 'Exportando PDF…',
    analysisSaved: '✓ Análise salva',
    saveAnalysis: '💾 Salvar análise',
    saving: 'Salvando…',
    decisorySynthesis: 'Síntese Decisória',
    analyticalSynthesis: 'Síntese Analítica',
    projectionTab: '🔮 Projeção Narrativa',
    generateProjectionNow: 'Gerar agora',
    projectionPreparing: 'Preparando projeção narrativa…',
    projectionPendingHint: 'A projeção é preparada em segundo plano quando a análise individual fica pronta.',
    saveAnalysesBatch: '💾 Salvar análises ({{count}})',
    exportPdfBatch: '📄 Exportar PDF ({{count}})',
    doComparative: 'Fazer análise comparativa →',
    viewComparative: 'Ver análise comparativa →',
    viewRealScenario: 'Ver Projeção Narrativa',
    viewComparisonRealScenario: 'Ver Projeção Narrativa Comparativa',
    generatingProjection: 'Gerando projeção narrativa…',
    narrativeProjectionTitle: 'Projeção Narrativa',
    projectionDisclaimerTitle: 'Como ler esta projeção',
    projectionDisclaimerBody: 'Este texto é uma tangibilização narrativa baseada nos dados da análise, no contexto da vaga e nos princípios da empresa. A linguagem é propositalmente mais viva e prospectiva. Não é previsão determinística.',
    comparativeLoadingCta: '⏳ Gerando análise comparativa...',
    viewIndividualsBack: '← Ver análises individuais',
    library: '📚 Biblioteca',
    analysisLibrary: 'Biblioteca de Análises',
    loadingSavedAnalyses: 'Carregando análises salvas…',
    noSavedAnalyses: 'Nenhuma análise salva ainda',
    noSavedAnalysesHint: 'Análises salvas aparecerão aqui, organizadas por posição.',
    analysesSavedCount: '{{count}} análise{{plural}} salva{{plural}}',
    reusePosition: '↺ Reaproveitar posição',
    reusePositionTitle: 'Usar as configurações desta posição para uma nova análise',
    comparativeShort: '⚖ Comparativa',
    individualShort: '👤 Individual',
    footerTitle: 'SkillCert · Ferramenta de Apoio à Decisão em RH',
    footerCandidates: '{{count}} candidato{{plural}} · {{cargo}}',
    errSelectBothPdfs: 'Selecione os dois arquivos PDF para continuar',
    errCandidateStartFail: 'Candidato "{{name}}" adicionado, mas falha ao iniciar análise: {{error}}.',
    errCandidateProcessingFail: 'A análise de "{{name}}" falhou durante o processamento.',
    errConnection: 'Erro de conexão: {{error}}',
    errDeleteCandidate: 'Erro ao excluir {{name}}: {{error}}',
    errDeleteCandidateConnection: 'Erro de conexão ao excluir {{name}}. Tente novamente.',
    errMissingCompleteAnalyses: 'Candidatos sem análise completa: {{names}}',
    errComparativeProcessing: 'A análise comparativa falhou durante o processamento.',
    errGeneric: 'Erro: {{error}}',
    errAddAtLeastOne: 'Adicione pelo menos 1 candidato',
    errSavePrinciples: 'Erro ao salvar princípios: {{error}}',
    errSavePosition: 'Erro ao salvar posição: {{error}}',
    errSaveAnalysis: 'Erro ao salvar análise: {{error}}',
    errSaveAnalysisConnection: 'Erro de conexão ao salvar análise: {{error}}',
    errUnsupportedFormat: 'Formato não suportado. Envie um arquivo PDF ou texto (.txt, .md).',
    errCultureDocTooLarge: 'Arquivo muito grande. O limite é {{maxMb}} MB.',
    errProcessDocument: 'Erro ao processar documento: {{error}}',
    errProcessDocumentConnection: 'Erro de conexão ao processar documento: {{error}}',
    errUnexpectedServerResponse: 'Resposta inesperada do servidor.',
    errExportPdf: 'Erro ao exportar PDF: {{error}}',
    errNoIndividualForExport: 'Nenhuma análise individual disponível para exportação.',
    errInvalidCreds: 'Credenciais inválidas. Verifique e tente novamente.',
    errPasswordLoginDisabled: 'Login com senha está desabilitado.',
    errTenantRequired: 'Este email está em mais de uma empresa. Informe também o código da empresa.',
    errUsersLoad: 'Não foi possível carregar usuários: {{error}}',
    errInviteUser: 'Não foi possível enviar convite: {{error}}',
    errResendUserAccess: 'Não foi possível reenviar acesso: {{error}}',
    errUpdateUserAccess: 'Não foi possível atualizar usuário: {{error}}',
    errInviteEmailRequired: 'Informe o email para convidar o usuário.',
    errProvisionEmailRequired: 'Informe o email do usuário.',
    errProvisionUser: 'Não foi possível cadastrar usuário: {{error}}',
    errGlobalTenantsLoad: 'Não foi possível carregar tenants: {{error}}',
    errGlobalTenantCreate: 'Não foi possível criar tenant: {{error}}',
    errGlobalTenantUpdate: 'Não foi possível atualizar tenant: {{error}}',
    errGlobalUsersTenantMissing: 'Selecione um tenant para gerenciar usuários.',
    errPasswordReset: 'Não foi possível iniciar recuperação de senha: {{error}}',
    errResetEmailRequired: 'Informe seu email para recuperar a senha.',
    errLoginConnection: 'Erro de conexão. Tente novamente.',
    errSaveMissingAnalysisId: 'Não foi possível salvar: ID da análise não encontrado. Verifique os logs do Netlify.',
    errProjectionMissingAnalysis: 'Não foi possível gerar: ID da análise não encontrado.',
    errProjectionGenerate: 'Não foi possível gerar a projeção narrativa agora: {{error}}',
    cultureReviewCounter: '{{current}} de {{total}} alertas de cultura',
    contradictionDetected: 'Divergência detectada — {{name}}',
    tradeoffDetected: 'Trade-off identificado — {{name}}',
    contradictionCurrentChoice: 'Sua configuração atual',
    contradictionSuggestedChoice: 'Sugestão do documento de cultura',
    contradictionDescriptionIntro: 'No documento de cultura enviado,',
    tradeoffDescriptionIntro: 'No documento de cultura enviado, há sinais que sustentam sua configuração atual e também uma leitura alternativa.',
    tradeoffCurrentEvidenceTitle: 'Sustenta sua configuração atual',
    tradeoffAlternativeEvidenceTitle: 'Também sustenta leitura alternativa',
    tradeoffEvidenceFallback: 'Sem trecho objetivo suficiente para este lado no documento de cultura enviado.',
    contradictionPoleLeftTitle: 'Polo esquerdo',
    contradictionPoleRightTitle: 'Polo direito',
    contradictionOptionStrongLeft: 'Forte no polo esquerdo',
    contradictionOptionModerateLeft: 'Moderado no polo esquerdo',
    contradictionOptionModerateRight: 'Moderado no polo direito',
    contradictionOptionStrongRight: 'Forte no polo direito',
    configuredSlider: 'Configurado (slider)',
    inferredDoc: 'Inferido (documento)',
    levelN: 'Nível {{n}}',
    adjustSliderQuestion: 'Deseja aplicar a sugestão do documento de cultura?',
    tradeoffDecisionHint: 'Como há trade-off, confirme a opção que melhor representa a prática da empresa.',
    keepCurrentSlider: 'Manter slider atual',
    adjustToLevel: 'Ajustar para nível {{n}}',
    adjustToChoice: 'Ajustar para: {{choice}}',
    applySuggestedChoice: 'Aplicar opção sugerida',
    modalNoCandidateRecommended: 'Nenhum candidato recomendado',
    modalOnlyOneQualified: 'Apenas um candidato qualificado',
    cancel: 'Cancelar',
    generateAnyway: 'Gerar assim mesmo',
    saveAnalysisModalTitle: 'Salvar análise',
    saveAnalysisModalBody: 'Esta análise ficará visível para todos os usuários da empresa na biblioteca de análises.',
    confirm: 'Confirmar',
    confirmHide60Days: 'Confirmar e não mostrar esta mensagem por 60 dias',
    deleteCandidateModalTitle: 'Excluir candidato',
    deleteCandidateWithComparative: 'Tem certeza que deseja excluir {{name}}? Existe uma análise comparativa gerada que inclui este candidato. Ela também será excluída do banco e da biblioteca, e precisará ser refeita.',
    deleteCandidateNoComparative: 'Tem certeza que deseja excluir {{name}}? O candidato e sua análise serão removidos permanentemente.',
    delete: 'Excluir',
    editPrinciplesTitle: 'Editar Princípios de Trabalho',
    editPrinciplesWarning: 'Os Princípios de Trabalho são compartilhados por toda a organização. Qualquer alteração afetará todos os usuários e todas as posições criadas a partir de agora. Posições e análises já existentes não serão afetadas.',
    editPositionWarning: 'Ao editar esta etapa, as análises já geradas serão perdidas e será necessário fazer novo upload dos candidatos.',
    confirmEdit: 'Confirmar edição',
  },
  'en-US': {
    appTitle: 'HR Decision Support Tool',
    loginSubtitle: 'Enter your access credentials',
    emailPlaceholder: 'Your email',
    passwordPlaceholder: 'Your password',
    tokenPlaceholder: 'Company access code',
    forgotPassword: 'Forgot password',
    manageUsersHeaderButton: '👥 Users',
    creditsHeaderButton: '💳 Credits',
    helpHeaderButton: '❓ Help',
    faqTitle: 'FAQ & Guidance',
    faqSubtitle: 'Quick guide to interpret SkillCert assessment results in the real context of the role and company culture.',
    faqBack: '← Back to app',
    faqExpandAll: 'Expand all',
    faqCollapseAll: 'Collapse all',
    faqWhatToDoPrefix: 'What to do:',
    userManagementTitle: 'User management',
    userManagementSubtitle: 'Manage tenant users by updating role and status.',
    userManagementBack: '← Back to app',
    userManagementRefresh: 'Refresh list',
    userManagementInviteDisabledBanner: 'Email invites are temporarily disabled on this screen.',
    userManagementProvisionTitle: 'Provision user without email',
    userManagementProvisionSubmit: 'Provision user',
    userManagementProvisionSubmitting: 'Provisioning user…',
    userManagementProvisionSuccess: 'User {{email}} provisioned successfully.',
    userManagementInviteTitle: 'Invite user',
    userManagementInviteName: 'Name (optional)',
    userManagementInviteEmail: 'Email',
    userManagementInviteRole: 'Role',
    userManagementInviteSubmit: 'Send invite',
    userManagementInviteSubmitting: 'Sending invite…',
    userManagementListTitle: 'Tenant users',
    userManagementLoading: 'Loading users…',
    userManagementEmpty: 'No users found for this tenant.',
    userManagementRole: 'Role',
    userManagementStatus: 'Status',
    userManagementResend: 'Resend access',
    userManagementSave: 'Save change',
    userManagementSaving: 'Saving…',
    userManagementSelfTag: 'You',
    userManagementNoChanges: 'No changes to save.',
    roleLabelMasterAdmin: 'Master admin',
    roleLabelAdminClient: 'Tenant admin',
    roleLabelUserClient: 'Tenant user',
    statusLabelInvited: 'Invited',
    statusLabelActive: 'Active',
    statusLabelInactive: 'Inactive',
    userManagementInviteSuccess: 'Invite sent successfully to {{email}}.',
    userManagementResendInviteSuccess: 'Access invite resent successfully to {{email}}.',
    userManagementResendResetSuccess: 'Recovery email sent to {{email}}.',
    userManagementUpdateSuccess: 'Access for {{email}} updated successfully.',
    globalTenantsTitle: 'Tenant management',
    globalTenantsSubtitle: 'Create and manage active tenants without manual SQL.',
    globalTenantsRefresh: 'Refresh tenants',
    globalTenantsCreateTitle: 'Create tenant',
    globalTenantsName: 'Tenant name',
    globalTenantsLanguage: 'Language',
    globalTenantsStatus: 'Active',
    globalTenantsToken: 'Access token',
    globalTenantsGenerateToken: 'Generate token',
    globalTenantsCreateSubmit: 'Create tenant',
    globalTenantsCreateSubmitting: 'Creating tenant…',
    globalTenantsListTitle: 'Active tenants',
    globalTenantsEmpty: 'No active tenants found.',
    globalTenantsManageUsers: 'Manage users',
    globalTenantsEdit: 'Edit tenant',
    globalTenantsSaveEdit: 'Save tenant',
    globalTenantsSavingEdit: 'Saving…',
    globalTenantsActive: 'Active',
    globalTenantsInactive: 'Inactive',
    globalTenantsCreatedAt: 'Created at',
    globalTenantsCreateSuccess: 'Tenant "{{name}}" created successfully.',
    globalTenantsUpdateSuccess: 'Tenant "{{name}}" updated successfully.',
    globalUsersForTenant: 'Tenant users: {{name}}',
    globalUsersBack: '← Back to Tenants',
    creditsTitle: 'Credits management',
    creditsSubtitle: 'Tenant balance and consumption history.',
    creditsBack: '← Back to app',
    creditsRefresh: 'Refresh',
    creditsCurrentBalance: 'Available balance',
    creditsUsedTotal: 'Credits consumed',
    creditsPolicy: 'Current rule: individual={{individual}} · comparative={{comparative}}',
    creditsHistoryTitle: 'Credits history',
    creditsHistoryEmpty: 'No credit transactions found.',
    creditsHistoryDate: 'Date',
    creditsHistoryOperation: 'Operation',
    creditsHistoryAmount: 'Amount',
    creditsHistoryBalanceAfter: 'Balance after',
    creditsHistoryPerformedBy: 'Performed by',
    creditsHistoryAnalysis: 'Analysis',
    creditsUnknownUser: 'Unknown user',
    creditsUnknownAnalysis: 'No linked analysis',
    creditsOperationUse: 'Consumption',
    creditsOperationAdd: 'Addition',
    creditsOperationRefund: 'Refund',
    creditsOperationAdjust: 'Manual adjustment',
    creditsAdjustTitle: 'Adjust credits (master_admin)',
    creditsAdjustAmountLabel: 'Credits delta (integer)',
    creditsAdjustAmountPlaceholder: 'E.g. 50 or -10',
    creditsAdjustReasonLabel: 'Adjustment reason',
    creditsAdjustReasonPlaceholder: 'E.g. contracted load / operational correction',
    creditsAdjustSubmit: 'Apply adjustment',
    creditsAdjustSubmitting: 'Applying...',
    creditsAdjustSuccess: 'Adjustment applied successfully.',
    errCreditsLoad: 'Could not load credits: {{error}}',
    errCreditsAdjust: 'Could not adjust credits: {{error}}',
    errCreditsAdjustInvalidAmount: 'Enter a non-zero integer.',
    selectContextTitle: 'Choose where to sign in',
    selectContextSubtitle: 'This user can access more than one context. Select a tenant or the admin area.',
    selectContextLabel: 'Access context',
    enterSelectedContext: 'Enter this context',
    backToLogin: 'Back to login',
    globalAdminArea: 'Global Admin Area',
    switchTenant: 'Switch tenant',
    switchTenantConfirm: 'Do you want to switch tenant now? Unsaved data will be lost.',
    globalAdminWelcomeTitle: 'Global Administration',
    globalAdminWelcomeBody: 'You are in master admin mode. Select the tenant you want to view.',
    enterTenant: 'Enter tenant',
    errContextSelection: 'Could not select context: {{error}}',
    passwordResetSent: 'If the email exists, we will send recovery instructions.',
    checking: 'Checking…',
    enter: 'Sign in',
    principlesOrg: 'Organization Work Principles',
    principlesView: 'View configured principles',
    principlesSetup: 'Configure once — this is the company cultural context',
    principlesPendingAdmin: 'Work Principles are not configured yet by a tenant admin. Ask an admin to configure them before continuing.',
    edit: '✏️ Edit',
    leftPole: '← LEFT POLE',
    rightPole: 'RIGHT POLE →',
    strong: 'Strong',
    moderate: 'Moderate',
    cultureComplementTitle: 'Complement with culture document',
    cultureComplementBody: 'Upload a values manual, code of conduct, or any document describing how the company actually operates. AI will infer levels and flag mismatches.',
    analyzingDocument: 'Analyzing document…',
    uploadCultureDoc: '+ Upload culture document',
    cultureDocDone: '✓ Document processed. Cultural context added to analyses.',
    cultureDocNoAlerts: 'No strong divergence or actionable trade-off was identified in the uploaded culture document.',
    cultureDocReviewAlerts: 'Culture document alerts: {{strong}} strong divergences and {{tradeoff}} trade-offs.',
    cultureDocSameLevelTradeoffOnly: 'Trade-off identified in {{count}} principle{{plural}} with no level adjustment suggestion.',
    continueToPosition: 'Continue to Position →',
    positionDefinition: 'Position Definition',
    positionView: 'View configured position',
    positionSubtitle: 'Describe the real demand of this role.',
    positionGuidance: 'Select the lowest demand that still sustains consistent strong performance.',
    positionGuidanceExpandedIntro: 'Use these rules to calibrate without inflating the role:',
    positionGuidanceTieBreaker: 'If in doubt between two options, choose the lower one that would still work well in the first 90 days.',
    positionGuidanceBias: 'The first option does not reduce role importance; it describes the demand type for this dimension.',
    positionGuidanceExceptions: 'If it happens only in exceptions, do not use it to inflate demand.',
    positionGuidanceCheckQuestion: 'Calibration check: without this bar, would a strong person still perform well in the first 90 days?',
    positionGuidanceReadMore: 'View full guidance',
    positionGuidanceReadLess: 'Hide guidance',
    roleName: 'Role Name *',
    rolePlaceholder: 'Ex: CFO, Business Analyst, Commercial Manager...',
    selectLevel: 'Select the option that best matches the role’s real routine',
    positionDimensionRequirement: 'Role demand in this dimension',
    positionOptionHint: 'Select an option to view the practical explanation.',
    positionMeaningLabel: 'What this choice means',
    calibrationAlertLabel: 'Calibration alert:',
    commonMistakeLabel: 'Common mistake:',
    howToCalibrateLabel: 'How to calibrate:',
    whenInDoubtLabel: 'When in doubt:',
    positionConfirmCalibration: 'Confirm calibration for this dimension',
    positionCalibrationConfirmed: 'Calibration confirmed.',
    positionEditChoiceTooltip: 'Click to edit this dimension',
    positionEditChoiceTitle: 'Edit dimension choice',
    positionEditChoiceConfirm: 'Do you want to edit "{{dimension}}"? You will need to confirm this choice again.',
    positionChecklistTitle: 'Pending items to continue:',
    positionPendingRole: 'Provide the role name.',
    positionPendingOneDimension: 'Select 1 dimension.',
    positionPendingManyDimensions: 'Select {{count}} dimensions.',
    positionPendingOneCritical: 'Confirm choice in 1 dimension.',
    positionPendingManyCritical: 'Confirm choice in {{count}} dimensions.',
    roleContext: 'Additional role context (optional)',
    roleContextPlaceholder: 'Describe responsibilities, current challenges, team context, or any relevant information for analysis...',
    roleContextHint: 'This field enriches the analysis — more context yields better interpretation.',
    chooseFile: 'Choose file',
    noFileChosen: 'No file chosen',
    back: '← Back',
    continueToUpload: 'Continue to Upload →',
    uploadReports: 'Report Upload',
    uploadSubtitle: 'Add 1 to 5 candidates — each upload automatically triggers individual analysis',
    cognitivePdf: 'PDF — Cognitive Report *',
    candidateNameAuto: 'Candidate name will be extracted automatically from file name.',
    socioPdf: 'PDF — Socioemotional Report *',
    addCandidate: '+ Add Candidate',
    processingCandidate: 'Processing {{name}}…',
    uploadProcessingHint: 'PDFs processed. Analysis is running in background — status will update automatically shortly.',
    candidatesAdded: 'Candidates added ({{count}}/5)',
    candidateReady: '✓ Analysis ready',
    candidateTimeout: '⚠ Timeout — reload',
    candidateAnalyzing: '⏳ Analyzing...',
    deleteCandidateTitle: 'Delete candidate',
    continueToAnalysis: 'Continue to Analysis →',
    analysisTitle: 'Analysis',
    analysisSubtitlePlural: 'Individual analyses ready — choose viewing mode',
    analysisSubtitleSingle: 'Individual analysis ready',
    individualMode: 'Individual',
    comparativeMode: 'Comparative',
    generateComparative: 'Generate Comparative Analysis',
    viewIndividual: 'View Individual Analysis',
    generatingAnalysis: 'Generating analysis…',
    waitMoments: 'Please wait a moment',
    generatingComparative: 'Generating comparative analysis…',
    mayTakeMinutes: 'This may take a few minutes',
    comparativeAnalysisTitle: 'Comparative Analysis',
    exportPdf: '📄 Export PDF',
    exportingPdf: 'Exporting PDF…',
    analysisSaved: '✓ Analysis saved',
    saveAnalysis: '💾 Save analysis',
    saving: 'Saving…',
    decisorySynthesis: 'Decisory Synthesis',
    analyticalSynthesis: 'Analytical Synthesis',
    projectionTab: '🔮 Narrative Projection',
    generateProjectionNow: 'Generate now',
    projectionPreparing: 'Preparing narrative projection…',
    projectionPendingHint: 'Projection is prepared in background once individual analysis is ready.',
    saveAnalysesBatch: '💾 Save analyses ({{count}})',
    exportPdfBatch: '📄 Export PDF ({{count}})',
    doComparative: 'Run comparative analysis →',
    viewComparative: 'View comparative analysis →',
    viewRealScenario: 'View Narrative Projection',
    viewComparisonRealScenario: 'View Comparative Narrative Projection',
    generatingProjection: 'Generating narrative projection…',
    narrativeProjectionTitle: 'Narrative Projection',
    projectionDisclaimerTitle: 'How to read this projection',
    projectionDisclaimerBody: 'This text is a narrative tangibilization based on analysis data, role context, and company principles. Language is intentionally vivid and prospective. It is not deterministic prediction.',
    comparativeLoadingCta: '⏳ Generating comparative analysis...',
    viewIndividualsBack: '← View individual analyses',
    library: '📚 Library',
    analysisLibrary: 'Analysis Library',
    loadingSavedAnalyses: 'Loading saved analyses…',
    noSavedAnalyses: 'No saved analyses yet',
    noSavedAnalysesHint: 'Saved analyses will appear here, grouped by position.',
    analysesSavedCount: '{{count}} analys{{plural}} saved',
    reusePosition: '↺ Reuse position',
    reusePositionTitle: 'Use this position configuration for a new analysis',
    comparativeShort: '⚖ Comparative',
    individualShort: '👤 Individual',
    footerTitle: 'SkillCert · HR Decision Support Tool',
    footerCandidates: '{{count}} candidate{{plural}} · {{cargo}}',
    errSelectBothPdfs: 'Select both PDF files to continue',
    errCandidateStartFail: 'Candidate "{{name}}" was added, but failed to start analysis: {{error}}.',
    errCandidateProcessingFail: 'Analysis for "{{name}}" failed during processing.',
    errConnection: 'Connection error: {{error}}',
    errDeleteCandidate: 'Error deleting {{name}}: {{error}}',
    errDeleteCandidateConnection: 'Connection error while deleting {{name}}. Please try again.',
    errMissingCompleteAnalyses: 'Candidates without complete analysis: {{names}}',
    errComparativeProcessing: 'Comparative analysis failed during processing.',
    errGeneric: 'Error: {{error}}',
    errAddAtLeastOne: 'Add at least 1 candidate',
    errSavePrinciples: 'Error saving principles: {{error}}',
    errSavePosition: 'Error saving position: {{error}}',
    errSaveAnalysis: 'Error saving analysis: {{error}}',
    errSaveAnalysisConnection: 'Connection error while saving analysis: {{error}}',
    errUnsupportedFormat: 'Unsupported format. Upload a PDF or text file (.txt, .md).',
    errCultureDocTooLarge: 'File is too large. The limit is {{maxMb}} MB.',
    errProcessDocument: 'Error processing document: {{error}}',
    errProcessDocumentConnection: 'Connection error while processing document: {{error}}',
    errUnexpectedServerResponse: 'Unexpected server response.',
    errExportPdf: 'Error exporting PDF: {{error}}',
    errNoIndividualForExport: 'No individual analysis available for export.',
    errInvalidCreds: 'Invalid credentials. Please check and try again.',
    errPasswordLoginDisabled: 'Password login is disabled.',
    errTenantRequired: 'This email belongs to more than one company. Enter the company code too.',
    errUsersLoad: 'Could not load users: {{error}}',
    errInviteUser: 'Could not send invite: {{error}}',
    errResendUserAccess: 'Could not resend access: {{error}}',
    errUpdateUserAccess: 'Could not update user: {{error}}',
    errInviteEmailRequired: 'Enter an email to invite the user.',
    errProvisionEmailRequired: 'Enter the user email.',
    errProvisionUser: 'Could not provision user: {{error}}',
    errGlobalTenantsLoad: 'Could not load tenants: {{error}}',
    errGlobalTenantCreate: 'Could not create tenant: {{error}}',
    errGlobalTenantUpdate: 'Could not update tenant: {{error}}',
    errGlobalUsersTenantMissing: 'Select a tenant to manage users.',
    errPasswordReset: 'Could not start password recovery: {{error}}',
    errResetEmailRequired: 'Enter your email to recover your password.',
    errLoginConnection: 'Connection error. Please try again.',
    errSaveMissingAnalysisId: 'Unable to save: analysis ID not found. Check Netlify logs.',
    errProjectionMissingAnalysis: 'Unable to generate: analysis ID was not found.',
    errProjectionGenerate: 'Could not generate narrative projection right now: {{error}}',
    cultureReviewCounter: '{{current}} of {{total}} culture alerts',
    contradictionDetected: 'Mismatch detected — {{name}}',
    tradeoffDetected: 'Trade-off identified — {{name}}',
    contradictionCurrentChoice: 'Current setting',
    contradictionSuggestedChoice: 'Document suggests',
    contradictionDescriptionIntro: 'In the uploaded culture document,',
    tradeoffDescriptionIntro: 'In the uploaded culture document, there are signals supporting both your current setting and an alternative reading.',
    tradeoffCurrentEvidenceTitle: 'Supports your current setting',
    tradeoffAlternativeEvidenceTitle: 'Also supports an alternative reading',
    tradeoffEvidenceFallback: 'No objective excerpt was identified for this side in the uploaded culture document.',
    contradictionPoleLeftTitle: 'Left pole',
    contradictionPoleRightTitle: 'Right pole',
    contradictionOptionStrongLeft: 'Strong on left pole',
    contradictionOptionModerateLeft: 'Moderate on left pole',
    contradictionOptionModerateRight: 'Moderate on right pole',
    contradictionOptionStrongRight: 'Strong on right pole',
    configuredSlider: 'Configured (slider)',
    inferredDoc: 'Inferred (document)',
    levelN: 'Level {{n}}',
    adjustSliderQuestion: 'Do you want to apply the suggestion from the culture document?',
    tradeoffDecisionHint: 'Because this is a trade-off, confirm the option that best represents how your company operates in practice.',
    keepCurrentSlider: 'Keep current slider',
    adjustToLevel: 'Adjust to level {{n}}',
    adjustToChoice: 'Adjust to: {{choice}}',
    applySuggestedChoice: 'Apply suggested option',
    modalNoCandidateRecommended: 'No candidate recommended',
    modalOnlyOneQualified: 'Only one qualified candidate',
    cancel: 'Cancel',
    generateAnyway: 'Generate anyway',
    saveAnalysisModalTitle: 'Save analysis',
    saveAnalysisModalBody: 'This analysis will be visible to all company users in the analysis library.',
    confirm: 'Confirm',
    confirmHide60Days: 'Confirm and do not show this message for 60 days',
    deleteCandidateModalTitle: 'Delete candidate',
    deleteCandidateWithComparative: 'Are you sure you want to delete {{name}}? There is a comparative analysis that includes this candidate. It will also be deleted from database and library and must be regenerated.',
    deleteCandidateNoComparative: 'Are you sure you want to delete {{name}}? The candidate and their analysis will be permanently removed.',
    delete: 'Delete',
    editPrinciplesTitle: 'Edit Work Principles',
    editPrinciplesWarning: 'Work Principles are shared across the organization. Any change will affect all users and all positions created from now on. Existing positions and analyses will not be affected.',
    editPositionWarning: 'Editing this step will discard generated analyses and require re-uploading candidates.',
    confirmEdit: 'Confirm edit',
  },
};

const FAQ_CONTENT = {
  'pt-BR': [
    {
      id: 'purpose',
      question: 'Para que serve esta ferramenta?',
      paragraphs: [
        'Esta ferramenta ajuda o RH a interpretar resultados dos testes SkillCert de candidatos no contexto real da vaga e da cultura da empresa.',
        'Ela não substitui o julgamento do RH. Ela organiza evidências para apoiar uma decisão mais consistente.'
      ],
      action: 'Use este material junto com entrevista, referências e avaliação técnica.'
    },
    {
      id: 'full-flow',
      question: 'Como fazer uma rodada completa sem erro (passo a passo rápido)?',
      steps: [
        'Configure os Princípios de Trabalho da empresa.',
        'Defina a vaga com foco na exigência real.',
        'Faça upload dos dois PDFs por candidato (cognitivo e socioemocional).',
        'Aguarde as análises individuais concluírem.',
        'Leia primeiro a Síntese Decisória e depois a Síntese Analítica.',
        'Gere a comparativa quando houver pelo menos 2 candidatos com análise individual pronta.',
        'Salve as análises relevantes na Biblioteca.'
      ],
      action: 'Siga essa ordem para reduzir retrabalho e erro de interpretação.'
    },
    {
      id: 'work-principles',
      question: 'O que são Princípios de Trabalho?',
      paragraphs: [
        'Princípios de Trabalho descrevem como a empresa realmente opera no dia a dia: ritmo de decisão, estabilidade vs. mudança, intensidade, colaboração e tolerância a erro.',
        'Eles funcionam como contexto de cultura organizacional para interpretar aderência de perfil com mais precisão.'
      ],
      action: 'Mantenha esses princípios atualizados quando houver mudança de liderança, estratégia ou forma de trabalho.'
    },
    {
      id: 'three-views',
      question: 'Qual a diferença entre as 3 visões do relatório individual?',
      points: [
        'Síntese Decisória: resposta objetiva para decisão (recomendação, riscos e condições de sucesso).',
        'Síntese Analítica: explica o "porquê" da recomendação (leitura cognitiva, socioemocional e fit cultural).',
        'Projeção Narrativa: visão aplicada de como o perfil pode se manifestar no contexto da vaga.'
      ],
      action: 'Leia nessa ordem: Decisória -> Analítica -> Projeção Narrativa.'
    },
    {
      id: 'narrative-reading',
      question: 'Como ler a Projeção Narrativa da forma correta?',
      paragraphs: [
        'A Projeção Narrativa usa linguagem mais concreta e situacional para facilitar entendimento prático.',
        'Ela não é previsão determinística de comportamento futuro.'
      ],
      action: 'Use como cenário plausível para onboarding, gestão e mitigação de risco, nunca como certeza sobre a pessoa.'
    },
    {
      id: 'outcomes',
      question: 'Quais resultados podem aparecer e o que significam na prática?',
      points: [
        'Recomendado com Destaque: aderência forte às exigências centrais da vaga.',
        'Recomendado: boa aderência geral, com ajustes pontuais gerenciáveis.',
        'Recomendado com Ressalva: contratação possível, com risco claro que precisa de gestão ativa.',
        'Não Recomendado: mismatch relevante para o nível de exigência atual da vaga.'
      ],
      action: 'Antes da decisão final, registre risco principal e condição de sucesso.'
    },
    {
      id: 'how-result',
      question: 'Como o sistema chega ao resultado (em linguagem simples)?',
      paragraphs: [
        'O sistema combina quatro blocos: dados dos testes, configuração da vaga, contexto adicional da vaga e Princípios de Trabalho da empresa.',
        'Primeiro aplica regras de consistência e aderência. Depois gera sínteses em linguagem natural para apoiar leitura do RH.'
      ],
      action: 'Revise a qualidade dos dados de entrada antes de interpretar o resultado.'
    },
    {
      id: 'calibrate-position',
      question: 'Como calibrar a vaga para evitar distorção de resultado?',
      points: [
        'Descreva o mínimo necessário para boa performance consistente nos primeiros 90 dias.',
        'Se estiver em dúvida entre dois níveis, escolha o menor que ainda sustenta entrega boa.',
        'Evite inflar exigência com base em situações raras.',
        'Use o campo de contexto da vaga para detalhar desafios reais da função.'
      ],
      action: 'A calibragem correta melhora a qualidade da leitura comparativa.'
    },
    {
      id: 'credits-policy',
      question: 'Como funciona consumo de créditos?',
      paragraphs: [
        'Regra atual: análise individual consome 1 crédito e análise comparativa consome 2 créditos.',
        'Há checagem de saldo antes do início. Em falha técnica após débito, existe estorno técnico automático.'
      ],
      action: 'Acompanhe saldo e histórico na tela de Créditos antes de rodadas maiores.'
    },
    {
      id: 'credits-insufficient',
      question: 'Como resolver "créditos insuficientes"?',
      paragraphs: [
        'Esse aviso significa que a empresa está sem saldo para aquele tipo de análise.'
      ],
      action: 'Valide saldo e histórico em Créditos e solicite ajuste para um administrador global (master_admin).'
    },
    {
      id: 'library-save-review',
      question: 'Como salvar e rever análises na Biblioteca?',
      paragraphs: [
        'Depois de gerar, clique em "Salvar análise".',
        'Somente análises salvas aparecem na Biblioteca.',
        'Na Biblioteca, as análises ficam agrupadas por posição e podem ser reabertas depois.'
      ],
      action: 'Salve apenas análises que devem ficar disponíveis para o time da empresa.'
    },
    {
      id: 'reuse-position',
      question: 'Como reaproveitar uma posição para nova rodada?',
      paragraphs: [
        'Na Biblioteca, use "Reaproveitar posição".',
        'A configuração da vaga é carregada para uma nova rodada, reduzindo retrabalho.'
      ],
      action: 'Após reaproveitar, faça novo upload dos candidatos para gerar análises novas.'
    },
    {
      id: 'who-edits-principles',
      question: 'Quem pode editar Princípios de Trabalho?',
      points: [
        'Administrador global (master_admin) e administrador da empresa (admin_client) podem editar.',
        'Usuário da empresa (user_client) usa os princípios configurados, sem permissão de edição.'
      ],
    },
    {
      id: 'who-manages-users-credits',
      question: 'Quem pode gerenciar usuários e créditos?',
      points: [
        'Gestão de usuários: master_admin e admin_client.',
        'Visualização de saldo e histórico de créditos: master_admin, admin_client e user_client.',
        'Ajuste manual de créditos: somente master_admin.'
      ],
    },
    {
      id: 'comparative-without-recommended',
      question: 'Posso gerar comparativa mesmo se ninguém foi recomendado?',
      paragraphs: [
        'Pode. A comparativa pode ser gerada e vai explicitar o cenário de assimetria.'
      ],
      action: 'Use esse resultado para revisar calibragem da vaga e critérios de decisão, não como ranking forçado.'
    },
    {
      id: 'limits',
      question: 'Quais são os limites de interpretação dos outputs?',
      paragraphs: [
        'Os testes SkillCert medem habilidades cognitivas e socioemocionais, que são fortes preditoras de fit e performance.',
        'Mesmo assim, algumas vagas exigem avaliação específica de conhecimento de área e expertise técnica.'
      ],
      action: 'Sempre complemente com avaliação técnica, estudo de caso, entrevista situacional e referências quando a função exigir domínio técnico específico.'
    },
  ],
  'en-US': [
    {
      id: 'purpose',
      question: 'What is this tool for?',
      paragraphs: [
        'This tool helps HR interpret SkillCert candidate assessment results in the real context of the role and company culture.',
        'It does not replace HR judgment. It structures evidence to support more consistent decisions.'
      ],
      action: 'Use this guidance together with interviews, references, and technical evaluation.'
    },
    {
      id: 'full-flow',
      question: 'How to run a full round with fewer mistakes (quick flow)?',
      steps: [
        'Configure company Work Principles.',
        'Define the role based on real demand.',
        'Upload both PDFs per candidate (cognitive and socioemotional).',
        'Wait for individual analyses to complete.',
        'Read Decisory Synthesis first, then Analytical Synthesis.',
        'Run comparative analysis when at least 2 candidates have completed individual analysis.',
        'Save relevant analyses to Library.'
      ],
      action: 'Follow this sequence to reduce rework and interpretation mistakes.'
    },
    {
      id: 'work-principles',
      question: 'What are Work Principles?',
      paragraphs: [
        'Work Principles describe how the company actually operates day to day: decision pace, stability vs. change, intensity, collaboration, and error tolerance.',
        'They provide organizational culture context for more accurate fit interpretation.'
      ],
      action: 'Update them whenever leadership style, strategy, or operating model changes.'
    },
    {
      id: 'three-views',
      question: 'What is the difference between the 3 individual report views?',
      points: [
        'Decisory Synthesis: direct decision view (recommendation, risks, and success conditions).',
        'Analytical Synthesis: explains the "why" (cognitive, socioemotional, and cultural fit).',
        'Narrative Projection: applied view of how the profile may show up in role context.'
      ],
      action: 'Read in this order: Decisory -> Analytical -> Narrative Projection.'
    },
    {
      id: 'narrative-reading',
      question: 'How should I read Narrative Projection correctly?',
      paragraphs: [
        'Narrative Projection uses more concrete and situational language to make interpretation practical.',
        'It is not deterministic prediction of future behavior.'
      ],
      action: 'Treat it as a plausible scenario for onboarding, management planning, and risk mitigation, not as certainty about the person.'
    },
    {
      id: 'outcomes',
      question: 'Which outcomes can appear and what do they mean in practice?',
      points: [
        'Highly Recommended: strong fit with core role demands.',
        'Recommended: good overall fit, with manageable targeted adjustments.',
        'Recommended with Caveats: hire is possible, with a clear risk that requires active management.',
        'Not Recommended: relevant mismatch for the current role demand level.'
      ],
      action: 'Before final decision, explicitly register main risk and success condition.'
    },
    {
      id: 'how-result',
      question: 'How does the system reach the result (plain language)?',
      paragraphs: [
        'The system combines four inputs: assessment data, role setup, additional role context, and company Work Principles.',
        'It first applies consistency and fit rules, then generates natural-language syntheses for HR interpretation.'
      ],
      action: 'Always review input quality before interpreting outputs.'
    },
    {
      id: 'calibrate-position',
      question: 'How to calibrate role setup to avoid distorted outputs?',
      points: [
        'Set the minimum level required for consistent strong performance in the first 90 days.',
        'If you are between two levels, choose the lower one that still sustains performance.',
        'Do not inflate requirements based on rare exceptions.',
        'Use additional role context to describe real constraints and challenges.'
      ],
      action: 'Better calibration improves comparative reading quality.'
    },
    {
      id: 'credits-policy',
      question: 'How does credit consumption work?',
      paragraphs: [
        'Current rule: individual analysis uses 1 credit and comparative analysis uses 2 credits.',
        'There is a precheck before start. If a technical failure occurs after debit, technical refund is applied automatically.'
      ],
      action: 'Monitor balance and history in Credits before larger rounds.'
    },
    {
      id: 'credits-insufficient',
      question: 'How to resolve "insufficient credits"?',
      paragraphs: [
        'This warning means the company does not have enough balance for that analysis type.'
      ],
      action: 'Check balance and history in Credits and request a manual adjustment from global admin (master_admin).'
    },
    {
      id: 'library-save-review',
      question: 'How do I save and review analyses in Library?',
      paragraphs: [
        'After generation, click "Save analysis".',
        'Only saved analyses appear in Library.',
        'In Library, analyses are grouped by position and can be reopened later.'
      ],
      action: 'Save only analyses that should be visible to the company team.'
    },
    {
      id: 'reuse-position',
      question: 'How to reuse a position for a new round?',
      paragraphs: [
        'In Library, use "Reuse position".',
        'Role setup is prefilled for a new round, reducing rework.'
      ],
      action: 'After reusing the position, upload new candidate files to generate new analyses.'
    },
    {
      id: 'who-edits-principles',
      question: 'Who can edit Work Principles?',
      points: [
        'Global admin (master_admin) and company admin (admin_client) can edit.',
        'Company user (user_client) can use configured principles but cannot edit them.'
      ],
    },
    {
      id: 'who-manages-users-credits',
      question: 'Who can manage users and credits?',
      points: [
        'User management: master_admin and admin_client.',
        'Credit balance/history view: master_admin, admin_client, and user_client.',
        'Manual credit adjustment: only master_admin.'
      ],
    },
    {
      id: 'comparative-without-recommended',
      question: 'Can I run comparative analysis if no one was recommended?',
      paragraphs: [
        'Yes. Comparative analysis can still be generated and will explicitly describe the asymmetry.'
      ],
      action: 'Use this output to review role calibration and decision criteria, not as forced ranking.'
    },
    {
      id: 'limits',
      question: 'What are the interpretation limits of outputs?',
      paragraphs: [
        'SkillCert assessments measure cognitive and socioemotional skills, which are strong predictors of fit and performance.',
        'Still, some roles require direct evaluation of domain knowledge and technical expertise.'
      ],
      action: 'Always complement with technical assessment, case exercise, situational interview, and references when role-specific expertise is critical.'
    },
  ],
};

function buildFaqExpansionMap(items, expand = false) {
  return (items || []).reduce((acc, item, index) => {
    acc[item.id] = expand ? true : index === 0;
    return acc;
  }, {});
}

const PRINCIPIOS_TRABALHO_EN = {
  PT1: {
    titulo: 'Decision Pace',
    esquerda: 'Important decisions wait for the necessary level of analysis and rationale — even if that takes time.',
    direita: 'Important decisions are made with the information currently available — adjustments happen along the way.',
  },
  PT2: {
    titulo: 'Stability versus Change',
    esquerda: 'Guidelines and priorities tend to be stable — direction changes are exceptions and require strategic review.',
    direita: 'Guidelines and priorities change frequently — adapting to new directions is a normal part of work.',
  },
  PT3: {
    titulo: 'Interpersonal Tension and Conflict',
    esquerda: 'Disagreements are resolved in a contained and diplomatic way — open debate between people is avoided to prevent unnecessary wear in the environment.',
    direita: 'Disagreements are addressed explicitly — open debate between people is a normal part of the process.',
  },
  PT4: {
    titulo: 'Workplace Intensity',
    esquerda: 'The work pace is driven by predictability and sustainability — demand peaks are planned exceptions.',
    direita: 'The work pace is high-intensity with continuous demand — delivery under pressure is a structural pillar.',
  },
  PT5: {
    titulo: 'Individual versus Collective Work',
    esquerda: 'Focus is on individual autonomy: each person is evaluated and recognized primarily for their own delivery.',
    direita: 'Collective delivery carries more weight: those who elevate the team are valued even without standout individual results.',
  },
  PT6: {
    titulo: 'Relationship with Deviation and Error',
    esquerda: 'Following process, mitigating risk, and avoiding errors are priorities — deviations can carry reputational cost regardless of outcomes.',
    direita: 'Outcomes and learning prevail over strict process adherence — justifiable shortcuts and mistakes are accepted when they create value.',
  },
};

const DIMENSION_LABELS_EN = {
  'Habilidades Analíticas': 'Analytical Skills',
  'Tomada de Decisão': 'Decision-Making',
  'Agilidade e Aprendizado': 'Agility and Learning',
  Liderança: 'Leadership',
  'Orientação para Cliente': 'Customer Orientation',
  'Influência e Gestão de Stakeholders': 'Influence and Stakeholder Management',
};

const LEVEL_DESCRIPTIONS_EN = {
  HA1: 'Operational profile. Structured work with defined processes and scripts. Simple analytical reasoning.',
  HA2: 'Critical reasoning required from the start. Moderately complex problems, without advanced mathematical modeling.',
  HA3: 'Complex challenges from the start, involving advanced mathematical modeling.',
  TD1: 'Decisions based on known rules, patterns, or procedures.',
  TD2: 'Decisions with some autonomy on "how", within defined parameters.',
  TD3: 'Decisions in new or ambiguous situations, with incomplete information and medium impact.',
  TD4: 'Strategic decisions under high uncertainty, pressure, broad impact, and relevant irreversibility.',
  AA1: 'Current knowledge is sufficient. Stable domain with periodic and predictable updates.',
  AA2: 'Continuous learning in evolving domains, but within areas adjacent to existing expertise.',
  AA3: 'Demand for learning new and complex knowledge with high speed and consistency.',
  LD1: 'Position does not involve leadership now nor in the visible future.',
  LD2: 'Position involves (or may soon involve) team leadership.',
  LD3: 'Position involves (or may soon involve) area leadership.',
  LD4: 'Position involves (or may soon involve) business unit or company leadership.',
  OC1: 'No direct interaction with customers or external users.',
  OC2: 'Customer contact is occasional and low in relational complexity.',
  OC3: 'Frequent customer interactions requiring consistent relational skills and expectation management.',
  OC4: 'Critical relationships with high-value or high-complexity customers, where outcomes strongly depend on relationship quality.',
  IN1: 'Individual technical execution, without need to persuade or align positions with others.',
  IN2: 'Peer persuasion and resource negotiation in low-resistance contexts.',
  IN3: 'Persuading stakeholders with conflicting interests. Requires political articulation and consensus building.',
  IN4: 'Negotiating critical alliances, institutional representation, or shifting worldviews in high-impact forums.',
};

const POSITION_LEVEL_COPY = {
  HA1: {
    label: { 'pt-BR': 'Execução analítica estruturada', 'en-US': 'Structured analytical execution' },
    detail: {
      'pt-BR': 'Trabalho com método, processo e critérios já definidos.',
      'en-US': 'Work with defined methods, processes, and criteria.',
    },
  },
  HA2: {
    label: { 'pt-BR': 'Análise crítica aplicada', 'en-US': 'Applied critical analysis' },
    detail: {
      'pt-BR': 'Interpreta dados, identifica causas e compara alternativas em problemas moderados.',
      'en-US': 'Interprets data, identifies root causes, and compares alternatives in moderately complex problems.',
    },
  },
  HA3: {
    label: { 'pt-BR': 'Análise avançada com modelagem', 'en-US': 'Advanced analysis with modeling' },
    detail: {
      'pt-BR': 'Lida com problemas complexos e usa modelagem quantitativa/matemática quando necessário.',
      'en-US': 'Handles complex problems and uses quantitative/mathematical modeling when needed.',
    },
  },
  TD1: {
    label: { 'pt-BR': 'Execução com regra definida', 'en-US': 'Execution with defined rules' },
    detail: {
      'pt-BR': 'Aplica decisões e regras já estabelecidas; pouca autonomia para definir caminho.',
      'en-US': 'Applies pre-defined decisions and rules; limited autonomy to define the path.',
    },
  },
  TD2: {
    label: { 'pt-BR': 'Decisão de execução dentro de parâmetros', 'en-US': 'Execution decisions within clear parameters' },
    detail: {
      'pt-BR': 'Decide o "como" executar (prioridades, abordagem, sequência) dentro de limites claros.',
      'en-US': 'Decides how to execute (priorities, approach, sequencing) within clear boundaries.',
    },
  },
  TD3: {
    label: { 'pt-BR': 'Decisão em contexto ambíguo', 'en-US': 'Decision in ambiguous contexts' },
    detail: {
      'pt-BR': 'Decide com informação incompleta em situações novas, com impacto relevante.',
      'en-US': 'Decides with incomplete information in new situations with meaningful impact.',
    },
  },
  TD4: {
    label: { 'pt-BR': 'Decisão estratégica crítica', 'en-US': 'Critical strategic decision' },
    detail: {
      'pt-BR': 'Decide sob alta incerteza e pressão, com impacto amplo e difícil reversão.',
      'en-US': 'Decides under high uncertainty and pressure, with broad and hard-to-reverse impact.',
    },
  },
  AA1: {
    label: { 'pt-BR': 'Aprendizado pontual', 'en-US': 'Occasional learning' },
    detail: {
      'pt-BR': 'Aprende ajustes ocasionais para manter a operação.',
      'en-US': 'Learns occasional adjustments needed to keep operations running.',
    },
  },
  AA2: {
    label: { 'pt-BR': 'Aprendizado contínuo na mesma área', 'en-US': 'Continuous learning in the same domain' },
    detail: {
      'pt-BR': 'Precisa aprender com frequência dentro do domínio principal da função.',
      'en-US': 'Needs frequent learning within the role’s main domain.',
    },
  },
  AA3: {
    label: { 'pt-BR': 'Aprendizado acelerado em temas novos', 'en-US': 'Accelerated learning in new topics' },
    detail: {
      'pt-BR': 'Precisa aprender rápido temas novos e complexos, fora do repertório atual.',
      'en-US': 'Needs to quickly learn new, complex topics beyond current repertoire.',
    },
  },
  LD1: {
    label: { 'pt-BR': 'Sem liderança esperada', 'en-US': 'No expected people leadership' },
    detail: {
      'pt-BR': 'Não há expectativa de liderar pessoas no horizonte da função.',
      'en-US': 'No expected people leadership in this role horizon.',
    },
  },
  LD2: {
    label: { 'pt-BR': 'Liderança de time', 'en-US': 'Team leadership' },
    detail: {
      'pt-BR': 'Lidera (ou deve liderar em breve) equipe direta.',
      'en-US': 'Leads (or is expected to soon lead) a direct team.',
    },
  },
  LD3: {
    label: { 'pt-BR': 'Liderança de área', 'en-US': 'Area leadership' },
    detail: {
      'pt-BR': 'Lidera uma área com múltiplas frentes.',
      'en-US': 'Leads an area with multiple workstreams.',
    },
  },
  LD4: {
    label: { 'pt-BR': 'Liderança de unidade/negócio', 'en-US': 'Business unit/company leadership' },
    detail: {
      'pt-BR': 'Lidera unidade relevante do negócio ou o negócio.',
      'en-US': 'Leads a relevant business unit or the overall business.',
    },
  },
  OC1: {
    label: { 'pt-BR': 'Sem contato direto com cliente/usuário', 'en-US': 'No direct customer/user contact' },
    detail: {
      'pt-BR': 'Não há interação direta com cliente/usuário externo.',
      'en-US': 'No direct interaction with external customers/users.',
    },
  },
  OC2: {
    label: { 'pt-BR': 'Contato pontual e transacional', 'en-US': 'Occasional transactional contact' },
    detail: {
      'pt-BR': 'Interações ocasionais para demandas específicas, com baixa complexidade relacional.',
      'en-US': 'Occasional interactions for specific requests, with low relational complexity.',
    },
  },
  OC3: {
    label: { 'pt-BR': 'Relacionamento recorrente com cliente/usuário', 'en-US': 'Recurring customer/user relationship' },
    detail: {
      'pt-BR': 'Interação frequente para alinhar prazo, escopo, qualidade e prioridades.',
      'en-US': 'Frequent interaction to align timeline, scope, quality, and priorities.',
    },
  },
  OC4: {
    label: { 'pt-BR': 'Relacionamento crítico de alto impacto', 'en-US': 'Critical high-impact relationship' },
    detail: {
      'pt-BR': 'A relação com clientes estratégicos/sensíveis afeta diretamente receita, risco ou reputação.',
      'en-US': 'Relationships with strategic/sensitive customers directly affect revenue, risk, or reputation.',
    },
  },
  IN1: {
    label: { 'pt-BR': 'Execução individual', 'en-US': 'Individual execution' },
    detail: {
      'pt-BR': 'Entrega majoritariamente individual, com baixa necessidade de influência.',
      'en-US': 'Mostly individual delivery with low influence demand.',
    },
  },
  IN2: {
    label: { 'pt-BR': 'Alinhamento entre pares', 'en-US': 'Peer alignment' },
    detail: {
      'pt-BR': 'Influencia pares e negocia ajustes em contexto de baixa resistência.',
      'en-US': 'Influences peers and negotiates adjustments in low-resistance contexts.',
    },
  },
  IN3: {
    label: { 'pt-BR': 'Articulação entre áreas com interesses divergentes', 'en-US': 'Cross-area articulation with divergent interests' },
    detail: {
      'pt-BR': 'Constrói consenso entre áreas internas com objetivos conflitantes.',
      'en-US': 'Builds consensus across internal areas with conflicting goals.',
    },
  },
  IN4: {
    label: { 'pt-BR': 'Negociação institucional crítica', 'en-US': 'Critical institutional negotiation' },
    detail: {
      'pt-BR': 'Negocia decisões/alianças de alto impacto institucional com alta liderança e/ou atores externos.',
      'en-US': 'Negotiates high-impact institutional decisions/alliances with senior leaders and/or external actors.',
    },
  },
};

const POSITION_DIMENSION_HELPERS = {
  'Tomada de Decisão': {
    'pt-BR': 'Aqui avaliamos o tipo de decisão da função (autonomia, risco e impacto), não a sofisticação analítica.',
    'en-US': 'This dimension evaluates the role’s decision context (autonomy, risk, and impact), not analytical sophistication.',
  },
  'Agilidade e Aprendizado': {
    'pt-BR': 'Avalia o ritmo e a profundidade de aprendizado exigidos para manter boa performance na função.',
    'en-US': 'Evaluates the required pace and depth of learning to sustain strong performance in this role.',
  },
  'Orientação para Cliente': {
    'pt-BR': 'Aqui "expectativas" = expectativas de clientes/usuários externos sobre prazo, qualidade, escopo e resultado.',
    'en-US': 'Here, "expectations" means external customer/user expectations about timeline, quality, scope, and outcomes.',
  },
};

const POSITION_DIMENSION_COMMON_ERRORS = {
  'Tomada de Decisão': {
    commonMistake: {
      'pt-BR': 'marcar exigência estratégica sem decisões críticas recorrentes na rotina.',
      'en-US': 'selecting a strategic level without recurring critical decisions in daily work.',
    },
    howToCalibrate: {
      'pt-BR': 'use nível alto quando há decisões frequentes sem regra pronta, com risco e impacto real.',
      'en-US': 'use a high level when decisions are frequent, rule-light, and carry real risk/impact.',
    },
    whenInDoubt: {
      'pt-BR': 'se a maior parte do trabalho segue playbook, mantenha nível mais baixo.',
      'en-US': 'if most work follows a playbook, keep a lower level.',
    },
  },
  'Orientação para Cliente': {
    commonMistake: {
      'pt-BR': 'marcar nível alto porque a empresa é customer-centric, mesmo quando a vaga quase não interage com cliente.',
      'en-US': 'setting a high level because the company is customer-centric, even when the role rarely interacts with customers.',
    },
    howToCalibrate: {
      'pt-BR': 'use nível alto quando há contato recorrente para alinhar prazo, escopo, qualidade e trade-offs.',
      'en-US': 'use a high level when there is recurring interaction to align timeline, scope, quality, and trade-offs.',
    },
    whenInDoubt: {
      'pt-BR': 'se o contato é raro ou indireto, escolha nível mais baixo.',
      'en-US': 'if contact is rare or indirect, choose a lower level.',
    },
  },
  'Influência e Gestão de Stakeholders': {
    commonMistake: {
      'pt-BR': 'confundir boa comunicação com necessidade real de negociar conflitos entre áreas.',
      'en-US': 'confusing good communication with a real need to negotiate cross-functional conflicts.',
    },
    howToCalibrate: {
      'pt-BR': 'use nível alto quando a função precisa arbitrar interesses divergentes com impacto transversal no negócio.',
      'en-US': 'use a high level when the role must arbitrate competing interests with cross-business impact.',
    },
    whenInDoubt: {
      'pt-BR': 'se a articulação fica dentro do próprio time, mantenha nível mais baixo.',
      'en-US': 'if alignment stays mostly within the same team, keep a lower level.',
    },
  },
};

function translate(locale, key, vars = {}) {
  const dict = UI_TEXT[locale] || UI_TEXT['pt-BR'];
  const fallback = UI_TEXT['pt-BR'];
  const template = dict[key] || fallback[key] || key;
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.replaceAll(`{{${k}}}`, String(v)),
    template
  );
}

function getLocaleText(locale, value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[locale] || value['pt-BR'] || '';
}

// Renderiza markdown como HTML
function renderMarkdown(text) {
  if (!text) return '';
  const escapeHtml = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

  const lines = text.split('\n');
  const result = [];
  let inList = false;
  for (let line of lines) {
    if (/^## (.+)$/.test(line)) {
      if (inList) { result.push('</ul>'); inList = false; }
      const heading = escapeHtml(line.replace(/^## /, ''));
      result.push(`<h2>${heading}</h2>`);
    } else if (/^### (.+)$/.test(line)) {
      if (inList) { result.push('</ul>'); inList = false; }
      const heading = escapeHtml(line.replace(/^### /, ''));
      result.push(`<h3>${heading}</h3>`);
    } else if (/^---$/.test(line)) {
      if (inList) { result.push('</ul>'); inList = false; }
      result.push('<hr/>');
    } else if (/^(?:[→•-]|\d+\.)\s/.test(line) && line.trim()) {
      if (!inList) { result.push('<ul>'); inList = true; }
      const content = escapeHtml(line.replace(/^(?:[→•-]|\d+\.)\s*/, '').trim())
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>');
      result.push('<li>' + content + '</li>');
    } else if (line.trim()) {
      if (inList) { result.push('</ul>'); inList = false; }
      const content = escapeHtml(line)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>');
      result.push('<p>' + content + '</p>');
    } else {
      if (inList) { result.push('</ul>'); inList = false; }
    }
  }
  if (inList) result.push('</ul>');
  return result.join('');
}

// Badge de recomendação
function RecomendacaoBadge({ recomendacao, locale = 'pt-BR' }) {
  const map = locale === 'en-US'
    ? {
      highly_recommended: { label: 'Highly Recommended', cls: 'badge-blue' },
      recommended: { label: 'Recommended', cls: 'badge-green' },
      recommended_with_caveats: { label: 'Recommended with Caveats', cls: 'badge-yellow' },
      not_recommended: { label: 'Not Recommended', cls: 'badge-red' },
    }
    : {
      highly_recommended: { label: 'Recomendado com Destaque', cls: 'badge-blue' },
      recommended: { label: 'Recomendado', cls: 'badge-green' },
      recommended_with_caveats: { label: 'Recomendado com Ressalva', cls: 'badge-yellow' },
      not_recommended: { label: 'Não Recomendado', cls: 'badge-red' },
    };
  const item = map[recomendacao];
  if (!item) return null;
  return <span className={`rec-badge ${item.cls}`}>{item.label}</span>;
}

// ===========================================================================
// COMPONENTE PRINCIPAL
// ===========================================================================
export default function App() {
  // Sessão — carregada via autenticação por senha + seleção de contexto
  const [sessao, setSessao] = useState(null); // { company_id, company_name, users }
  const [usuarioAtivo, setUsuarioAtivo] = useState(null);
  const [authAccessToken, setAuthAccessToken] = useState(null);
  const [contextOptions, setContextOptions] = useState([]);
  const [pendingContextSelection, setPendingContextSelection] = useState(false);
  const [selectedContextValue, setSelectedContextValue] = useState('');
  const [contextLoginEmail, setContextLoginEmail] = useState('');
  const [contextCarregando, setContextCarregando] = useState(false);

  const companyId = sessao?.company_id || null;
  const sessionToken = sessao?.session_token || null;
  const isGlobalAdminContext = sessao?.context_type === 'global_admin';
  const tenantLocale = sessao?.language || 'en-US';
  const t = (key, vars = {}) => translate(tenantLocale, key, vars);
  const isEn = tenantLocale === 'en-US';
  const faqItems = FAQ_CONTENT[tenantLocale] || FAQ_CONTENT['pt-BR'];
  const [etapa, setEtapa] = useState(1);
  const [principios, setPrincipios] = useState({});
  const [candidatos, setCandidatos] = useState([]);
  const [positionId, setPositionId] = useState(null);
  const [cargo, setCargo] = useState('');
  const [dimensoes, setDimensoes] = useState({});
  const [observacoes, setObservacoes] = useState('');
  const [analises, setAnalises] = useState([]);
  const [analisesIndividuais, setAnalisesIndividuais] = useState({});
  const [comparativaCache, setComparativaCache] = useState(null); // preserva resultado gerado ao navegar
  const [, setComparativaPollingId] = useState(null); // analysis_id sendo polled
  const [comparativaProcessando, setComparativaProcessando] = useState(false); // true durante o polling
  const [analisesSalvas, setAnalisesSalvas] = useState({}); // { analysis_id: true } — controle local de salvas
  const [modalSalvar, setModalSalvar] = useState(null); // { analysisIds: [], tipo } — controle do modal
  const [salvando, setSalvando] = useState(false);
  const [modalComparativa, setModalComparativa] = useState(null); // { nenhum: bool, finalistaNome: str|null, naoRecomendados: [], podeExcluir: bool }
  const [telaAtual, setTelaAtual] = useState('app'); // 'app' | 'usuarios' | 'global-usuarios' | 'creditos' | 'biblioteca' | 'analise-detalhe' | 'faq'
  const [biblioteca, setBiblioteca] = useState(null); // { positions: [...] }
  const [bibliotecaCarregando, setBibliotecaCarregando] = useState(false);
  const [analiseDetalhe, setAnaliseDetalhe] = useState(null); // análise selecionada na biblioteca
  const [analiseDetalheTab, setAnaliseDetalheTab] = useState('decisoria');
  const [faqExpanded, setFaqExpanded] = useState(() => buildFaqExpansionMap(FAQ_CONTENT['pt-BR']));
  // Camada 0B
  const [camada0bProcessando, setCamada0bProcessando] = useState(false);
  const [camada0bResultado, setCamada0bResultado] = useState(null); // resultado da inferência
  const [camada0bReviewSummary, setCamada0bReviewSummary] = useState(null); // { strong, tradeoffs, sameLevelTradeoffs, hasActionableAlerts, hasAnyAlerts }
  const [modalContradiction, setModalContradiction] = useState(null); // { contradictions: [], inference: {} }
  const [contradictionIdx, setContradictionIdx] = useState(0); // índice da contradição atual no fluxo
  const [carregando, setCarregando] = useState(false);
  const [modoComparacao, setModoComparacao] = useState(false);
  const [erro, setErro] = useState('');
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const [uploadingCandidate, setUploadingCandidate] = useState(null);
  const [arquivoCognitivoNome, setArquivoCognitivoNome] = useState('');
  const [arquivoSocioNome, setArquivoSocioNome] = useState('');
  const [analiseExpandida, setAnaliseExpandida] = useState({});
  const [projecoesNarrativas, setProjecoesNarrativas] = useState({}); // { key: markdown }
  const [projecaoCarregandoKey, setProjecaoCarregandoKey] = useState(null); // key em geração
  const [modalProjecaoNarrativa, setModalProjecaoNarrativa] = useState(null); // { title, markdown }
  const [modoVisualizacao, setModoVisualizacao] = useState(false); // etapas 1 e 2 em modo só-leitura ao navegar de volta
  const [positionGuidanceExpanded, setPositionGuidanceExpanded] = useState(false);
  const [positionDimensionConfirmations, setPositionDimensionConfirmations] = useState({});
  const [modalEditarDimensao, setModalEditarDimensao] = useState(null); // { categoria }
  const [modalConfirmarEdicao, setModalConfirmarEdicao] = useState(null); // { etapaDestino } — modal antes de editar
  const [modalDeletarCandidato, setModalDeletarCandidato] = useState(null); // { candidateId, nome, temComparativa }
  const [ptsSalvos, setPtsSalvos] = useState(false); // true após PTs salvos com sucesso (controla navegação etapa 1→2)
  const [pseudonimoMap, setPseudonimoMap] = useState({}); // { candidateId: nomeReal } — substituição pós-output
  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersCarregando, setUsersCarregando] = useState(false);
  const [usersErro, setUsersErro] = useState('');
  const [usersFeedback, setUsersFeedback] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role: 'user_client',
  });
  const [userDraftMap, setUserDraftMap] = useState({});
  const [userActionLoadingMap, setUserActionLoadingMap] = useState({});
  const [globalTenants, setGlobalTenants] = useState([]);
  const [globalTenantsLoading, setGlobalTenantsLoading] = useState(false);
  const [globalTenantsError, setGlobalTenantsError] = useState('');
  const [globalTenantsFeedback, setGlobalTenantsFeedback] = useState('');
  const [globalTenantCreateLoading, setGlobalTenantCreateLoading] = useState(false);
  const [globalTenantEditMap, setGlobalTenantEditMap] = useState({});
  const [globalTenantEditLoadingMap, setGlobalTenantEditLoadingMap] = useState({});
  const [globalEnteringTenantId, setGlobalEnteringTenantId] = useState(null);
  const [globalUsersTenant, setGlobalUsersTenant] = useState(null);
  const [globalTenantTokenTouched, setGlobalTenantTokenTouched] = useState(false);
  const [globalTenantForm, setGlobalTenantForm] = useState(() => ({
    name: '',
    language: 'pt-BR',
    active: true,
    access_token: `tenant-${Math.random().toString(36).slice(2, 8)}`,
  }));
  const [provisionForm, setProvisionForm] = useState({
    name: '',
    email: '',
    role: 'user_client',
    status: 'active',
  });
  const [provisionSubmitting, setProvisionSubmitting] = useState(false);
  const [creditsSummary, setCreditsSummary] = useState(null);
  const [creditsHistory, setCreditsHistory] = useState([]);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState('');
  const [creditsFeedback, setCreditsFeedback] = useState('');
  const [creditsAdjustForm, setCreditsAdjustForm] = useState({
    amount_delta: '',
    reason: '',
  });
  const [creditsAdjustLoading, setCreditsAdjustLoading] = useState(false);

  const userId = usuarioAtivo?.id;
  const normalizeUserRole = (value) => {
    const role = String(value || '').trim().toLowerCase();
    if (role === 'admin-master' || role === 'master_admin') return 'master_admin';
    if (role === 'master-client' || role === 'admin_client') return 'admin_client';
    if (role === 'user-client' || role === 'user_client') return 'user_client';
    if (role === 'admin' || role === 'owner') return 'admin_client';
    return role;
  };
  const normalizeUserStatus = (value) => {
    const status = String(value || '').trim().toLowerCase();
    if (status === 'invited' || status === 'active' || status === 'inactive') return status;
    return 'active';
  };
  const normalizedUserRole = normalizeUserRole(usuarioAtivo?.type);
  const isMasterAdmin = normalizedUserRole === 'master_admin';
  const canEditWorkPrinciples = ['admin_client', 'master_admin'].includes(normalizedUserRole);
  const canManageUsersTenantContext = ['admin_client', 'master_admin'].includes(normalizedUserRole) && !isGlobalAdminContext;
  const canManageUsersGlobalContext = isMasterAdmin && isGlobalAdminContext;
  const canManageUsers = canManageUsersTenantContext || canManageUsersGlobalContext;
  const canAdjustCredits = normalizedUserRole === 'master_admin' && !isGlobalAdminContext;
  const buildHeaders = (overrideSessionToken = null) => {
    const headers = { 'Content-Type': 'application/json' };
    const effectiveToken = overrideSessionToken || sessionToken;
    if (effectiveToken) headers.Authorization = `Bearer ${effectiveToken}`;
    return headers;
  };
  const getRoleLabel = (role) => {
    const normalized = normalizeUserRole(role);
    if (normalized === 'master_admin') return t('roleLabelMasterAdmin');
    if (normalized === 'admin_client') return t('roleLabelAdminClient');
    return t('roleLabelUserClient');
  };
  const getStatusLabel = (status) => {
    const normalized = normalizeUserStatus(status);
    if (normalized === 'invited') return t('statusLabelInvited');
    if (normalized === 'inactive') return t('statusLabelInactive');
    return t('statusLabelActive');
  };
  const getCreditOperationLabel = (operation) => {
    const normalized = String(operation || '').trim().toLowerCase();
    if (normalized === 'use') return t('creditsOperationUse');
    if (normalized === 'add') return t('creditsOperationAdd');
    if (normalized === 'refund') return t('creditsOperationRefund');
    if (normalized === 'adjust') return t('creditsOperationAdjust');
    return normalized || '-';
  };
  const getCreditSignedAmount = ({ operation, amount }) => {
    const normalized = String(operation || '').trim().toLowerCase();
    const value = Number(amount || 0);
    const sign = normalized === 'use' || normalized === 'adjust' ? '-' : '+';
    return `${sign}${Math.abs(value)}`;
  };
  const getCreditAmountClass = (operation) => {
    const normalized = String(operation || '').trim().toLowerCase();
    if (normalized === 'use' || normalized === 'adjust') return 'credit-amount-negative';
    return 'credit-amount-positive';
  };
  const getAnalysisTypeLabel = (analysisType) => {
    if (analysisType === 'individual') return t('individualShort');
    if (analysisType === 'comparative') return t('comparativeShort');
    return t('creditsUnknownAnalysis');
  };

  const getLastContextStorageKey = (email) => {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) return null;
    return `${LAST_CONTEXT_STORAGE_PREFIX}${normalized}`;
  };

  const readLastContextValue = (email) => {
    const key = getLastContextStorageKey(email);
    if (!key || typeof window === 'undefined') return '';
    try {
      return String(window.localStorage.getItem(key) || '');
    } catch {
      return '';
    }
  };

  const writeLastContextValue = (email, contextValue) => {
    const key = getLastContextStorageKey(email);
    if (!key || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, String(contextValue || ''));
    } catch {
      // no-op (storage pode estar indisponível)
    }
  };

  const contextValueFromOption = (option) => {
    if (!option || typeof option !== 'object') return '';
    if (option.context_type === 'global_admin') return 'global_admin';
    if (!option.company_id) return '';
    return `company:${option.company_id}`;
  };

  const parseContextValue = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return { context_type: 'company', company_id: null };
    if (raw === 'global_admin') return { context_type: 'global_admin', company_id: null };
    if (raw.startsWith('company:')) return { context_type: 'company', company_id: raw.slice('company:'.length) };
    return { context_type: 'company', company_id: raw };
  };

  const buildContextOptions = (contexts = [], hasGlobalAdmin = false) => {
    const list = Array.isArray(contexts) ? contexts : [];
    const companies = list
      .filter((item) => item && item.context_type === 'company' && item.company_id && item.user)
      .map((item) => ({
        ...item,
        context_type: 'company',
        value: contextValueFromOption(item),
      }));

    if (hasGlobalAdmin) {
      companies.unshift({
        context_type: 'global_admin',
        value: 'global_admin',
      });
    }

    const dedup = new Map();
    companies.forEach((item) => {
      if (!item.value) return;
      if (!dedup.has(item.value)) dedup.set(item.value, item);
    });
    return Array.from(dedup.values());
  };

  const currentContextValue = isGlobalAdminContext
    ? 'global_admin'
    : (sessao?.company_id ? `company:${sessao.company_id}` : '');

  const extractApiErrorMessage = (data, fallback = 'erro desconhecido') => {
    const message = String(
      data?.error || data?.errorMessage || data?.message || ''
    ).trim();
    return message || fallback;
  };
  const appendRequestId = (message, data, response) => {
    const requestId = String(
      data?.request_id ||
      response?.headers?.get('x-nf-request-id') ||
      response?.headers?.get('x-request-id') ||
      ''
    ).trim();
    return requestId ? `${message} [req ${requestId}]` : message;
  };
  const readJsonResponseSafe = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  const buildTenantTokenCandidate = (name) => {
    const slug = String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '') || 'tenant';
    const suffix = Math.random().toString(36).slice(2, 8);
    return `${slug}-${suffix}`;
  };

  const createDefaultGlobalTenantForm = (name = '') => ({
    name,
    language: 'pt-BR',
    active: true,
    access_token: buildTenantTokenCandidate(name),
  });

  const usersTargetCompanyId = isGlobalAdminContext
    ? (globalUsersTenant?.company_id || null)
    : companyId;

  const applyTenantUsersSnapshot = (users = []) => {
    const normalizedUsers = (Array.isArray(users) ? users : []).map((item) => ({
      ...item,
      role: normalizeUserRole(item.role || item.role_raw || item.type),
      status: normalizeUserStatus(item.status),
    }));

    const nextDraftMap = {};
    normalizedUsers.forEach((item) => {
      nextDraftMap[item.id] = {
        role: item.role,
        status: item.status,
      };
    });

    setTenantUsers(normalizedUsers);
    setUserDraftMap(nextDraftMap);
  };

  const resetUserManagementState = () => {
    setTenantUsers([]);
    setUsersCarregando(false);
    setUsersErro('');
    setUsersFeedback('');
    setInviteSubmitting(false);
    setInviteForm({
      name: '',
      email: '',
      role: 'user_client',
    });
    setUserDraftMap({});
    setUserActionLoadingMap({});
  };

  const resetGlobalAdminState = () => {
    setGlobalTenants([]);
    setGlobalTenantsLoading(false);
    setGlobalTenantsError('');
    setGlobalTenantsFeedback('');
    setGlobalTenantCreateLoading(false);
    setGlobalTenantEditMap({});
    setGlobalTenantEditLoadingMap({});
    setGlobalEnteringTenantId(null);
    setGlobalUsersTenant(null);
    setGlobalTenantTokenTouched(false);
    setGlobalTenantForm(createDefaultGlobalTenantForm(''));
    setProvisionForm({
      name: '',
      email: '',
      role: 'user_client',
      status: 'active',
    });
    setProvisionSubmitting(false);
  };

  const resetCreditsState = () => {
    setCreditsSummary(null);
    setCreditsHistory([]);
    setCreditsLoading(false);
    setCreditsError('');
    setCreditsFeedback('');
    setCreditsAdjustForm({
      amount_delta: '',
      reason: '',
    });
    setCreditsAdjustLoading(false);
  };

  const setUserActionLoading = (targetUserId, isLoading) => {
    setUserActionLoadingMap((prev) => {
      const next = { ...prev };
      if (isLoading) next[targetUserId] = true;
      else delete next[targetUserId];
      return next;
    });
  };

  const hasUserDraftChanges = (user) => {
    const draft = userDraftMap[user.id];
    if (!draft) return false;
    return (
      normalizeUserRole(draft.role) !== normalizeUserRole(user.role)
      || normalizeUserStatus(draft.status) !== normalizeUserStatus(user.status)
    );
  };

  const updateUserDraftField = (user, field, value) => {
    if (!user?.id) return;
    setUserDraftMap((prev) => {
      const current = prev[user.id] || {
        role: normalizeUserRole(user.role),
        status: normalizeUserStatus(user.status),
      };
      const next = {
        ...current,
        [field]: field === 'role' ? normalizeUserRole(value) : normalizeUserStatus(value),
      };
      return {
        ...prev,
        [user.id]: next,
      };
    });
  };

  const carregarUsuariosTenant = async ({ companyIdOverride = null, keepFeedback = false } = {}) => {
    if (!canManageUsers) return;

    const targetCompanyId = companyIdOverride || usersTargetCompanyId;
    if (!targetCompanyId) {
      setUsersErro(t('errGlobalUsersTenantMissing'));
      return;
    }

    setUsersCarregando(true);
    setUsersErro('');
    if (!keepFeedback) setUsersFeedback('');

    try {
      const response = await fetch(`${FUNCTIONS_URL}/listCompanyUsers`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ company_id: targetCompanyId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }
      applyTenantUsersSnapshot(data.users || []);
    } catch (err) {
      setUsersErro(t('errUsersLoad', { error: err.message }));
    } finally {
      setUsersCarregando(false);
    }
  };

  const abrirGestaoUsuarios = async () => {
    setGlobalUsersTenant(null);
    setTelaAtual('usuarios');
    await carregarUsuariosTenant({ companyIdOverride: companyId });
  };

  const abrirGestaoUsuariosGlobal = async (tenant) => {
    if (!tenant?.company_id) return;
    setGlobalUsersTenant(tenant);
    setTelaAtual('global-usuarios');
    await carregarUsuariosTenant({ companyIdOverride: tenant.company_id });
  };

  const setTenantEditLoading = (tenantId, isLoading) => {
    setGlobalTenantEditLoadingMap((prev) => {
      const next = { ...prev };
      if (isLoading) next[tenantId] = true;
      else delete next[tenantId];
      return next;
    });
  };

  const loadGlobalTenants = async ({ keepFeedback = false } = {}) => {
    if (!canManageUsersGlobalContext) return;
    setGlobalTenantsLoading(true);
    setGlobalTenantsError('');
    if (!keepFeedback) setGlobalTenantsFeedback('');

    try {
      const response = await fetch(`${FUNCTIONS_URL}/listGlobalTenants`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ include_inactive: false }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }

      const tenants = Array.isArray(data.tenants) ? data.tenants : [];
      setGlobalTenants(tenants);
      setGlobalTenantEditMap((prev) => {
        const next = { ...prev };
        tenants.forEach((tenant) => {
          if (!tenant?.company_id) return;
          if (!next[tenant.company_id]) {
            next[tenant.company_id] = {
              name: tenant.name || '',
              language: tenant.language || 'pt-BR',
              active: tenant.active !== false,
            };
          }
        });
        return next;
      });
    } catch (err) {
      setGlobalTenantsError(t('errGlobalTenantsLoad', { error: err.message }));
    } finally {
      setGlobalTenantsLoading(false);
    }
  };

  const handleGlobalTenantFieldChange = (field, value) => {
    setGlobalTenantForm((prev) => {
      if (field === 'name') {
        const nextName = String(value || '');
        if (globalTenantTokenTouched) return { ...prev, name: nextName };
        return {
          ...prev,
          name: nextName,
          access_token: buildTenantTokenCandidate(nextName),
        };
      }
      if (field === 'active') return { ...prev, active: Boolean(value) };
      return { ...prev, [field]: value };
    });
  };

  const regenerateGlobalTenantToken = () => {
    setGlobalTenantTokenTouched(true);
    setGlobalTenantForm((prev) => ({
      ...prev,
      access_token: buildTenantTokenCandidate(prev.name),
    }));
  };

  const handleCreateGlobalTenant = async (e) => {
    e.preventDefault();
    const tenantName = String(globalTenantForm.name || '').trim();
    if (!tenantName) {
      setGlobalTenantsError(t('errGlobalTenantCreate', { error: 'name é obrigatório' }));
      return;
    }

    setGlobalTenantCreateLoading(true);
    setGlobalTenantsError('');
    setGlobalTenantsFeedback('');

    try {
      const response = await fetch(`${FUNCTIONS_URL}/createGlobalTenant`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          name: tenantName,
          language: globalTenantForm.language,
          active: Boolean(globalTenantForm.active),
          access_token: String(globalTenantForm.access_token || '').trim(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }

      const createdTenant = data?.tenant || null;
      setGlobalTenantsFeedback(t('globalTenantsCreateSuccess', { name: createdTenant?.name || tenantName }));
      setGlobalTenantTokenTouched(false);
      setGlobalTenantForm(createDefaultGlobalTenantForm(''));
      await loadGlobalTenants({ keepFeedback: true });
    } catch (err) {
      setGlobalTenantsError(t('errGlobalTenantCreate', { error: err.message }));
    } finally {
      setGlobalTenantCreateLoading(false);
    }
  };

  const updateGlobalTenantDraftField = (tenant, field, value) => {
    if (!tenant?.company_id) return;
    setGlobalTenantEditMap((prev) => {
      const current = prev[tenant.company_id] || {
        name: tenant.name || '',
        language: tenant.language || 'pt-BR',
        active: tenant.active !== false,
      };
      const next = {
        ...current,
        [field]: field === 'active' ? Boolean(value) : value,
      };
      return {
        ...prev,
        [tenant.company_id]: next,
      };
    });
  };

  const hasGlobalTenantChanges = (tenant) => {
    const draft = globalTenantEditMap[tenant.company_id];
    if (!draft) return false;
    return (
      String(draft.name || '').trim() !== String(tenant.name || '').trim()
      || String(draft.language || 'pt-BR') !== String(tenant.language || 'pt-BR')
      || Boolean(draft.active) !== Boolean(tenant.active !== false)
    );
  };

  const handleSaveGlobalTenant = async (tenant) => {
    if (!tenant?.company_id) return;
    const draft = globalTenantEditMap[tenant.company_id] || {
      name: tenant.name || '',
      language: tenant.language || 'pt-BR',
      active: tenant.active !== false,
    };
    const payload = {};
    if (String(draft.name || '').trim() !== String(tenant.name || '').trim()) payload.name = String(draft.name || '').trim();
    if (String(draft.language || 'pt-BR') !== String(tenant.language || 'pt-BR')) payload.language = draft.language;
    if (Boolean(draft.active) !== Boolean(tenant.active !== false)) payload.active = Boolean(draft.active);

    if (Object.keys(payload).length === 0) return;

    setTenantEditLoading(tenant.company_id, true);
    setGlobalTenantsError('');
    setGlobalTenantsFeedback('');
    try {
      const response = await fetch(`${FUNCTIONS_URL}/updateGlobalTenant`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          company_id: tenant.company_id,
          ...payload,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }

      const updatedTenant = data?.tenant || tenant;
      setGlobalTenantsFeedback(t('globalTenantsUpdateSuccess', { name: updatedTenant.name || tenant.name }));
      await loadGlobalTenants({ keepFeedback: true });
    } catch (err) {
      setGlobalTenantsError(t('errGlobalTenantUpdate', { error: err.message }));
    } finally {
      setTenantEditLoading(tenant.company_id, false);
    }
  };

  const handleProvisionTenantUser = async (e) => {
    e.preventDefault();
    if (!globalUsersTenant?.company_id) {
      setUsersErro(t('errGlobalUsersTenantMissing'));
      return;
    }
    const email = String(provisionForm.email || '').trim().toLowerCase();
    if (!email) {
      setUsersErro(t('errProvisionEmailRequired'));
      return;
    }

    setProvisionSubmitting(true);
    setUsersErro('');
    setUsersFeedback('');
    try {
      const response = await fetch(`${FUNCTIONS_URL}/provisionTenantUser`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          company_id: globalUsersTenant.company_id,
          name: String(provisionForm.name || '').trim() || null,
          email,
          role: provisionForm.role,
          status: provisionForm.status,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }

      setProvisionForm((prev) => ({ ...prev, name: '', email: '', role: prev.role, status: prev.status }));
      setUsersFeedback(t('userManagementProvisionSuccess', { email }));
      await carregarUsuariosTenant({ companyIdOverride: globalUsersTenant.company_id, keepFeedback: true });
      await loadGlobalTenants({ keepFeedback: true });
    } catch (err) {
      setUsersErro(t('errProvisionUser', { error: err.message }));
    } finally {
      setProvisionSubmitting(false);
    }
  };

  const carregarCreditosTenant = async ({ keepFeedback = false } = {}) => {
    if (!companyId) return;

    setCreditsLoading(true);
    setCreditsError('');
    if (!keepFeedback) setCreditsFeedback('');

    try {
      const [summaryResponse, historyResponse] = await Promise.all([
        fetch(`${FUNCTIONS_URL}/getCompanyCredits`, {
          method: 'POST',
          headers: buildHeaders(),
          body: JSON.stringify({ company_id: companyId }),
        }),
        fetch(`${FUNCTIONS_URL}/getCreditsHistory`, {
          method: 'POST',
          headers: buildHeaders(),
          body: JSON.stringify({ company_id: companyId, limit: 100 }),
        }),
      ]);

      const summaryData = await summaryResponse.json().catch(() => ({}));
      const historyData = await historyResponse.json().catch(() => ({}));

      if (!summaryResponse.ok || !summaryData.success) {
        throw new Error(extractApiErrorMessage(summaryData));
      }
      if (!historyResponse.ok || !historyData.success) {
        throw new Error(extractApiErrorMessage(historyData));
      }

      setCreditsSummary({
        company: summaryData.company || null,
        credit_policy: summaryData.credit_policy || null,
      });
      setCreditsHistory(Array.isArray(historyData.history) ? historyData.history : []);
    } catch (err) {
      setCreditsError(t('errCreditsLoad', { error: err.message }));
    } finally {
      setCreditsLoading(false);
    }
  };

  const abrirCreditos = async () => {
    setTelaAtual('creditos');
    await carregarCreditosTenant();
  };

  const abrirFaq = () => {
    setFaqExpanded((prev) => {
      const hasExisting = faqItems.some((item) => Object.prototype.hasOwnProperty.call(prev, item.id));
      if (hasExisting) return prev;
      return buildFaqExpansionMap(faqItems);
    });
    setTelaAtual('faq');
  };

  const handleCreditAdjustSubmit = async (e) => {
    e.preventDefault();
    const amountDelta = Number(creditsAdjustForm.amount_delta);
    if (!Number.isFinite(amountDelta) || !Number.isInteger(amountDelta) || amountDelta === 0) {
      setCreditsError(t('errCreditsAdjustInvalidAmount'));
      return;
    }

    setCreditsAdjustLoading(true);
    setCreditsError('');
    setCreditsFeedback('');

    try {
      const response = await fetch(`${FUNCTIONS_URL}/adjustCompanyCredits`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          company_id: companyId,
          amount_delta: amountDelta,
          reason: String(creditsAdjustForm.reason || '').trim() || null,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }

      setCreditsAdjustForm({ amount_delta: '', reason: '' });
      setCreditsFeedback(t('creditsAdjustSuccess'));
      await carregarCreditosTenant({ keepFeedback: true });
    } catch (err) {
      setCreditsError(t('errCreditsAdjust', { error: err.message }));
    } finally {
      setCreditsAdjustLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!INVITES_ENABLED) {
      setUsersErro(t('userManagementInviteDisabledBanner'));
      return;
    }

    const targetCompanyId = usersTargetCompanyId;
    if (!targetCompanyId) {
      setUsersErro(t('errGlobalUsersTenantMissing'));
      return;
    }

    const email = String(inviteForm.email || '').trim().toLowerCase();
    if (!email) {
      setUsersErro(t('errInviteEmailRequired'));
      return;
    }

    const allowedInviteRoles = normalizedUserRole === 'master_admin'
      ? ['master_admin', 'admin_client', 'user_client']
      : ['admin_client', 'user_client'];
    const desiredRole = normalizeUserRole(inviteForm.role);
    const inviteRole = allowedInviteRoles.includes(desiredRole) ? desiredRole : 'user_client';

    setInviteSubmitting(true);
    setUsersErro('');
    setUsersFeedback('');

    try {
      const response = await fetch(`${FUNCTIONS_URL}/inviteCompanyUser`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          company_id: targetCompanyId,
          requester_user_id: userId,
          invite_email: email,
          invite_name: String(inviteForm.name || '').trim() || null,
          invite_role: inviteRole,
          redirect_to: `${window.location.origin}/auth-callback.html`,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }

      if (data.delivery_method === 'password_recovery') {
        setUsersFeedback(t('userManagementResendResetSuccess', { email: data.invited_email || email }));
      } else {
        setUsersFeedback(t('userManagementInviteSuccess', { email: data.invited_email || email }));
      }
      setInviteForm((prev) => ({ ...prev, name: '', email: '', role: inviteRole }));
      await carregarUsuariosTenant({ keepFeedback: true });
    } catch (err) {
      setUsersErro(t('errInviteUser', { error: err.message }));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleResendUserAccess = async (user) => {
    if (!user?.id) return;
    if (!INVITES_ENABLED) {
      setUsersErro(t('userManagementInviteDisabledBanner'));
      return;
    }

    const targetCompanyId = usersTargetCompanyId;
    if (!targetCompanyId) {
      setUsersErro(t('errGlobalUsersTenantMissing'));
      return;
    }

    setUsersErro('');
    setUsersFeedback('');
    setUserActionLoading(user.id, true);

    try {
      const response = await fetch(`${FUNCTIONS_URL}/resendCompanyUserInvite`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          company_id: targetCompanyId,
          target_user_id: user.id,
          redirect_to: `${window.location.origin}/auth-callback.html`,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }

      if (data.delivery_method === 'password_recovery') {
        setUsersFeedback(t('userManagementResendResetSuccess', { email: data.email || user.email }));
      } else {
        setUsersFeedback(t('userManagementResendInviteSuccess', { email: data.email || user.email }));
      }
      await carregarUsuariosTenant({ keepFeedback: true });
    } catch (err) {
      setUsersErro(t('errResendUserAccess', { error: err.message }));
    } finally {
      setUserActionLoading(user.id, false);
    }
  };

  const handleSaveUserAccess = async (user) => {
    if (!user?.id) return;
    const targetCompanyId = usersTargetCompanyId;
    if (!targetCompanyId) {
      setUsersErro(t('errGlobalUsersTenantMissing'));
      return;
    }

    const draft = userDraftMap[user.id] || {
      role: normalizeUserRole(user.role),
      status: normalizeUserStatus(user.status),
    };
    const nextRole = normalizeUserRole(draft.role);
    const nextStatus = normalizeUserStatus(draft.status);
    const roleChanged = nextRole !== normalizeUserRole(user.role);
    const statusChanged = nextStatus !== normalizeUserStatus(user.status);

    if (!roleChanged && !statusChanged) {
      setUsersFeedback(t('userManagementNoChanges'));
      return;
    }

    const payload = {
      company_id: targetCompanyId,
      target_user_id: user.id,
    };
    if (roleChanged) payload.new_role = nextRole;
    if (statusChanged) payload.new_status = nextStatus;

    setUsersErro('');
    setUsersFeedback('');
    setUserActionLoading(user.id, true);

    try {
      const response = await fetch(`${FUNCTIONS_URL}/updateCompanyUserAccess`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(extractApiErrorMessage(data));
      }

      const updatedUser = data.user || {};
      const finalRole = normalizeUserRole(updatedUser.role || nextRole);
      const finalStatus = normalizeUserStatus(updatedUser.status || nextStatus);

      setTenantUsers((prev) => prev.map((item) => (
        item.id === user.id
          ? {
            ...item,
            role: finalRole,
            status: finalStatus,
            updated_at: updatedUser.updated_at || item.updated_at || null,
            deactivated_at: updatedUser.deactivated_at || null,
            deactivated_by: updatedUser.deactivated_by || null,
          }
          : item
      )));
      setUserDraftMap((prev) => ({
        ...prev,
        [user.id]: {
          role: finalRole,
          status: finalStatus,
        },
      }));
      setUsersFeedback(t('userManagementUpdateSuccess', { email: user.email }));
    } catch (err) {
      setUsersErro(t('errUpdateUserAccess', { error: err.message }));
    } finally {
      setUserActionLoading(user.id, false);
    }
  };

  useEffect(() => {
    if (!isGlobalAdminContext) return;
    if (telaAtual !== 'app') return;
    if (!canManageUsersGlobalContext) return;
    loadGlobalTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalAdminContext, telaAtual, canManageUsersGlobalContext]);

  // Se o Supabase redirecionar para "/#access_token=..." em vez de "/auth-callback.html",
  // força o callback dedicado para concluir recovery/invite.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname === '/auth-callback.html') return;

    const hashRaw = window.location.hash || '';
    const queryRaw = window.location.search || '';
    const hashParams = new URLSearchParams(hashRaw.startsWith('#') ? hashRaw.slice(1) : hashRaw);
    const queryParams = new URLSearchParams(queryRaw.startsWith('?') ? queryRaw.slice(1) : queryRaw);

    const type = String(hashParams.get('type') || queryParams.get('type') || '').trim().toLowerCase();
    const hasAccessToken = Boolean(hashParams.get('access_token') || queryParams.get('access_token'));
    const hasTokenHash = Boolean(queryParams.get('token_hash'));
    const hasAuthError = Boolean(hashParams.get('error') || queryParams.get('error'));
    const isRecoveryFlow = type === 'recovery' || type === 'invite';

    if (!isRecoveryFlow || (!hasAccessToken && !hasTokenHash && !hasAuthError)) return;

    window.location.replace(`/auth-callback.html${queryRaw}${hashRaw}`);
  }, []);

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
  });

  // Extrai o nome do candidato a partir do nome do arquivo PDF cognitivo.
  // Padrão garantido: tudo antes do primeiro " - " no nome do arquivo.
  // Ex: "Ângela Cavalcanti - Teste de Habilidades Cognitivas.pdf" → "Ângela Cavalcanti"
  const extrairNomeDoArquivo = (file) => {
    const nomeArquivo = file.name.replace(/\.pdf$/i, '');
    const partes = nomeArquivo.split(' - ');
    return partes[0].trim();
  };

  // ===========================================================================
  // PSEUDONIMIZAÇÃO — privacidade nas chamadas à OpenAI
  // O nome real nunca é enviado à API. Um pseudônimo neutro é gerado por
  // candidato e o mapeamento { candidateId → nomeReal } fica só no frontend.
  // Os outputs de texto são restaurados antes de exibir.
  // ===========================================================================

  const gerarPseudonimo = () => {
    const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const usados = new Set(Object.values(pseudonimoMap).map(v => v.pseudo));
    for (const letra of letras) {
      const pseudo = `Candidato ${letra}`;
      if (!usados.has(pseudo)) return pseudo;
    }
    // Fallback improvável (>26 candidatos na sessão)
    return `Candidato ${Date.now()}`;
  };

  // Infere gênero a partir do primeiro nome, sem enviar à OpenAI.
  // Heurística baseada em sufixos comuns do português brasileiro.
  // Retorna 'F' (feminino), 'M' (masculino) ou null (incerto).
  const inferirGenero = (nomeCompleto) => {
    const primeiro = nomeCompleto.split(' ')[0].toLowerCase();
    // Nomes femininos comuns que terminam em -a mas podem enganar (exceções masculinas)
    const excecoesMasculinas = ['luca', 'nikita', 'joshua', 'danilo', 'camilo', 'murilo',
      'otávio', 'flávio', 'márcio', 'fábio', 'caio', 'dario', 'mário', 'túlio',
      'emilio', 'julio', 'sergio', 'rodrigo', 'thiago', 'diogo', 'tiago', 'iago',
      'leonardo', 'pedro', 'andréia', 'andrea', 'nicola', 'matia'];
    // Terminações fortemente femininas
    const termFemininas = ['elle', 'iane', 'iana', 'iela', 'iara', 'ilda', 'ilma',
      'inha', 'ínia', 'inia', 'issa', 'ista', 'lete', 'lene', 'lina', 'lise',
      'lize', 'lizi', 'nara', 'neia', 'nete', 'nice', 'nide', 'nile', 'nina',
      'nira', 'nise', 'nize', 'nzia', 'rlei', 'ucia', 'uela', 'uene', 'uise',
      'ulce', 'uma', 'uria'];
    // Terminações fortemente masculinas
    const termMasculinas = ['aldo', 'ando', 'ardo', 'ário', 'ario', 'erto', 'ésio',
      'ésio', 'ésio', 'inho', 'inho', 'inho', 'into', 'ique', 'írio', 'irio',
      'ismo', 'isto', 'ito', 'ivan', 'iver', 'izio', 'oberto', 'obert',
      'onaldo', 'ondo', 'onto', 'orge', 'orge', 'orte', 'osto', 'otto',
      'oudo', 'ovani', 'ovanni', 'rio', 'rson', 'son', 'ton', 'ugo', 'ulgo',
      'ulio', 'undo', 'urgo', 'uso', 'van'];

    if (excecoesMasculinas.includes(primeiro)) return 'M';
    for (const t of termFemininas) { if (primeiro.endsWith(t)) return 'F'; }
    for (const t of termMasculinas) { if (primeiro.endsWith(t)) return 'M'; }
    // Regra geral: termina em -a → feminino, exceto -ma e exceções
    if (primeiro.endsWith('a') && !primeiro.endsWith('ma')) return 'F';
    // Termina em consoante ou vogal diferente de -a → masculino (heurística)
    if (/[bcdfghjklmnpqrstvwxyz]$/.test(primeiro)) return 'M';
    if (/[oueiy]$/.test(primeiro)) return 'M';
    return null; // incerto
  };

  const extrairPrimeiroNome = (nomeCompleto = '') => {
    const nome = String(nomeCompleto || '').trim();
    if (!nome) return '';
    return nome.split(' ')[0].trim();
  };

  const aplicarConcordanciaGenero = (texto, genero) => {
    if (!texto || genero !== 'F') return texto;
    return String(texto)
      .replace(/\bcandidato\b/g, 'candidata')
      .replace(/\bCandidato\b/g, 'Candidata')
      .replace(/\bo candidato\b/g, 'a candidata')
      .replace(/\bO candidato\b/g, 'A candidata')
      .replace(/\bo candidata\b/g, 'a candidata')
      .replace(/\bO candidata\b/g, 'A candidata')
      .replace(/\brecomendado\b/g, 'recomendada')
      .replace(/\bRecomendado\b/g, 'Recomendada')
      .replace(/\bnão recomendado\b/g, 'não recomendada')
      .replace(/\bNão Recomendado\b/g, 'Não Recomendada')
      .replace(/\bqualificado\b/g, 'qualificada')
      .replace(/\bpreparado\b/g, 'preparada')
      .replace(/\badaptado\b/g, 'adaptada')
      .replace(/\bposicionado\b/g, 'posicionada')
      .replace(/\bmotivado\b/g, 'motivada')
      .replace(/\bele\b/g, 'ela')
      .replace(/\bEle\b/g, 'Ela')
      .replace(/\bdele\b/g, 'dela')
      .replace(/\bDele\b/g, 'Dela')
      .replace(/\bnele\b/g, 'nela')
      .replace(/\bNele\b/g, 'Nela')
      .replace(/\bseu\b/g, 'sua')
      .replace(/\bSeu\b/g, 'Sua')
      .replace(/\bseus\b/g, 'suas')
      .replace(/\bSeus\b/g, 'Suas');
  };

  // Substitui pseudônimos pelos nomes reais em um texto de output.
  // Regra: em títulos markdown (## e ###), usa nome inteiro; no texto corrido, usa primeiro nome.
  // Também corrige pronomes de gênero quando o gênero pode ser inferido do nome.
  const restaurarNomes = (texto, generoPreferencial = null) => {
    if (!texto) return texto;
    return texto.split('\n').map(linha => {
      const eTitulo = /^#{1,3} /.test(linha);
      let resultado = linha;
      Object.values(pseudonimoMap).forEach(({ pseudo, nomeReal }) => {
        const primeiroNome = extrairPrimeiroNome(nomeReal);
        const substituto = eTitulo ? nomeReal : primeiroNome;
        resultado = resultado.replace(new RegExp(pseudo, 'g'), substituto);
      });
      // Correção opcional e contextual: aplicar apenas quando o gênero do candidato-alvo for conhecido.
      if (generoPreferencial) resultado = aplicarConcordanciaGenero(resultado, generoPreferencial);
      return resultado;
    }).join('\n');
  };

  // Restaura pseudônimo em uma string simples (não markdown) — ex: lista de nomes na Biblioteca.
  // Usa o nome completo em vez do primeiro nome, pois é um campo de identificação.
  const restaurarNomeSimples = (texto) => {
    if (!texto) return texto;
    let resultado = texto;
    Object.values(pseudonimoMap).forEach(({ pseudo, nomeReal }) => {
      resultado = resultado.replace(new RegExp(pseudo, 'g'), nomeReal);
    });
    return resultado;
  };

  const restaurarAliasesProjecao = (texto, nomesExibicao = []) => {
    if (!texto || !Array.isArray(nomesExibicao) || nomesExibicao.length === 0) return texto;
    const nomes = nomesExibicao.map((nome) => String(nome || '').trim()).filter(Boolean);
    const singleCandidate = nomes.length === 1 ? nomes[0] : null;
    const singleFirstName = singleCandidate ? extrairPrimeiroNome(singleCandidate) : null;
    const singleGender = singleCandidate ? inferirGenero(singleCandidate) : null;

    return texto.split('\n').map((linha) => {
      const eTitulo = /^#{1,3} /.test(linha);
      let resultado = linha;

      nomes.forEach((nome, index) => {
        const letter = String.fromCharCode(65 + index);
        const aliases = [`Candidato ${letter}`, `Candidate ${letter}`];
        const substituto = eTitulo ? nome : (extrairPrimeiroNome(nome) || nome);
        aliases.forEach((alias) => {
          const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          resultado = resultado.replace(new RegExp(`\\b${escaped}\\b`, 'gi'), substituto);
        });
      });

      if (singleCandidate && singleFirstName) {
        const displayName = eTitulo ? singleCandidate : singleFirstName;
        resultado = resultado
          .replace(/\bo perfil\b/gi, displayName)
          .replace(/\ba pessoa\b/gi, displayName)
          .replace(/\bthe profile\b/gi, displayName)
          .replace(/\bthis person\b/gi, displayName);
        resultado = aplicarConcordanciaGenero(resultado, singleGender);
      }

      return resultado;
    }).join('\n');
  };

  const normalizarComparativaComNomes = (texto, nomesExibicao = []) => {
    const nomes = (nomesExibicao || []).map((nome) => String(nome || '').trim()).filter(Boolean);
    const isAliasCandidateLabel = (value) => /^(candidato|candidate)\s+[a-z]$/i.test(String(value || '').trim());
    const resolve = (letter) => {
      const raw = nomes[letter.charCodeAt(0) - 65];
      if (!raw) return letter;
      if (isAliasCandidateLabel(raw)) return raw;
      return extrairPrimeiroNome(raw) || raw;
    };
    const composeCandidateReference = (prefix, letter) => {
      const resolved = String(resolve(letter) || '').trim();
      // Se já resolvemos para nome real, removemos o prefixo "Candidato/Candidate"
      // para evitar construções artificiais como "Candidato Lucas".
      if (!isAliasCandidateLabel(resolved)) return resolved;
      const aliasLetter = resolved.split(/\s+/).pop();
      if (/(s)$/i.test(String(prefix || '').trim())) return `${prefix} ${aliasLetter}`;
      return resolved;
    };
    let resultado = restaurarNomes(texto || '');

    resultado = resultado.replace(/\b([A-Z])\s+e\s+([A-Z])\b/g, (_, a, b) => `${resolve(a)} e ${resolve(b)}`);
    resultado = resultado.replace(/\b([A-Z])\s+ou\s+([A-Z])\b/gi, (_, a, b) => `${resolve(a)} ou ${resolve(b)}`);
    resultado = resultado.replace(/\b([A-Z])\s+and\s+([A-Z])\b/gi, (_, a, b) => `${resolve(a)} and ${resolve(b)}`);
    resultado = resultado.replace(/\b([A-Z])\s+or\s+([A-Z])\b/gi, (_, a, b) => `${resolve(a)} or ${resolve(b)}`);
    resultado = resultado.replace(/\b(Prefira)\s+([A-Z])(\s+se\b)/gi, (_, p1, letter, p3) => `${p1} ${resolve(letter)}${p3}`);
    resultado = resultado.replace(/\b(Prefer)\s+([A-Z])(\s+if\b)/gi, (_, p1, letter, p3) => `${p1} ${resolve(letter)}${p3}`);
    resultado = resultado.replace(/\b(Candidato(?:s)?)\s+([A-Z])\b/gi, (_, p1, letter) => composeCandidateReference(p1, letter));
    resultado = resultado.replace(/\b(Candidate(?:s)?)\s+([A-Z])\b/gi, (_, p1, letter) => composeCandidateReference(p1, letter));
    resultado = resultado.replace(/^(\s*[-*]\s*)([A-Z])(\s*:)/gim, (_, p1, letter, p3) => `${p1}${resolve(letter)}${p3}`);
    // Limpeza defensiva para casos em que o texto já veio com nome real precedido
    // de "Candidato/Candidate".
    resultado = resultado.replace(/\bCandidatos?\s+([A-ZÀ-Ý][\wÀ-ÿ-]*)/g, '$1');
    resultado = resultado.replace(/\bCandidates?\s+([A-Z][A-Za-z'-]*)/g, '$1');
    return resultado;
  };

  const anonimizarTextoParaComparativa = (texto) => {
    if (!texto) return texto;
    let resultado = texto;
    Object.values(pseudonimoMap).forEach(({ pseudo, nomeReal }) => {
      if (!nomeReal || !pseudo) return;
      const escaped = nomeReal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      resultado = resultado.replace(new RegExp(`\\b${escaped}\\b`, 'g'), pseudo);
      const primeiroNome = nomeReal.split(' ')[0]?.trim();
      if (primeiroNome && primeiroNome.length > 1) {
        const firstEscaped = primeiroNome.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        resultado = resultado.replace(new RegExp(`\\b${firstEscaped}\\b`, 'g'), pseudo);
      }
    });
    return resultado;
  };

  const montarAnalisesIndividuaisParaComparativa = () => candidatos.map(c => {
    const pseudo = pseudonimoMap[c.id]?.pseudo || c.nome;
    const decisoria = analisesIndividuais[c.id]?.decisoria || null;
    const analitica = analisesIndividuais[c.id]?.analitica || null;

    return {
      candidate_id: c.id,
      nome: ENABLE_LEGACY_COMPARATIVE_PII ? c.nome : pseudo,
      analise_ok: c.analise_ok,
      decisoria: decisoria
        ? (ENABLE_LEGACY_COMPARATIVE_PII ? restaurarNomes(decisoria, inferirGenero(c.nome)) : anonimizarTextoParaComparativa(decisoria))
        : null,
      analitica: analitica
        ? (ENABLE_LEGACY_COMPARATIVE_PII ? restaurarNomes(analitica, inferirGenero(c.nome)) : anonimizarTextoParaComparativa(analitica))
        : null,
      recomendacao: analisesIndividuais[c.id]?.recomendacao || null,
      genero: inferirGenero(c.nome),
    };
  });

  const limparCorpoProjecao = (texto) => {
    if (!texto) return '';
    let resultado = texto;
    // Remove heading duplicado do topo (o título já está no modal)
    resultado = resultado.replace(/^##\s+(Projeção Narrativa|Narrative Projection)[^\n]*\n+/i, '');
    // Remove seção "Aviso de leitura/Reading note" quando vier no payload
    resultado = resultado.replace(/##\s+(Aviso de leitura|Reading note)[\s\S]*?(?=\n##\s|\s*$)/i, '');

    const oldFactsRegex = /##\s+(Notas de referência|Reference Notes)\s*([\s\S]*)$/i;
    const oldFactsMatch = resultado.match(oldFactsRegex);
    if (oldFactsMatch) {
      const factualHeading = tenantLocale === 'en-US'
        ? '## Factual basis of this projection'
        : '## Base factual desta projeção';
      const rawBullets = String(oldFactsMatch[2] || '')
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => /^[-*]\s+/.test(line))
        .map((line) => line.replace(/^[-*]\s+/, ''));

      const sanitizeBullet = (line) => {
        let clean = String(line || '')
          .replace(/\[Ref\d+\]\s*/gi, '')
          .replace(/\b(TD|HA|AA|LD|OC|IN)\d+\b/g, '')
          .replace(/[ \t]+/g, ' ')
          .replace(/\s+\|/g, ' |')
          .replace(/\|\s+/g, '| ')
          .replace(/\s+\./g, '.')
          .trim();

        if (!clean) return '';

        if (/^Dimensões críticas da vaga:/i.test(clean)) {
          const tail = clean.split(':').slice(1).join(':');
          const labels = tail
            .split('|')
            .map((part) => part.split(':')[0].trim())
            .filter(Boolean);
          if (!labels.length) return '';
          clean = `Dimensões mais exigidas na vaga: ${labels.join(', ')}.`;
        } else if (/^Critical role dimensions:/i.test(clean)) {
          const tail = clean.split(':').slice(1).join(':');
          const labels = tail
            .split('|')
            .map((part) => part.split(':')[0].trim())
            .filter(Boolean);
          if (!labels.length) return '';
          clean = `Most demanded role dimensions: ${labels.join(', ')}.`;
        }

        return clean;
      };

      const dedup = [];
      rawBullets.forEach((bullet) => {
        const clean = sanitizeBullet(bullet);
        if (!clean) return;
        if (dedup.some((item) => item.toLowerCase() === clean.toLowerCase())) return;
        dedup.push(clean);
      });

      const selectedBullets = dedup.slice(0, 3);
      resultado = resultado.replace(oldFactsRegex, '').trim();
      if (selectedBullets.length > 0) {
        resultado = `${resultado}\n\n${factualHeading}\n${selectedBullets.map((bullet) => `- ${bullet}`).join('\n')}`.trim();
      }
    }

    resultado = resultado.replace(/\[Ref\d+\]\s*/gi, '');
    return resultado.trim();
  };


  const handleUploadCandidate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const pdfCognitivo = formData.get('pdfCognitivo');
    const pdfSocioemocional = formData.get('pdfSocioemocional');

    if (!pdfCognitivo || !pdfSocioemocional) {
      setErro(t('errSelectBothPdfs'));
      return;
    }

    const nome = extrairNomeDoArquivo(pdfCognitivo);
    const pseudo = gerarPseudonimo();

    setUploadingCandidate(nome);
    setErro('');

    try {
      const cognitivoBase64 = await fileToBase64(pdfCognitivo);
      const socioemocionalBase64 = await fileToBase64(pdfSocioemocional);

      // Passo 1: parsear os PDFs — envia pseudônimo como nome; nome real apenas para limpeza do texto
      const parseResponse = await fetch(`${FUNCTIONS_URL}/parsePDF`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          pdf_cognitive_base64: cognitivoBase64,
          pdf_socioemotional_base64: socioemocionalBase64,
          candidate_name: pseudo,           // pseudônimo — salvo no banco
          candidate_real_name: nome,        // nome real — usado só para limpar o texto do PDF, nunca persiste
          company_id: companyId,
          user_id: userId
        })
      });

      const parseData = await parseResponse.json();
      if (!parseData.success) { setErro(JSON.stringify(parseData, null, 2)); return; }

      const candidateId = parseData.candidate_id;

      // Registrar mapeamento pseudônimo → nome real (só no frontend, nunca persiste)
      setPseudonimoMap(prev => ({ ...prev, [candidateId]: { pseudo, nomeReal: nome } }));

      // Passo 2: criar registro pending no banco e obter analysis_id
      const triggerResponse = await fetch(`${FUNCTIONS_URL}/triggerAnalysis`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          candidate_id: candidateId,
          position_id: positionId,
          company_id: companyId,
          user_id: userId
        })
      });

      const triggerData = await triggerResponse.json();
      if (!triggerData.success) {
        setCandidatos(prev => [...prev, { id: candidateId, nome, analise_ok: false }]);
        setErro(t('errCandidateStartFail', { name: nome, error: triggerData.error || 'erro desconhecido' }));
        return;
      }

      const analysisId = triggerData.analysis_id;

      // Disparar background function diretamente do frontend (fire-and-forget).
      // O Netlify não permite que funções chamem outras funções via fetch —
      // a background function deve ser chamada pelo cliente diretamente.
      fetch(`${FUNCTIONS_URL}/analyzeCandidate-background`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          candidate_id: candidateId,
          position_id: positionId,
          company_id: companyId,
          user_id: userId,
          analysis_id: analysisId,
          candidate_gender: inferirGenero(nome), // 'M', 'F' ou null — nunca o nome real
        }),
      }).catch(err => {
        // Erro esperado: background functions retornam 202 e fecham a conexão
        // antes do fetch resolver — isso não é falha real.
        console.log('[upload] Background function disparada:', err.message);
      });
      e.target.reset();
      setArquivoCognitivoNome('');
      setArquivoSocioNome('');

      // Adiciona candidato na lista imediatamente (análise ainda pendente)
      setCandidatos(prev => [...prev, { id: candidateId, nome, analise_ok: false }]);

      // Libera o botão de upload imediatamente — polling continua em background
      setUploadingCandidate(null);

      // Passo 3: polling a cada 3s até a análise ficar pronta (máximo 3 minutos)
      const MAX_TENTATIVAS = 60; // 60 × 3s = 180s
      let tentativas = 0;

      const poll = async () => {
        tentativas++;
        if (tentativas > MAX_TENTATIVAS) {
          setCandidatos(prev => prev.map(c =>
            c.id === candidateId ? { ...c, analise_ok: false, analise_timeout: true } : c
          ));
          return;
        }

        try {
          const statusResponse = await fetch(`${FUNCTIONS_URL}/getAnalysisStatus`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ analysis_id: analysisId, company_id: companyId })
          });

          const statusData = await statusResponse.json();

          if (statusData.status === 'completed') {
            setCandidatos(prev => prev.map(c =>
              c.id === candidateId ? { ...c, analise_ok: true } : c
            ));
            const generoInferido = inferirGenero(nome);
            const decisoriaComNome = restaurarNomes(statusData.decisory, generoInferido);
            const analiticaComNome = restaurarNomes(statusData.analytical, generoInferido);
            const narrativaPersistida = statusData.narrative
              ? restaurarAliasesProjecao(statusData.narrative, [nome])
              : null;
            setAnalisesIndividuais(prev => ({
              ...prev,
              [candidateId]: {
                analysis_id: statusData.analysis_id,
                // Restaurar nome real nos textos gerados pela OpenAI
                decisoria: decisoriaComNome,
                analitica: analiticaComNome,
                recomendacao: statusData.recommendation,
                narrativa: narrativaPersistida,
              }
            }));
            if (narrativaPersistida) {
              setProjecoesNarrativas(prev => ({
                ...prev,
                [`individual:${statusData.analysis_id}`]: narrativaPersistida,
              }));
            } else {
              // Pré-gerar projeção narrativa em background para reduzir espera na aba individual.
              gerarProjecaoNarrativa({
                analysisId: statusData.analysis_id,
                tipo: 'individual',
                nomesExibicao: [nome],
                silent: true,
              }).catch(() => {});
            }
          } else if (statusData.status === 'error') {
            setCandidatos(prev => prev.map(c =>
              c.id === candidateId ? { ...c, analise_ok: false } : c
            ));
            setErro(t('errCandidateProcessingFail', { name: nome }));
          } else {
            // Ainda pending — continuar polling
            setTimeout(poll, 3000);
          }
        } catch (pollError) {
          // Erro de rede no polling — tentar novamente
          setTimeout(poll, 3000);
        }
      };

      // Primeira consulta após 5s (dar tempo ao background de iniciar)
      setTimeout(poll, 5000);

    } catch (error) {
      setErro(t('errConnection', { error: error.message }));
      setUploadingCandidate(null);
    }
  };

  const atualizarPrincipios = async () => {
    try {
      const principiosFormatados = {};
      PRINCIPIOS_TRABALHO.forEach((pt, index) => {
        principiosFormatados[pt.id] = principios[index];
      });
      const response = await fetch(`${FUNCTIONS_URL}/updateWorkPrinciples`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ company_id: companyId, configured_by: userId, principles: principiosFormatados })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao atualizar princípios');
      setPtsSalvos(true);
      return true;
    } catch (error) {
      alert(t('errSavePrinciples', { error: error.message }));
      return false;
    }
  };

  const criarPosicao = async () => {
    try {
      const cargoLimpo = cargo.trim();
      const response = await fetch(`${FUNCTIONS_URL}/createPosition`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          company_id: companyId,
          created_by: userId,
          title: cargoLimpo,
          description: observacoes || null,
          dimensions: {
            habilidades_analiticas: dimensoes['Habilidades Analíticas'],
            tomada_de_decisao: dimensoes['Tomada de Decisão'],
            agilidade_e_aprendizado: dimensoes['Agilidade e Aprendizado'],
            lideranca: dimensoes['Liderança'],
            orientacao_para_cliente: dimensoes['Orientação para Cliente'],
            influencia_e_gestao_de_stakeholders: dimensoes['Influência e Gestão de Stakeholders']
          }
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erro ao criar posição');
      setPositionId(data.position_id);
      return data.position_id;
    } catch (error) {
      alert(t('errSavePosition', { error: error.message }));
      return null;
    }
  };

  // Abre o modal de confirmação de exclusão de candidato.
  // Detecta se há análise comparativa gerada para avisar o usuário.
  const abrirModalDeletar = (candidato) => {
    const temComparativa = !!comparativaCache;
    setModalDeletarCandidato({ candidateId: candidato.id, nome: candidato.nome, temComparativa });
  };

  // Executa a exclusão após confirmação no modal.
  const confirmarDeletarCandidato = async () => {
    const { candidateId, nome } = modalDeletarCandidato;
    setModalDeletarCandidato(null);

    try {
      const response = await fetch(`${FUNCTIONS_URL}/deleteCandidate`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ candidate_id: candidateId, company_id: companyId }),
      });
      const data = await response.json();
      if (!data.success) {
        setErro(t('errDeleteCandidate', { name: nome, error: data.error || 'erro desconhecido' }));
        return;
      }

      // Remover do estado local
      setCandidatos(prev => prev.filter(c => c.id !== candidateId));
      setAnalisesIndividuais(prev => {
        const novo = { ...prev };
        delete novo[candidateId];
        return novo;
      });
      setPseudonimoMap(prev => {
        const novo = { ...prev };
        delete novo[candidateId];
        return novo;
      });

      // Limpar comparativa se existia (foi deletada do banco também)
      setComparativaCache(null);
    setComparativaPollingId(null);
    setComparativaProcessando(false);
      setAnalises([]);
      setModoComparacao(false);

    } catch (err) {
      setErro(t('errDeleteCandidateConnection', { name: nome }));
    }
  };

  // Executa a comparativa — fire-and-forget + polling via getAnalysisStatus.
  // Evita timeout de 504 em chamadas longas ao GPT-4o.
  const executarComparativa = async (forceAll = false) => {
    setCarregando(false);
    setErro('');
    setAnalises([]);
    setModalComparativa(null);
    setComparativaProcessando(true);
    setComparativaPollingId(null);

    try {
      const semAnalise = candidatos.filter(c => !c.analise_ok || !analisesIndividuais[c.id]?.decisoria);
      if (semAnalise.length > 0) {
        setErro(t('errMissingCompleteAnalyses', { names: semAnalise.map(a => a.nome).join(', ') }));
        setComparativaProcessando(false);
        return;
      }

      const individualAnalysesPayload = montarAnalisesIndividuaisParaComparativa();

      // Disparar — backend retorna 202 com analysis_id imediatamente
      const response = await fetch(`${FUNCTIONS_URL}/compareCandidates`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          candidate_ids: candidatos.map(c => c.id),
          individual_analyses: individualAnalysesPayload,
          position_id: positionId,
          company_id: companyId,
          user_id: userId,
          force_all: forceAll,
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!data.success || !data.analysis_id) throw new Error(data.error || 'Erro ao iniciar comparativa');

      console.log('[comparativa] Iniciada. Disparando background e iniciando polling:', data.analysis_id);
      setComparativaPollingId(data.analysis_id);

      // Disparar background function diretamente — fire-and-forget.
      // O Netlify bloqueia fetch entre funções do mesmo site, por isso o frontend
      // dispara a background diretamente, sem aguardar resposta.
      fetch(`${FUNCTIONS_URL}/compareCandidates-background`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          analysis_id: data.analysis_id,
          candidate_ids: candidatos.map(c => c.id),
          individual_analyses: individualAnalysesPayload,
          position_id: positionId,
          company_id: companyId,
          user_id: userId,
          force_all: forceAll,
        }),
      }).catch(() => {}); // Erro esperado: background functions fecham a conexão

      // Polling — a cada 4s, até completar ou falhar
      const poll = async () => {
        try {
          const statusRes = await fetch(`${FUNCTIONS_URL}/getAnalysisStatus`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ analysis_id: data.analysis_id, company_id: companyId }),
          });
          const statusData = await statusRes.json();

          if (statusData.status === 'completed') {
            const texto = normalizarComparativaComNomes(
              statusData.comparative || '',
              candidatos.map((c) => c.nome)
            );
            const resultado = [{
              tipo: 'comparativa',
              texto,
              analysisId: data.analysis_id,
              recomendacao: statusData.recommendation || null,
            }];
            setAnalises(resultado);
            setComparativaCache(resultado);
            setComparativaProcessando(false);
            setComparativaPollingId(null);
          } else if (statusData.status === 'error') {
            setErro(t('errComparativeProcessing'));
            setComparativaProcessando(false);
            setComparativaPollingId(null);
          } else {
            setTimeout(poll, 4000);
          }
        } catch (pollErr) {
          console.error('[comparativa] Erro no polling:', pollErr);
          setTimeout(poll, 4000);
        }
      };

      setTimeout(poll, 5000);

    } catch (error) {
      setErro(t('errGeneric', { error: error.message }));
      setComparativaProcessando(false);
    }
  };

  const gerarAnalises = async () => {
    if (candidatos.length === 0) { setErro(t('errAddAtLeastOne')); return; }

    if (modoComparacao && candidatos.length >= 2) {
      // Verificar finalistas/not_recommended antes de disparar a chamada.
      // Sempre perguntar ao usuário quando houver pelo menos 1 not_recommended.
      const finalistas = candidatos.filter(c =>
        analisesIndividuais[c.id]?.recomendacao !== 'not_recommended'
      );
      const naoRecomendados = candidatos.filter(c =>
        analisesIndividuais[c.id]?.recomendacao === 'not_recommended'
      );

      if (naoRecomendados.length > 0) {
        // Sempre perguntar: incluir ou não candidatos not_recommended.
        // Se houver 0/1 finalista, excluir não é viável e só a inclusão permite seguir.
        setModalComparativa({
          nenhum: finalistas.length === 0,
          finalistaNome: finalistas.length === 1 ? finalistas[0].nome : null,
          naoRecomendados: naoRecomendados.map(c => c.nome),
          podeExcluir: finalistas.length >= 2,
        });
        return;
      }

      // Sem not_recommended no pool — fluxo normal.
      await executarComparativa(false);
    } else {
      // Modo individual
      setAnalises(candidatos.map(c => ({
        candidato: c.nome,
        candidatoId: c.id,
        analysisId: analisesIndividuais[c.id]?.analysis_id || null,
        decisoria: analisesIndividuais[c.id]?.decisoria || '',
        analitica: analisesIndividuais[c.id]?.analitica || '',
        recomendacao: analisesIndividuais[c.id]?.recomendacao || ''
      })));
    }
  };

  // Verifica se o usuário suprimiu o modal nos últimos 60 dias
  const modalSuprimido = () => {
    const ts = localStorage.getItem('skillcert_suprimir_modal_salvar');
    if (!ts) return false;
    const diff = Date.now() - parseInt(ts, 10);
    return diff < 60 * 24 * 60 * 60 * 1000; // 60 dias em ms
  };

  const normalizarAnalysisIds = (analysisIdOrIds) => {
    if (Array.isArray(analysisIdOrIds)) {
      return analysisIdOrIds.filter(Boolean);
    }
    return analysisIdOrIds ? [analysisIdOrIds] : [];
  };

  const salvarAnalises = async (analysisIdsInput, suprimir = false) => {
    const analysisIds = normalizarAnalysisIds(analysisIdsInput);
    if (analysisIds.length === 0) return;

    setSalvando(true);
    const erros = [];
    try {
      for (const analysisId of analysisIds) {
        try {
          const response = await fetch(`${FUNCTIONS_URL}/saveAnalysis`, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({ analysis_id: analysisId, company_id: companyId, user_id: userId })
          });
          const data = await response.json();
          if (data.success) {
            setAnalisesSalvas(prev => ({ ...prev, [analysisId]: true }));
          } else {
            erros.push(`${analysisId}: ${data.error || 'erro desconhecido'}`);
          }
        } catch (err) {
          erros.push(`${analysisId}: ${err.message}`);
        }
      }

      if (suprimir) {
        localStorage.setItem('skillcert_suprimir_modal_salvar', Date.now().toString());
      }

      if (erros.length > 0) {
        alert(t('errSaveAnalysis', { error: erros.join(' | ') }));
      }
    } finally {
      setSalvando(false);
      setModalSalvar(null);
    }
  };

  const iniciarSalvar = (analysisIdOrIds, tipo) => {
    const analysisIds = normalizarAnalysisIds(analysisIdOrIds);
    if (analysisIds.length === 0) return;

    if (modalSuprimido()) {
      salvarAnalises(analysisIds, false);
    } else {
      setModalSalvar({ analysisIds, tipo });
    }
  };

  const executarSalvarLote = async (analysisIds, suprimir = false) => {
    await salvarAnalises(analysisIds, suprimir);
  };

  const gerarProjecaoNarrativa = async ({ analysisId, tipo, nomesExibicao = [], silent = false }) => {
    if (!analysisId) {
      if (!silent) setErro(t('errProjectionMissingAnalysis'));
      return null;
    }

    const key = `${tipo}:${analysisId}`;

    if (projecoesNarrativas[key]) {
      return projecoesNarrativas[key];
    }

    setProjecaoCarregandoKey(key);
    try {
      const response = await fetch(`${FUNCTIONS_URL}/generateNarrativeProjection`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          analysis_id: analysisId,
          company_id: companyId,
          user_id: userId,
        }),
      });
      const raw = await response.text();
      let data = null;
      try {
        data = JSON.parse(raw);
      } catch {
        const htmlLike = /^<!doctype html>|^<html/i.test((raw || '').trim());
        const detail = htmlLike
          ? `HTTP ${response.status} (HTML)`
          : `HTTP ${response.status} (non-JSON)`;
        throw new Error(detail);
      }

      if (!response.ok || !data.success || !data.projection) {
        const requestId = data?.request_id ? ` [req ${data.request_id}]` : '';
        throw new Error((data.error || 'erro desconhecido') + requestId);
      }

      const projectionComNome = restaurarAliasesProjecao(data.projection, nomesExibicao);
      setProjecoesNarrativas(prev => ({ ...prev, [key]: projectionComNome }));
      return projectionComNome;
    } catch (err) {
      if (!silent) {
        setErro(t('errProjectionGenerate', { error: err.message }));
      }
      if (!silent && telaAtual !== 'app') {
        alert(t('errProjectionGenerate', { error: err.message }));
      }
      return null;
    } finally {
      setProjecaoCarregandoKey(null);
    }
  };

  const abrirProjecaoNarrativa = async ({ analysisId, tipo, tituloFallback, nomesExibicao = [] }) => {
    const projection = await gerarProjecaoNarrativa({ analysisId, tipo, nomesExibicao, silent: false });
    if (!projection) return;

    const title = `${t('narrativeProjectionTitle')} · ${tituloFallback || ''}`.trim();
    setModalProjecaoNarrativa({ title, markdown: projection });
  };

  // ===========================================================================
  // BIBLIOTECA — funções
  // ===========================================================================

  // ===========================================================================
  // CAMADA 0B — funções
  // ===========================================================================

  const handleInferCulture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isPdf = file.type === 'application/pdf';
    const lowerName = String(file.name || '').toLowerCase();
    const isText = file.type.startsWith('text/') || lowerName.endsWith('.md') || lowerName.endsWith('.txt');
    if (!isPdf && !isText) {
      alert(t('errUnsupportedFormat'));
      e.target.value = '';
      return;
    }
    if (file.size > CULTURE_DOC_MAX_BYTES) {
      alert(t('errCultureDocTooLarge', { maxMb: CULTURE_DOC_MAX_MB }));
      e.target.value = '';
      return;
    }

    setCamada0bProcessando(true);
    setCamada0bResultado(null);
    setCamada0bReviewSummary(null);

    try {
      if (!companyId) {
        throw new Error('Sessão sem empresa ativa. Recarregue a página e tente novamente.');
      }
      const fileBase64 = await fileToBase64(file);
      if (!fileBase64) {
        throw new Error('Não foi possível ler o arquivo selecionado.');
      }

      const response = await fetch(`${FUNCTIONS_URL}/inferCulture`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          file_base64: fileBase64,
          file_type: file.type || 'text/plain',
          file_name: file.name,
          company_id: companyId,
          user_id: userId,
        })
      });

      const data = await readJsonResponseSafe(response);
      if (!response.ok) {
        const message = appendRequestId(
          extractApiErrorMessage(data, `HTTP ${response.status}`),
          data,
          response
        );
        throw new Error(message);
      }
      if (!data?.success || !data?.inference) {
        const message = appendRequestId(
          extractApiErrorMessage(data, t('errUnexpectedServerResponse')),
          data,
          response
        );
        throw new Error(message);
      }

      setCamada0bResultado(data.inference);

      const reviewItems = Array.isArray(data?.contradictions) ? data.contradictions : [];
      const inferGap = (item) => {
        const explicitGap = Number(item?.gap);
        if (Number.isFinite(explicitGap)) return Math.abs(explicitGap);

        const slider = Number(item?.slider_level);
        const inferred = Number(item?.inferred_level);
        if (Number.isFinite(slider) && Number.isFinite(inferred)) return Math.abs(inferred - slider);
        return null;
      };

      const actionableItems = reviewItems.filter((item) => {
        const kind = item?.kind;
        if (kind === 'strong_divergence') return true;
        if (kind !== 'tradeoff') return false;
        const gap = inferGap(item);
        return gap !== null ? gap >= 1 : true;
      });

      const sameLevelTradeoffItems = reviewItems.filter((item) => item?.kind === 'tradeoff' && inferGap(item) === 0);
      const hasItemDetails = reviewItems.length > 0;

      const strongFromItems = actionableItems.filter((item) => item?.kind === 'strong_divergence').length;
      const actionableTradeoffsFromItems = actionableItems.filter((item) => item?.kind === 'tradeoff').length;
      const strongFromSummary = Number(data?.review_summary?.strong_divergences) || 0;
      const tradeoffsFromSummary = Number(data?.review_summary?.tradeoffs) || 0;

      const strong = hasItemDetails ? strongFromItems : strongFromSummary;
      const tradeoffs = hasItemDetails ? actionableTradeoffsFromItems : tradeoffsFromSummary;
      const sameLevelTradeoffs = hasItemDetails ? sameLevelTradeoffItems.length : 0;
      const hasActionableAlerts = strong > 0 || tradeoffs > 0;
      const hasAnyAlerts = hasActionableAlerts || sameLevelTradeoffs > 0;

      setCamada0bReviewSummary({ strong, tradeoffs, sameLevelTradeoffs, hasActionableAlerts, hasAnyAlerts });

      if (actionableItems.length > 0) {
        // Iniciar fluxo de contradições — uma por vez
        setContradictionIdx(0);
        setModalContradiction({ contradictions: actionableItems, inference: data.inference });
      } else {
        // Sem alertas acionáveis: salvar enrichment_notes direto
        await salvarEnrichmentNotes(data.inference);
      }
    } catch (err) {
      alert(t('errProcessDocument', { error: err?.message || t('errUnexpectedServerResponse') }));
    } finally {
      setCamada0bProcessando(false);
      e.target.value = '';
    }
  };

  const salvarEnrichmentNotes = async (inference) => {
    if (!inference?.principles) return;
    // Para cada PT com enrichment_notes, atualiza a coluna no banco
    const updates = inference.principles
      .filter(p => p.enrichment_notes)
      .map(p =>
        supabaseUpdateEnrichment(p.id, p.enrichment_notes)
      );
    await Promise.all(updates);
  };

  // Atualiza enrichment_notes de um PT via upsert direto no Supabase
  // (chama endpoint próprio para não expor service key no frontend)
  const supabaseUpdateEnrichment = async (principleId, notes) => {
    const response = await fetch(`${FUNCTIONS_URL}/updateEnrichmentNotes`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        company_id: companyId,
        principle_id: principleId,
        enrichment_notes: notes,
      })
    });
    const data = await readJsonResponseSafe(response);
    if (!response.ok || data?.success === false) {
      const message = appendRequestId(
        extractApiErrorMessage(data, `HTTP ${response.status}`),
        data,
        response
      );
      throw new Error(message);
    }
  };

  const PT_NAMES = isEn
    ? {
      PT1: 'Decision Pace',
      PT2: 'Stability versus Change',
      PT3: 'Interpersonal Tension and Conflict',
      PT4: 'Workplace Intensity',
      PT5: 'Individual versus Collective Work',
      PT6: 'Relationship with Deviation and Error',
    }
    : {
      PT1: 'Ritmo de decisão',
      PT2: 'Estabilidade versus mudança',
      PT3: 'Tensão interpessoal e conflito',
      PT4: 'Intensidade do ambiente',
      PT5: 'Trabalho individual versus coletivo',
      PT6: 'Relação com desvio e erro',
    };

  const getPrincipleById = (principleId) => {
    if (isEn) return PRINCIPIOS_TRABALHO_EN[principleId] || null;
    return PRINCIPIOS_TRABALHO.find((pt) => pt.id === principleId) || null;
  };

  const getSliderChoiceLabel = (level) => {
    const numericLevel = Number(level);
    if (numericLevel === 1) return t('contradictionOptionStrongLeft');
    if (numericLevel === 2) return t('contradictionOptionModerateLeft');
    if (numericLevel === 3) return t('contradictionOptionModerateRight');
    if (numericLevel === 4) return t('contradictionOptionStrongRight');
    return t('levelN', { n: level });
  };

  const summarizeText = (text, maxLength = 92) => {
    const normalized = String(text || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    if (normalized.length <= maxLength) return normalized;
    return `${normalized.slice(0, maxLength).trimEnd()}...`;
  };

  const getPoleSummaryByLevel = (principle, level) => {
    if (!principle) return '';
    const numericLevel = Number(level);
    const sourceText = [1, 2].includes(numericLevel) ? principle.esquerda : principle.direita;
    return summarizeText(sourceText);
  };

  const getContradictionDescription = (rawDescription) => {
    const base = String(rawDescription || '').replace(/\s+/g, ' ').trim();
    if (!base) return t('contradictionDescriptionIntro');

    if (!isEn) {
      const cleaned = base
        .replace(/^o texto\s+/i, '')
        .replace(/^esse texto\s+/i, '')
        .replace(/^o documento\s+/i, '')
        .replace(/^este documento\s+/i, '');
      return `${t('contradictionDescriptionIntro')} ${cleaned}`.trim();
    }

    const cleanedEn = base
      .replace(/^the text\s+/i, '')
      .replace(/^this text\s+/i, '')
      .replace(/^the document\s+/i, '')
      .replace(/^this document\s+/i, '');
    return `${t('contradictionDescriptionIntro')} ${cleanedEn}`.trim();
  };

  const getTradeoffDescription = (item) => {
    const intro = t('tradeoffDescriptionIntro');
    const base = String(item?.tradeoff_reason || item?.description || '').replace(/\s+/g, ' ').trim();
    if (!base) return intro;

    if (!isEn) {
      const cleaned = base
        .replace(/^o texto\s+/i, '')
        .replace(/^esse texto\s+/i, '')
        .replace(/^o documento\s+/i, '')
        .replace(/^este documento\s+/i, '');
      return `${intro} ${cleaned}`.trim();
    }

    const cleanedEn = base
      .replace(/^the text\s+/i, '')
      .replace(/^this text\s+/i, '')
      .replace(/^the document\s+/i, '')
      .replace(/^this document\s+/i, '');
    return `${intro} ${cleanedEn}`.trim();
  };

  const getTradeoffEvidence = (evidenceText) => {
    const summary = summarizeText(evidenceText, 180);
    return summary || t('tradeoffEvidenceFallback');
  };

  const handleContradictionDecision = async (adjust) => {
    const { contradictions, inference } = modalContradiction;
    const current = contradictions[contradictionIdx];

    if (adjust) {
      // Ajustar o slider para o nível inferido
      const ptIndex = PRINCIPIOS_TRABALHO.findIndex(pt => pt.id === current.id);
      if (ptIndex !== -1) {
        setPrincipios(prev => ({ ...prev, [ptIndex]: current.inferred_level }));
      }
    }

    const next = contradictionIdx + 1;
    if (next < contradictions.length) {
      setContradictionIdx(next);
    } else {
      // Terminou o fluxo de contradições — salvar enrichment_notes
      setModalContradiction(null);
      setContradictionIdx(0);
      await salvarEnrichmentNotes(inference);
    }
  };

  const abrirBiblioteca = async () => {
    setTelaAtual('biblioteca');
    setBibliotecaCarregando(true);
    try {
      const response = await fetch(`${FUNCTIONS_URL}/getLibrary`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ company_id: companyId })
      });
      const data = await response.json();
      if (data.success) {
        setBiblioteca(data);
      } else {
        setBiblioteca({ positions: [] });
      }
    } catch (err) {
      setBiblioteca({ positions: [] });
    } finally {
      setBibliotecaCarregando(false);
    }
  };

  const abrirAnaliseDetalhe = (analise) => {
    if (analise?.analysis_type === 'individual' && analise?.output_narrative) {
      const nome = restaurarNomeSimples(analise.candidate_names?.[0] || '');
      const narrativaComNome = restaurarAliasesProjecao(analise.output_narrative, [nome].filter(Boolean));
      if (narrativaComNome) {
        setProjecoesNarrativas(prev => ({ ...prev, [`individual:${analise.id}`]: narrativaComNome }));
      }
    }
    setAnaliseDetalhe(analise);
    setAnaliseDetalheTab('decisoria');
    setTelaAtual('analise-detalhe');
  };

  const reaproveitarPosicao = (positionData) => {
    // Pré-preenche os campos da posição com os dados salvos e leva o usuário à etapa 2
    const novasDimensoes = {};
    if (positionData.position_title) setCargo(positionData.position_title);
    if (positionData.position_description) setObservacoes(positionData.position_description);
    if (positionData.position_dimensions) {
      // Mapeia de volta as chaves EN para os nomes PT usados no select
      const dimMap = {
        habilidades_analiticas: 'Habilidades Analíticas',
        tomada_de_decisao: 'Tomada de Decisão',
        agilidade_e_aprendizado: 'Agilidade e Aprendizado',
        lideranca: 'Liderança',
        orientacao_para_cliente: 'Orientação para Cliente',
        influencia_e_gestao_de_stakeholders: 'Influência e Gestão de Stakeholders',
      };
      Object.entries(positionData.position_dimensions).forEach(([chaveEN, valor]) => {
        const chavePT = dimMap[chaveEN];
        if (chavePT) novasDimensoes[chavePT] = valor;
      });
    }
    setDimensoes(novasDimensoes);
    // Resetar candidatos e análises para nova rodada
    setCandidatos([]);
    setAnalisesIndividuais({});
    setAnalises([]);
    setComparativaCache(null);
    setComparativaPollingId(null);
    setComparativaProcessando(false);
    const confirmacoesReaproveitadas = {};
    Object.keys(novasDimensoes).forEach((categoria) => {
      if (novasDimensoes[categoria]) confirmacoesReaproveitadas[categoria] = true;
    });
    setPositionDimensionConfirmations(confirmacoesReaproveitadas);
    setPositionGuidanceExpanded(false);
    setPositionId(null);
    setTelaAtual('app');
    setEtapa(2);
  };

  const formatarData = (isoString) => {
    const d = new Date(isoString);
    const conector = tenantLocale === 'en-US' ? ' at ' : ' às ';
    return d.toLocaleDateString(tenantLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })
      + conector
      + d.toLocaleTimeString(tenantLocale, { hour: '2-digit', minute: '2-digit' });
  };

  const formatarDataArquivo = (isoString = null) => {
    const d = isoString ? new Date(isoString) : new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const labelRecomendacao = (rec) => {
    const map = tenantLocale === 'en-US'
      ? {
        highly_recommended: { label: 'Highly Recommended', cls: 'badge-blue' },
        recommended: { label: 'Recommended', cls: 'badge-green' },
        recommended_with_caveats: { label: 'Recommended with Caveats', cls: 'badge-yellow' },
        not_recommended: { label: 'Not Recommended', cls: 'badge-red' },
        preferred_candidate: { label: 'Preferred Candidate', cls: 'badge-blue' },
        preferred_with_tradeoffs: { label: 'Preferred with Trade-offs', cls: 'badge-yellow' },
        technical_tie: { label: 'Technical Tie', cls: 'badge-yellow' },
        none_acceptable: { label: 'None Acceptable', cls: 'badge-red' },
      }
      : {
        highly_recommended: { label: 'Recomendado com Destaque', cls: 'badge-blue' },
        recommended: { label: 'Recomendado', cls: 'badge-green' },
        recommended_with_caveats: { label: 'Recomendado com Ressalva', cls: 'badge-yellow' },
        not_recommended: { label: 'Não Recomendado', cls: 'badge-red' },
        preferred_candidate: { label: 'Candidato Preferencial', cls: 'badge-blue' },
        preferred_with_tradeoffs: { label: 'Preferencial com Trade-offs', cls: 'badge-yellow' },
        technical_tie: { label: 'Empate Técnico', cls: 'badge-yellow' },
        none_acceptable: { label: 'Nenhum Recomendável', cls: 'badge-red' },
      };
    return map[rec] || { label: rec, cls: 'badge-yellow' };
  };

  const exportarPdf = async (report) => {
    if (exportandoPdf) return;
    setExportandoPdf(true);
    try {
      await exportSkillCertPdf(report);
    } catch (err) {
      alert(t('errExportPdf', { error: err.message }));
    } finally {
      setExportandoPdf(false);
    }
  };

  const obterProjecaoParaPdf = async (analise, opts = {}) => {
    const analysisId = opts.analysisId || analise.analysisId || null;
    const candidato = analise.candidato || opts.candidateName || '';
    const prefetchedNarrative = opts.narrativeProjection || analise.narrativa || analise.output_narrative || null;
    const key = analysisId ? `individual:${analysisId}` : null;
    if (key && projecoesNarrativas[key]) return projecoesNarrativas[key];
    if (prefetchedNarrative) {
      const projectionComNome = restaurarAliasesProjecao(prefetchedNarrative, [candidato].filter(Boolean));
      if (key && projectionComNome) {
        setProjecoesNarrativas(prev => ({ ...prev, [key]: projectionComNome }));
      }
      return projectionComNome;
    }
    if (!analysisId) return null;

    return await gerarProjecaoNarrativa({
      analysisId,
      tipo: 'individual',
      nomesExibicao: [candidato].filter(Boolean),
      silent: true,
    });
  };

  const exportarPdfIndividual = async (analise, opts = {}) => {
    const rec = labelRecomendacao(analise.recomendacao);
    const dataArquivo = formatarDataArquivo(opts.createdAt || null);
    const baseName = sanitizeFileName(`individual_${dataArquivo}_${analise.candidato || 'candidato'}`);
    const positionFallback = tenantLocale === 'en-US' ? 'Position not provided' : 'Posição não informada';
    const narrativa = await obterProjecaoParaPdf(analise, opts);

    await exportarPdf({
      type: 'individual',
      locale: tenantLocale,
      fileName: baseName,
      positionTitle: opts.positionTitle || cargo || positionFallback,
      candidateNames: [analise.candidato],
      recommendationLabel: rec.label,
      recommendationClass: rec.cls,
      decisory: analise.decisoria || '',
      analytical: analise.analitica || '',
      narrativeProjection: narrativa ? limparCorpoProjecao(narrativa) : '',
    });
  };

  const exportarPdfComparativa = ({ texto, nomes, recomendacao, positionTitle, createdAt }) => {
    const rec = recomendacao ? labelRecomendacao(recomendacao) : null;
    const dataArquivo = formatarDataArquivo(createdAt || null);
    const comparativePrefix = tenantLocale === 'en-US' ? 'comparative' : 'comparativa';
    const baseName = sanitizeFileName(`${comparativePrefix}_${dataArquivo}_${positionTitle || cargo || 'posicao'}`);
    const positionFallback = tenantLocale === 'en-US' ? 'Position not provided' : 'Posição não informada';

    exportarPdf({
      type: 'comparative',
      locale: tenantLocale,
      fileName: baseName,
      positionTitle: positionTitle || cargo || positionFallback,
      candidateNames: nomes || [],
      recommendationLabel: rec?.label || null,
      recommendationClass: rec?.cls || null,
      comparative: texto || '',
    });
  };

  const exportarPdfIndividualLote = async (listaAnalises) => {
    const validas = (listaAnalises || []).filter(a => a.decisoria || a.analitica);
    if (validas.length === 0) {
      alert(t('errNoIndividualForExport'));
      return;
    }

    const dataArquivo = formatarDataArquivo();
    const baseName = sanitizeFileName(`individual_${dataArquivo}_${cargo || 'posicao'}`);

    const itens = [];
    for (const analise of validas) {
      const narrativa = await obterProjecaoParaPdf(analise, {});
      itens.push({
        candidateName: analise.candidato,
        recommendationLabel: labelRecomendacao(analise.recomendacao).label,
        recommendationClass: labelRecomendacao(analise.recomendacao).cls,
        decisory: analise.decisoria || '',
        analytical: analise.analitica || '',
        narrativeProjection: narrativa ? limparCorpoProjecao(narrativa) : '',
        generatedAtLabel: new Date().toLocaleString(tenantLocale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        positionTitle: cargo || (tenantLocale === 'en-US' ? 'Position not provided' : 'Posição não informada'),
      });
    }

    await exportarPdf({
      type: 'individual_batch',
      locale: tenantLocale,
      fileName: baseName,
      items: itens,
    });
  };

  const toggleExpandida = (key) => {
    setAnaliseExpandida(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Navega para etapa anterior em modo visualização (sem edição).
  // Se houver análises geradas e o usuário quiser editar, exibe modal de confirmação.
  const navegarParaEtapa = (etapaDestino) => {
    setModoVisualizacao(true);
    setEtapa(etapaDestino);
  };

  const confirmarEdicao = () => {
    const { etapaDestino } = modalConfirmarEdicao;
    setModalConfirmarEdicao(null);
    setModoVisualizacao(false);

    if (etapaDestino === 1) {
      // Editar PTs: não apaga candidatos nem análises — apenas libera os sliders
      setEtapa(1);
    } else {
      // Editar Posição: apaga candidatos e análises — serão refeitos
      setAnalises([]);
      setAnalisesIndividuais({});
      setCandidatos([]);
      setComparativaCache(null);
      setComparativaPollingId(null);
      setComparativaProcessando(false);
      setPositionDimensionConfirmations({});
      setPositionGuidanceExpanded(false);
      setPositionId(null);
      setEtapa(etapaDestino);
    }
  };

  const resetWorkspaceState = (isWorkPrinciplesEditor = false) => {
    setTelaAtual('app');
    setEtapa(1);
    setPrincipios({});
    setPtsSalvos(false);
    setModoVisualizacao(!isWorkPrinciplesEditor);
    setPositionGuidanceExpanded(false);
    setPositionDimensionConfirmations({});
    setCargo('');
    setDimensoes({});
    setObservacoes('');
    setPositionId(null);
    setCandidatos([]);
    setAnalisesIndividuais({});
    setAnalises([]);
    setComparativaCache(null);
    setComparativaPollingId(null);
    setComparativaProcessando(false);
    setAnalisesSalvas({});
    setPseudonimoMap({});
    resetUserManagementState();
    resetGlobalAdminState();
    resetCreditsState();
  };

  const resetAuthToLogin = (keepContextSelector = false) => {
    setSessao(null);
    setUsuarioAtivo(null);
    setLoginErro('');
    setLoginCarregando(false);
    setContextCarregando(false);
    resetWorkspaceState(false);

    if (!keepContextSelector) {
      setAuthAccessToken(null);
      setContextOptions([]);
      setPendingContextSelection(false);
      setSelectedContextValue('');
      setContextLoginEmail('');
    }
  };

  // ===========================================================================
  // TELA DE LOGIN — email + senha
  // ===========================================================================
  const [loginPassword, setLoginPassword] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginErro, setLoginErro] = useState('');
  const [loginCarregando, setLoginCarregando] = useState(false);

  const applyContextSelectionResult = async ({ data, accessToken, selectedValue, loginEmailValue }) => {
    const normalizedEmail = String(loginEmailValue || data?.login_email || '').trim().toLowerCase();
    const nextOptions = buildContextOptions(data?.available_contexts || contextOptions, data?.has_global_admin === true);
    if (nextOptions.length > 0) setContextOptions(nextOptions);
    if (accessToken) setAuthAccessToken(accessToken);
    if (normalizedEmail) setContextLoginEmail(normalizedEmail);
    if (selectedValue) {
      setSelectedContextValue(selectedValue);
      writeLastContextValue(normalizedEmail, selectedValue);
    }
    setPendingContextSelection(false);
    setLoginErro('');

    if (data?.context_type === 'global_admin') {
      const globalUser = data?.user || {
        id: 'global-master-admin',
        nome: normalizedEmail || 'Master Admin',
        email: normalizedEmail || null,
        avatar: String(normalizedEmail || 'M').charAt(0).toUpperCase() || 'M',
        type: 'master_admin',
      };
      resetWorkspaceState(true);
      setSessao({
        context_type: 'global_admin',
        company_id: null,
        company_name: t('globalAdminArea'),
        language: data?.default_language || 'pt-BR',
        session_token: data?.session_token || null,
      });
      setUsuarioAtivo(globalUser);
      return true;
    }

    if (!data?.company_id || !data?.user) {
      setLoginErro(t('errContextSelection', { error: 'contexto inválido' }));
      return false;
    }

    setSessao({
      context_type: 'company',
      company_id: data.company_id,
      company_name: data.company_name,
      language: data.company_language || 'pt-BR',
      session_token: data.session_token || null,
    });
    await handleSelecionarUsuario(data.user, data.company_id, data.session_token || null);
    return true;
  };

  const handleSelectContext = async ({
    selectedValue,
    accessToken = authAccessToken,
    loginEmailValue = contextLoginEmail,
    silentOnError = false,
  }) => {
    const parsed = parseContextValue(selectedValue);
    if (!accessToken) {
      if (!silentOnError) setLoginErro(t('errContextSelection', { error: 'sessão expirada' }));
      return false;
    }
    if (parsed.context_type === 'company' && !parsed.company_id) {
      if (!silentOnError) setLoginErro(t('errContextSelection', { error: 'tenant inválido' }));
      return false;
    }

    try {
      const response = await fetch(`${FUNCTIONS_URL}/selectLoginContext`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: accessToken,
          context_type: parsed.context_type,
          company_id: parsed.company_id,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        if (!silentOnError) {
          setLoginErro(t('errContextSelection', { error: data.error || 'erro desconhecido' }));
        }
        return false;
      }

      return await applyContextSelectionResult({
        data,
        accessToken,
        selectedValue,
        loginEmailValue,
      });
    } catch (err) {
      if (!silentOnError) setLoginErro(t('errContextSelection', { error: err.message }));
      return false;
    }
  };

  const handleContextSelectionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedContextValue) return;

    setContextCarregando(true);
    setLoginErro('');
    try {
      await handleSelectContext({
        selectedValue: selectedContextValue,
        accessToken: authAccessToken,
        loginEmailValue: contextLoginEmail,
        silentOnError: false,
      });
    } finally {
      setContextCarregando(false);
    }
  };

  const handleSwitchContext = async () => {
    if (!authAccessToken || contextOptions.length === 0) return;
    if (!window.confirm(t('switchTenantConfirm'))) return;

    const fallbackValue = contextOptions[0]?.value || '';
    const preferredValue = contextOptions.some((option) => option.value === currentContextValue)
      ? currentContextValue
      : fallbackValue;

    resetAuthToLogin(true);
    setPendingContextSelection(true);
    setSelectedContextValue(preferredValue);
  };

  const handleCancelContextSelection = () => {
    setPendingContextSelection(false);
    setContextOptions([]);
    setAuthAccessToken(null);
    setSelectedContextValue('');
    setContextLoginEmail('');
    setLoginErro('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim()) return;
    if (!loginPassword.trim()) return;

    setLoginCarregando(true);
    setLoginErro('');
    try {
      const response = await fetch(`${FUNCTIONS_URL}/loginWithPassword`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          email: loginEmail.trim(),
          password: loginPassword,
        }),
      });
      const data = await response.json();
      if (!data.success) {
        if (data.code === 'tenant_required') {
          setLoginErro(t('errTenantRequired'));
          return;
        }
        setLoginErro(data.error || t('errPasswordLoginDisabled'));
        return;
      }

      if (data.requires_context_selection) {
        const normalizedEmail = String(data.login_email || loginEmail).trim().toLowerCase();
        const options = buildContextOptions(data.contexts || [], data.has_global_admin === true);
        if (!data.auth_access_token || options.length === 0) {
          setLoginErro(t('errContextSelection', { error: 'nenhum contexto disponível' }));
          return;
        }

        setAuthAccessToken(data.auth_access_token);
        setContextLoginEmail(normalizedEmail);
        setContextOptions(options);
        setPendingContextSelection(true);

        const remembered = readLastContextValue(normalizedEmail);
        const defaultValue = options.some((option) => option.value === remembered)
          ? remembered
          : options[0].value;
        setSelectedContextValue(defaultValue);

        if (remembered && options.some((option) => option.value === remembered)) {
          setContextCarregando(true);
          const autoSelected = await handleSelectContext({
            selectedValue: remembered,
            accessToken: data.auth_access_token,
            loginEmailValue: normalizedEmail,
            silentOnError: true,
          });
          setContextCarregando(false);
          if (!autoSelected) setLoginErro(t('selectContextSubtitle'));
        }
        return;
      }

      if (data.auth_access_token) setAuthAccessToken(data.auth_access_token);
      if (data.login_email) setContextLoginEmail(String(data.login_email).trim().toLowerCase());
      if (data.available_contexts) {
        setContextOptions(buildContextOptions(data.available_contexts, data.has_global_admin === true));
      }
      setPendingContextSelection(false);

      setSessao({
        context_type: data.context_type || 'company',
        company_id: data.company_id,
        company_name: data.company_name,
        language: data.company_language || 'pt-BR',
        session_token: data.session_token || null,
      });
      await handleSelecionarUsuario(data.user, data.company_id, data.session_token || null);
    } catch (err) {
      setLoginErro(t('errLoginConnection'));
    } finally {
      setLoginCarregando(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail.trim()) {
      setLoginErro(t('errResetEmailRequired'));
      return;
    }

    setLoginCarregando(true);
    setLoginErro('');
    try {
      const response = await fetch(`${FUNCTIONS_URL}/requestPasswordReset`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify({
          email: loginEmail.trim(),
          redirect_to: `${window.location.origin}/auth-callback.html`,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        if (data.code === 'tenant_required') {
          setLoginErro(t('errTenantRequired'));
          return;
        }
        setLoginErro(t('errPasswordReset', { error: data.error || 'erro desconhecido' }));
        return;
      }
      setLoginErro(t('passwordResetSent'));
    } catch (err) {
      setLoginErro(t('errPasswordReset', { error: err.message }));
    } finally {
      setLoginCarregando(false);
    }
  };

  // Carrega PTs salvos do tenant ao selecionar usuário
  const handleSelecionarUsuario = async (usuario, companyId, sessionTokenOverride = null) => {
    const role = normalizeUserRole(usuario?.type);
    const isWorkPrinciplesEditor = ['admin_client', 'master_admin'].includes(role);
    resetWorkspaceState(isWorkPrinciplesEditor);
    setUsuarioAtivo(usuario);

    try {
      const response = await fetch(`${FUNCTIONS_URL}/getWorkPrinciples`, {
        method: 'POST',
        headers: buildHeaders(sessionTokenOverride),
        body: JSON.stringify({ company_id: companyId }),
      });
      const data = await response.json();
      if (data.success && data.principles?.length > 0) {
        const ptMap = { PT1: 0, PT2: 1, PT3: 2, PT4: 3, PT5: 4, PT6: 5 };
        const novosPrincipios = {};
        data.principles.forEach(p => {
          const idx = ptMap[p.principle_id];
          if (idx !== undefined && isValidPrincipleLevel(p.level_chosen)) {
            novosPrincipios[idx] = Number(p.level_chosen);
          }
        });
        setPrincipios(novosPrincipios);
        const allPrinciplesConfigured = PRINCIPIOS_TRABALHO.every((_, idx) => isValidPrincipleLevel(novosPrincipios[idx]));
        setPtsSalvos(allPrinciplesConfigured);

        if (allPrinciplesConfigured) {
          // PTs já configurados: pular para etapa 2.
          // Edição de PT permanece disponível apenas para admin_client/master_admin.
          setModoVisualizacao(!isWorkPrinciplesEditor);
          setEtapa(2);
        } else {
          setModoVisualizacao(!isWorkPrinciplesEditor);
          setEtapa(1);
        }
      }
    } catch (err) {
      console.error('[handleSelecionarUsuario] Erro ao carregar PTs:', err);
    }
  };

  const shouldShowContextSelector = pendingContextSelection && authAccessToken && contextOptions.length > 0;
  const getContextOptionLabel = (option) => {
    if (!option) return '';
    if (option.context_type === 'global_admin') return t('globalAdminArea');
    return option.company_name || option.company_id || '';
  };

  if (!sessao || !usuarioAtivo) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="login-logo">
            <img src={skillcertLogo} alt="SkillCert" className="logo-img" />
          </div>
          <h2 className="login-title">{t('appTitle')}</h2>
          <p className="login-subtitle">{shouldShowContextSelector ? t('selectContextTitle') : t('loginSubtitle')}</p>

          {shouldShowContextSelector ? (
            <form onSubmit={handleContextSelectionSubmit} style={{ width: '100%' }}>
              <p className="field-hint" style={{ marginBottom: '12px' }}>{t('selectContextSubtitle')}</p>
              <label className="context-select-label">{t('selectContextLabel')}</label>
              <select
                className="login-token-input"
                value={selectedContextValue}
                onChange={(e) => { setSelectedContextValue(e.target.value); setLoginErro(''); }}
                style={{ marginBottom: '8px' }}
              >
                {contextOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {getContextOptionLabel(option)}
                  </option>
                ))}
              </select>
              {loginErro && <p className="login-erro">{loginErro}</p>}
              <button
                type="submit"
                className="btn-primary"
                style={{ width: '100%', marginTop: '12px' }}
                disabled={contextCarregando || !selectedContextValue}
              >
                {contextCarregando ? t('checking') : t('enterSelectedContext')}
              </button>
              <button
                type="button"
                className="btn-secondary"
                style={{ width: '100%', marginTop: '8px' }}
                disabled={contextCarregando}
                onClick={handleCancelContextSelection}
              >
                {t('backToLogin')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} style={{ width: '100%' }}>
              <input
                type="email"
                className="login-token-input"
                placeholder={t('emailPlaceholder')}
                value={loginEmail}
                onChange={e => { setLoginEmail(e.target.value); setLoginErro(''); }}
                autoFocus
                autoComplete="email"
                style={{ marginBottom: '12px' }}
              />
              <input
                type="password"
                className="login-token-input"
                placeholder={t('passwordPlaceholder')}
                value={loginPassword}
                onChange={e => { setLoginPassword(e.target.value); setLoginErro(''); }}
                autoComplete="current-password"
                style={{ marginBottom: '12px' }}
              />
              <button
                type="button"
                className="btn-secondary"
                style={{ width: '100%', marginTop: '8px' }}
                disabled={loginCarregando}
                onClick={handleForgotPassword}
              >
                {t('forgotPassword')}
              </button>
              {loginErro && <p className="login-erro">{loginErro}</p>}
              <button
                type="submit"
                className="btn-primary"
                style={{ width: '100%', marginTop: '16px' }}
                disabled={
                  loginCarregando
                  || !loginEmail.trim()
                  || !loginPassword.trim()
                  || !PASSWORD_AUTH_ENABLED
                }
              >
                {loginCarregando ? t('checking') : t('enter')}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ===========================================================================
  // ETAPA 1: PRINCÍPIOS
  // ===========================================================================
  const renderEtapa1 = () => {
    const allCompleted = PRINCIPIOS_TRABALHO.every((_, idx) => isValidPrincipleLevel(principios[idx]));
    const canProceedFromStep1 = canEditWorkPrinciples ? (modoVisualizacao || allCompleted) : allCompleted;

    return (
      <div className="etapa-container">
        <div className="etapa-header">
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
            <div>
              <h2>{t('principlesOrg')}</h2>
              <p className="subtitle">
                {modoVisualizacao ? t('principlesView') : t('principlesSetup')}
              </p>
            </div>
            {modoVisualizacao && canEditWorkPrinciples && (
              <button
                className="btn-secondary"
                style={{marginLeft:'16px', whiteSpace:'nowrap'}}
                onClick={() => setModalConfirmarEdicao({ etapaDestino: 1 })}
              >
                {t('edit')}
              </button>
            )}
          </div>
        </div>

        {!canEditWorkPrinciples && !allCompleted && (
          <p className="field-hint" style={{ marginBottom: '16px' }}>
            {t('principlesPendingAdmin')}
          </p>
        )}

        {PRINCIPIOS_TRABALHO.map((pt, index) => {
          const valorEscolhido = principios[index];
          const principleText = isEn ? (PRINCIPIOS_TRABALHO_EN[pt.id] || pt) : pt;
          return (
            <div key={index} className={`principio-card ${valorEscolhido !== undefined ? 'principio-done' : ''}`}>
              <div className="principio-header">
                <h3>{principleText.titulo}</h3>
                {valorEscolhido !== undefined && <span className="check-icon">✓</span>}
              </div>

              <div className="polos-container">
                <div className={`polo polo-left ${valorEscolhido === 1 || valorEscolhido === 2 ? 'polo-active' : ''}`}>
                  <div className="polo-label">{t('leftPole')}</div>
                  <p>{principleText.esquerda}</p>
                </div>
                <div className={`polo polo-right ${valorEscolhido === 3 || valorEscolhido === 4 ? 'polo-active' : ''}`}>
                  <div className="polo-label">{t('rightPole')}</div>
                  <p>{principleText.direita}</p>
                </div>
              </div>

              <div className="slider-container">
                <div className="radio-group">
                  {[
                    { v: 1, label: t('strong') },
                    { v: 2, label: t('moderate') },
                    { v: 3, label: t('moderate') },
                    { v: 4, label: t('strong') },
                  ].map(({ v, label }) => (
                    <label key={v} className={`radio-label ${valorEscolhido === v ? 'radio-selected' : ''} ${modoVisualizacao || !canEditWorkPrinciples ? 'radio-disabled' : ''}`}>
                      <input
                        type="radio"
                        name={`principio-${index}`}
                        checked={valorEscolhido === v}
                        onChange={() => (!modoVisualizacao && canEditWorkPrinciples) && setPrincipios(prev => ({ ...prev, [index]: v }))}
                        disabled={modoVisualizacao || !canEditWorkPrinciples}
                      />
                      <span className="radio-dot"></span>
                      <span className="radio-text">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        {/* Camada 0B — upload de documento de cultura */}
        {canEditWorkPrinciples && <div className="cultura-upload-box">
          <div className="cultura-upload-header">
            <span className="cultura-upload-icon">📄</span>
            <div>
              <h4>{t('cultureComplementTitle')}</h4>
              <p>{t('cultureComplementBody')}</p>
            </div>
          </div>
          <label className={`btn-cultura-upload ${camada0bProcessando ? 'btn-disabled' : ''}`}>
            {camada0bProcessando
              ? <><span className="btn-spinner"></span> {t('analyzingDocument')}</>
              : t('uploadCultureDoc')}
            <input
              type="file"
              accept=".pdf,.txt,.md,text/plain,application/pdf"
              onChange={handleInferCulture}
              disabled={camada0bProcessando}
              style={{ display: 'none' }}
            />
          </label>
          {camada0bResultado && (
            <>
              <p className="cultura-upload-ok">{t('cultureDocDone')}</p>
              {camada0bReviewSummary && (
                <p className={`cultura-upload-review ${camada0bReviewSummary.hasAnyAlerts ? 'is-alert' : 'is-clear'}`}>
                  {camada0bReviewSummary.hasActionableAlerts
                    ? t('cultureDocReviewAlerts', {
                      strong: camada0bReviewSummary.strong,
                      tradeoff: camada0bReviewSummary.tradeoffs,
                    })
                    : camada0bReviewSummary.sameLevelTradeoffs > 0
                      ? t('cultureDocSameLevelTradeoffOnly', {
                        count: camada0bReviewSummary.sameLevelTradeoffs,
                        plural: camada0bReviewSummary.sameLevelTradeoffs > 1 ? 's' : '',
                      })
                      : t('cultureDocNoAlerts')}
                </p>
              )}
            </>
          )}
        </div>}

        <div className="button-container">
          <button
            onClick={async () => {
              if (!canEditWorkPrinciples) {
                if (allCompleted) setEtapa(2);
                return;
              }
              if (modoVisualizacao) { setEtapa(2); return; }
              const ok = await atualizarPrincipios();
              if (ok) setEtapa(2);
            }}
            disabled={!canProceedFromStep1}
            className="btn-primary"
          >
            {t('continueToPosition')}
          </button>
        </div>
      </div>
    );
  };

  const destravarDimensaoConfirmada = (categoria) => {
    if (!categoria) return;
    setModalEditarDimensao({ categoria });
  };

  const confirmarEdicaoDimensao = () => {
    const categoria = modalEditarDimensao?.categoria;
    if (!categoria) return;
    setPositionDimensionConfirmations((prev) => ({ ...prev, [categoria]: false }));
    setModalEditarDimensao(null);
  };

  // ===========================================================================
  // ETAPA 2: POSIÇÃO
  // ===========================================================================
  const renderEtapa2 = () => {
    const cargoLimpo = cargo.trim();
    const missingDimensions = Object.keys(DIMENSOES_POSICAO).filter((cat) => !dimensoes[cat]);
    const pendingCalibrations = Object.keys(DIMENSOES_POSICAO).filter(
      (cat) => dimensoes[cat] && !positionDimensionConfirmations[cat]
    );
    const canContinue = Boolean(cargoLimpo)
      && missingDimensions.length === 0
      && pendingCalibrations.length === 0;
    const positionReadOnly = modoVisualizacao && canEditWorkPrinciples;
    const pendingItems = [];

    if (!cargoLimpo) pendingItems.push(t('positionPendingRole'));
    if (missingDimensions.length === 1) pendingItems.push(t('positionPendingOneDimension'));
    if (missingDimensions.length > 1) pendingItems.push(t('positionPendingManyDimensions', { count: missingDimensions.length }));
    if (pendingCalibrations.length === 1) pendingItems.push(t('positionPendingOneCritical'));
    if (pendingCalibrations.length > 1) pendingItems.push(t('positionPendingManyCritical', { count: pendingCalibrations.length }));

    return (
      <div className="etapa-container">
        <div className="etapa-header">
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start'}}>
            <div>
              <h2>{t('positionDefinition')}</h2>
              <p className="subtitle">
                {positionReadOnly ? t('positionView') : t('positionSubtitle')}
              </p>
              {!positionReadOnly && (
                <div className="posicao-orientacao posicao-orientacao-compact">
                  <p>{t('positionGuidance')}</p>
                  <button
                    type="button"
                    className="btn-link-inline"
                    onClick={() => setPositionGuidanceExpanded((prev) => !prev)}
                  >
                    {positionGuidanceExpanded ? t('positionGuidanceReadLess') : t('positionGuidanceReadMore')}
                  </button>
                </div>
              )}
              {!positionReadOnly && positionGuidanceExpanded && (
                <div className="posicao-orientacao posicao-orientacao-expanded">
                  <p className="position-guidance-expanded-intro">{t('positionGuidanceExpandedIntro')}</p>
                  <ul className="position-guidance-list">
                    <li>{t('positionGuidanceTieBreaker')}</li>
                    <li>{t('positionGuidanceBias')}</li>
                    <li>{t('positionGuidanceExceptions')}</li>
                    <li>{t('positionGuidanceCheckQuestion')}</li>
                  </ul>
                </div>
              )}
            </div>
            {positionReadOnly && (
              <button
                className="btn-secondary"
                style={{marginLeft:'16px', whiteSpace:'nowrap'}}
                onClick={() => {
                  const temAnalises = Object.keys(analisesIndividuais).length > 0;
                  if (temAnalises) setModalConfirmarEdicao({ etapaDestino: 2 });
                  else { setModoVisualizacao(false); }
                }}
              >
                {t('edit')}
              </button>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>{t('roleName')}</label>
          <input
            type="text"
            value={cargo}
            onChange={(e) => !positionReadOnly && setCargo(e.target.value)}
            placeholder={t('rolePlaceholder')}
            readOnly={positionReadOnly}
            style={positionReadOnly ? {background:'var(--bg-light)', cursor:'default'} : {}}
          />
        </div>

        <div className="dimensoes-grid">
          {Object.entries(DIMENSOES_POSICAO).map(([categoria, niveis]) => {
            const selectedCode = dimensoes[categoria] || '';
            const selectedCopy = POSITION_LEVEL_COPY[selectedCode];
            const helper = getLocaleText(tenantLocale, POSITION_DIMENSION_HELPERS[categoria]);
            const calibrationGuide = POSITION_DIMENSION_COMMON_ERRORS[categoria];
            const commonMistake = getLocaleText(tenantLocale, calibrationGuide?.commonMistake);
            const howToCalibrate = getLocaleText(tenantLocale, calibrationGuide?.howToCalibrate);
            const whenInDoubt = getLocaleText(tenantLocale, calibrationGuide?.whenInDoubt);
            const selectedLabel = selectedCopy
              ? getLocaleText(tenantLocale, selectedCopy.label)
              : '';
            const selectedDetail = selectedCopy ? getLocaleText(tenantLocale, selectedCopy.detail) : '';
            const isConfirmed = selectedCode ? Boolean(positionDimensionConfirmations[categoria]) : false;

            return (
              <div
                key={categoria}
                className={`dimensao-card ${dimensoes[categoria] ? 'dimensao-done' : ''} ${isConfirmed ? 'dimensao-confirmed' : ''}`}
              >
                <div className="dimensao-header">
                  <h4>{isEn ? (DIMENSION_LABELS_EN[categoria] || categoria) : categoria}</h4>
                  {isConfirmed && (
                    positionReadOnly ? (
                      <span className="check-icon">✓</span>
                    ) : (
                      <button
                        type="button"
                        className="check-icon check-icon-btn"
                        title={t('positionEditChoiceTooltip')}
                        aria-label={t('positionEditChoiceTooltip')}
                        onClick={() => destravarDimensaoConfirmada(categoria)}
                      >
                        ✓
                      </button>
                    )
                  )}
                </div>
                {!isConfirmed && (
                  <>
                    <label className="dimensao-select-label">{t('positionDimensionRequirement')}</label>
                    <select
                      value={selectedCode}
                      onChange={(e) => {
                        if (positionReadOnly) return;
                        const nextCode = e.target.value;
                        setDimensoes((prev) => ({ ...prev, [categoria]: nextCode }));
                        setPositionDimensionConfirmations((prev) => ({ ...prev, [categoria]: false }));
                      }}
                      disabled={positionReadOnly}
                    >
                      <option value="">{t('selectLevel')}</option>
                      {niveis.map((nivel) => {
                        const optionCopy = POSITION_LEVEL_COPY[nivel.id];
                        const label = optionCopy
                          ? getLocaleText(tenantLocale, optionCopy.label)
                          : (isEn ? (LEVEL_DESCRIPTIONS_EN[nivel.id] || nivel.desc) : nivel.desc);
                        return (
                          <option key={nivel.id} value={nivel.id}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </>
                )}

                {selectedCode && (
                  <div className="dimensao-selection-meta">
                    {!isConfirmed && helper && <p className="dimensao-helper">{helper}</p>}
                    {isConfirmed && selectedLabel && <p className="dimensao-selected-label">{selectedLabel}</p>}
                    {!isConfirmed && <p className="dimensao-meta-label">{t('positionMeaningLabel')}</p>}
                    <p className="dimensao-option-detail">{selectedDetail}</p>
                    {!positionReadOnly && !isConfirmed && calibrationGuide && (
                      <div className="dimensao-calibration-alert">
                        {commonMistake && (
                          <p><strong>{t('commonMistakeLabel')}</strong> {commonMistake}</p>
                        )}
                        {howToCalibrate && (
                          <p><strong>{t('howToCalibrateLabel')}</strong> {howToCalibrate}</p>
                        )}
                        {whenInDoubt && (
                          <p><strong>{t('whenInDoubtLabel')}</strong> {whenInDoubt}</p>
                        )}
                      </div>
                    )}
                    {!positionReadOnly && !isConfirmed && (
                      <button
                        type="button"
                        className="btn-secondary btn-dimensao-confirm"
                        onClick={() => setPositionDimensionConfirmations((prev) => ({ ...prev, [categoria]: true }))}
                      >
                        {t('positionConfirmCalibration')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="form-group">
          <label>{t('roleContext')}</label>
          <textarea
            value={observacoes}
            onChange={(e) => !positionReadOnly && setObservacoes(e.target.value)}
            placeholder={t('roleContextPlaceholder')}
            rows={4}
            readOnly={positionReadOnly}
            style={positionReadOnly ? {background:'var(--bg-light)', cursor:'default'} : {}}
          />
          <p className="field-hint">{t('roleContextHint')}</p>
        </div>

        {!positionReadOnly && pendingItems.length > 0 && (
          <div className="position-pending-checklist">
            <p className="position-pending-title">{t('positionChecklistTitle')}</p>
            <ul>
              {pendingItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="button-container space-between">
          <button onClick={() => navegarParaEtapa(1)} className="btn-secondary">{t('back')}</button>
          <button
            onClick={async () => {
              if (positionReadOnly) { setEtapa(3); return; }
              const id = await criarPosicao();
              if (id) setEtapa(3);
            }}
            disabled={!positionReadOnly && !canContinue}
            className="btn-primary"
          >
            {t('continueToUpload')}
          </button>
        </div>
      </div>
    );
  };

  // ===========================================================================
  // ETAPA 3: UPLOAD
  // ===========================================================================
  const renderEtapa3 = () => (
    <div className="etapa-container">
      <div className="etapa-header">
        <h2>{t('uploadReports')}</h2>
        <p className="subtitle">{t('uploadSubtitle')}</p>
      </div>

      {erro && <div className="error-box"><p>{erro}</p></div>}

      <form onSubmit={handleUploadCandidate} className="upload-form">
        <div className="form-row">
          <div className="form-group">
            <label>{t('cognitivePdf')}</label>
            <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap'}}>
              <label className="btn-secondary" style={{cursor:'pointer', margin:0}}>
                {t('chooseFile')}
                <input
                  type="file"
                  name="pdfCognitivo"
                  accept=".pdf"
                  required
                  style={{display:'none'}}
                  onChange={(e) => setArquivoCognitivoNome(e.target.files?.[0]?.name || '')}
                />
              </label>
              <span className="form-hint">{arquivoCognitivoNome || t('noFileChosen')}</span>
            </div>
            <span className="form-hint">{t('candidateNameAuto')}</span>
          </div>
          <div className="form-group">
            <label>{t('socioPdf')}</label>
            <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap'}}>
              <label className="btn-secondary" style={{cursor:'pointer', margin:0}}>
                {t('chooseFile')}
                <input
                  type="file"
                  name="pdfSocioemocional"
                  accept=".pdf"
                  required
                  style={{display:'none'}}
                  onChange={(e) => setArquivoSocioNome(e.target.files?.[0]?.name || '')}
                />
              </label>
              <span className="form-hint">{arquivoSocioNome || t('noFileChosen')}</span>
            </div>
          </div>
        </div>

        <button type="submit" className="btn-upload" disabled={!!uploadingCandidate || candidatos.length >= 5}>
          {uploadingCandidate
            ? <><span className="btn-spinner"></span> {t('processingCandidate', { name: uploadingCandidate })}</>
            : t('addCandidate')}
        </button>
        {uploadingCandidate && (
          <p className="upload-processing-hint">
            {t('uploadProcessingHint')}
          </p>
        )}
      </form>

      {candidatos.length > 0 && (
        <div className="candidatos-lista">
          <h3>{t('candidatesAdded', { count: candidatos.length })}</h3>
          {candidatos.map((c, idx) => (
            <div key={idx} className="candidato-item">
              <div className="candidato-info">
                <span className="candidato-avatar">{c.nome.charAt(0)}</span>
                <div>
                  <strong>{c.nome}</strong>
                </div>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                <span className={`status-badge ${c.analise_ok ? 'status-ok' : 'status-warn'}`}>
                  {c.analise_ok ? t('candidateReady') : c.analise_timeout ? t('candidateTimeout') : t('candidateAnalyzing')}
                </span>
                <button
                  className="btn-deletar-candidato"
                  title={t('deleteCandidateTitle')}
                  onClick={() => abrirModalDeletar(c)}
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="button-container space-between">
        <button onClick={() => navegarParaEtapa(2)} className="btn-secondary">{t('back')}</button>
        <button
          onClick={() => {
            // Ao entrar na etapa 4, sempre abre no modo individual com candidatos colapsados.
            setModoComparacao(false);
            setAnaliseExpandida({}); // garante que todos comecem colapsados
            setAnalises(candidatos.map(c => ({
              candidato: c.nome,
              candidatoId: c.id,
              analysisId: analisesIndividuais[c.id]?.analysis_id || null,
              decisoria: analisesIndividuais[c.id]?.decisoria || '',
              analitica: analisesIndividuais[c.id]?.analitica || '',
              recomendacao: analisesIndividuais[c.id]?.recomendacao || ''
            })));
            setEtapa(4);
          }}
          disabled={candidatos.length === 0}
          className="btn-primary"
        >
          {t('continueToAnalysis')}
        </button>
      </div>
    </div>
  );

  // ===========================================================================
  // ETAPA 4: ANÁLISE
  // ===========================================================================
  const renderEtapa4 = () => (
    <div className="etapa-container">
      <div className="etapa-header">
        <h2>{t('analysisTitle')}</h2>
        <p className="subtitle">
          {candidatos.length >= 2
            ? t('analysisSubtitlePlural')
            : t('analysisSubtitleSingle')}
        </p>
      </div>

      {erro && <div className="error-box"><p>{erro}</p></div>}

      {candidatos.length >= 2 && (
        <div className="modo-toggle">
          <button
            className={`modo-btn ${!modoComparacao ? 'modo-active' : ''}`}
            onClick={() => {
              setModoComparacao(false);
              setAnaliseExpandida({}); // sempre recolhe ao trocar para individual
              // Popula direto com as análises já geradas — sem tela intermediária
              setAnalises(candidatos.map(c => ({
                candidato: c.nome,
                candidatoId: c.id,
                analysisId: analisesIndividuais[c.id]?.analysis_id || null,
                decisoria: analisesIndividuais[c.id]?.decisoria || '',
                analitica: analisesIndividuais[c.id]?.analitica || '',
                recomendacao: analisesIndividuais[c.id]?.recomendacao || ''
              })));
            }}
          >
            {t('individualMode')}
          </button>
          <button
            className={`modo-btn ${modoComparacao ? 'modo-active' : ''}`}
            onClick={() => {
              setModoComparacao(true);
              // Restaura resultado já gerado em vez de pedir para gerar de novo
              if (comparativaCache) setAnalises(comparativaCache);
              else setAnalises([]);
            }}
          >
            {t('comparativeMode')}
          </button>
        </div>
      )}

      {analises.length === 0 && !carregando && !comparativaProcessando && (
        <div className="analise-cta">
          <div className="cta-icon">◎</div>
          <h3>
            {modoComparacao
              ? (isEn
                ? `Compare ${candidatos.length} candidates for ${cargo}`
                : `Comparar ${candidatos.length} candidatos para ${cargo}`)
              : (isEn
                ? `View analysis for ${candidatos.length === 1 ? candidatos[0].nome : `${candidatos.length} candidates`}`
                : `Ver análise de ${candidatos.length === 1 ? candidatos[0].nome : candidatos.length + ' candidatos'}`)}
          </h3>
          <p className="cta-context">
            {cargo} · {isEn
              ? `${candidatos.length} candidate${candidatos.length > 1 ? 's' : ''}`
              : `${candidatos.length} candidato${candidatos.length > 1 ? 's' : ''}`}
          </p>
          <button onClick={gerarAnalises} className="btn-primary btn-large">
            {modoComparacao ? t('generateComparative') : t('viewIndividual')}
          </button>
        </div>
      )}

      {carregando && !comparativaProcessando && (
        <div className="loading-box">
          <div className="spinner-large"></div>
          <h3>{t('generatingAnalysis')}</h3>
          <p>{t('waitMoments')}</p>
        </div>
      )}

      {comparativaProcessando && (
        <div className="loading-box">
          <div className="spinner-large"></div>
          <h3>{t('generatingComparative')}</h3>
          <p>{t('mayTakeMinutes')}</p>
        </div>
      )}

      {analises.length > 0 && (
        <div className="analises-resultado">
          {analises.map((analise, idx) => (
            <div key={idx} className="analise-card">
              {analise.tipo === 'comparativa' ? (
                <div className="analise-content">
                  <div className="analise-card-header">
                    <h4>{t('comparativeAnalysisTitle')}</h4>
                    <span className="candidatos-tag">{candidatos.map(c => c.nome).join(' · ')}</span>
                  </div>
                  <div className="markdown-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(analise.texto) }} />
                  <div className="analise-actions">
                    <button
                      className="btn-salvar"
                      onClick={() => exportarPdfComparativa({
                        texto: analise.texto,
                        nomes: candidatos.map(c => c.nome),
                        recomendacao: analise.recomendacao,
                        positionTitle: cargo,
                      })}
                      disabled={exportandoPdf}
                    >
                      {exportandoPdf ? t('exportingPdf') : t('exportPdf')}
                    </button>
                    {analisesSalvas[analise.analysisId] ? (
                      <span className="salvo-badge">{t('analysisSaved')}</span>
                    ) : (
                      <button
                        className="btn-salvar"
                        onClick={() => {
                          if (!analise.analysisId) {
                            alert(t('errSaveMissingAnalysisId'));
                            return;
                          }
                          iniciarSalvar(analise.analysisId, 'comparativa');
                        }}
                        disabled={salvando}
                      >
                        {salvando ? t('saving') : t('saveAnalysis')}
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="analise-content">
                  <div className="analise-card-header" style={{cursor:'pointer'}} onClick={() => toggleExpandida(`${idx}-collapsed`)}>
                    <div className="analise-candidato-id">
                      <span className="candidato-avatar-sm">{analise.candidato.charAt(0)}</span>
                      <h4>{analise.candidato}</h4>
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                      <RecomendacaoBadge recomendacao={analise.recomendacao} locale={tenantLocale} />
                      <span className="collapse-btn">{analiseExpandida[`${idx}-collapsed`] ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {analiseExpandida[`${idx}-collapsed`] && (
                    <>
                      {(() => {
                        const tabAtual = analiseExpandida[`${idx}-tab`] || 'decisoria';
                        const narrativaKey = `individual:${analise.analysisId}`;
                        const narrativa = projecoesNarrativas[narrativaKey];
                        const narrativaLoading = projecaoCarregandoKey === narrativaKey;
                        return (
                          <>
                      <div className="analise-tabs">
                        <button
                          className={`tab-btn ${tabAtual === 'decisoria' ? 'tab-active' : ''}`}
                          onClick={() => setAnaliseExpandida(prev => ({ ...prev, [`${idx}-tab`]: 'decisoria' }))}
                        >
                          {t('decisorySynthesis')}
                        </button>
                        <button
                          className={`tab-btn ${tabAtual === 'analitica' ? 'tab-active' : ''}`}
                          onClick={() => setAnaliseExpandida(prev => ({ ...prev, [`${idx}-tab`]: 'analitica' }))}
                        >
                          {t('analyticalSynthesis')}
                        </button>
                        <button
                          className={`tab-btn tab-btn-projecao ${tabAtual === 'narrativa' ? 'tab-active' : ''}`}
                          onClick={() => setAnaliseExpandida(prev => ({ ...prev, [`${idx}-tab`]: 'narrativa' }))}
                        >
                          {t('projectionTab')}
                        </button>
                      </div>

                      {tabAtual === 'decisoria' ? (
                        <div className="markdown-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(analise.decisoria) }} />
                      ) : tabAtual === 'analitica' ? (
                        <div className="markdown-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(analise.analitica) }} />
                      ) : (
                        <div className="markdown-content">
                          {renderProjectionDisclaimerBox()}
                          {narrativa ? (
                            <div dangerouslySetInnerHTML={{ __html: renderMarkdown(limparCorpoProjecao(narrativa)) }} />
                          ) : (
                            <div className="projection-tab-empty">
                              <p>{narrativaLoading ? t('projectionPreparing') : t('projectionPendingHint')}</p>
                              {!narrativaLoading && (
                                <button
                                  className="btn-salvar"
                                  onClick={() => abrirProjecaoNarrativa({
                                    analysisId: analise.analysisId,
                                    tipo: 'individual',
                                    tituloFallback: analise.candidato,
                                    nomesExibicao: [analise.candidato],
                                  })}
                                  disabled={!analise.analysisId}
                                >
                                  {t('generateProjectionNow')}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                          </>
                        );
                      })()}

                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Botão coletivo de salvar — só aparece no modo individual, quando há análises não salvas */}
          {!modoComparacao && (() => {
            const naoSalvas = analises.filter(a => a.analysisId && !analisesSalvas[a.analysisId]);
            const exportaveis = analises.filter(a => !a.tipo && (a.decisoria || a.analitica));
            if (naoSalvas.length === 0 && exportaveis.length === 0) return null;
            return (
              <div className="button-container salvar-todas-container analise-actions-group">
                {exportaveis.length > 0 && (
                  <button
                    className="btn-salvar"
                    onClick={() => exportarPdfIndividualLote(exportaveis)}
                    disabled={exportandoPdf}
                  >
                    {exportandoPdf ? t('exportingPdf') : t('exportPdfBatch', { count: exportaveis.length })}
                  </button>
                )}
                {naoSalvas.length > 0 && (
                  <button
                    className="btn-salvar"
                    onClick={() => iniciarSalvar(naoSalvas.map(a => a.analysisId), 'individual')}
                    disabled={salvando}
                  >
                    {salvando ? t('saving') : t('saveAnalysesBatch', { count: naoSalvas.length })}
                  </button>
                )}
              </div>
            );
          })()}

          <div className="button-container space-between">
            <button onClick={() => setEtapa(3)} className="btn-secondary">{t('back')}</button>
            {!modoComparacao && candidatos.length >= 2 && (
              <button
                className="btn-primary"
                onClick={() => {
                  setModoComparacao(true);
                  if (comparativaCache) setAnalises(comparativaCache);
                  else setAnalises([]);
                }}
              >
                {comparativaProcessando ? t('comparativeLoadingCta') : comparativaCache ? t('viewComparative') : t('doComparative')}
              </button>
            )}
            {modoComparacao && (
              <button
                className="btn-secondary"
                onClick={() => {
                  setModoComparacao(false);
                  setAnaliseExpandida({}); // sempre recolhe ao voltar para individual
                  setAnalises(candidatos.map(c => ({
                    candidato: c.nome,
                    candidatoId: c.id,
                    analysisId: analisesIndividuais[c.id]?.analysis_id || null,
                    decisoria: analisesIndividuais[c.id]?.decisoria || '',
                    analitica: analisesIndividuais[c.id]?.analitica || '',
                    recomendacao: analisesIndividuais[c.id]?.recomendacao || ''
                  })));
                }}
              >
                {t('viewIndividualsBack')}
              </button>
            )}
          </div>
        </div>
      )}

      {analises.length === 0 && (
        <div className="button-container">
          <button onClick={() => setEtapa(3)} className="btn-secondary">{t('back')}</button>
        </div>
      )}
    </div>
  );

  // ===========================================================================
  // BIBLIOTECA — tela de lista
  // ===========================================================================
  const renderBiblioteca = () => (
    <div className="biblioteca-container content-card">
      <div className="biblioteca-header">
        <div className="biblioteca-header-left">
          <button className="btn-voltar" onClick={() => setTelaAtual('app')}>
            {t('back')}
          </button>
          <h2>{t('analysisLibrary')}</h2>
        </div>
      </div>

      {erro && <div className="error-box"><p>{erro}</p></div>}

      {bibliotecaCarregando && (
        <div className="loading-box">
          <div className="spinner-large"></div>
          <p>{t('loadingSavedAnalyses')}</p>
        </div>
      )}

      {!bibliotecaCarregando && biblioteca && biblioteca.positions.length === 0 && (
        <div className="biblioteca-vazia">
          <div className="biblioteca-vazia-icon">📂</div>
          <h3>{t('noSavedAnalyses')}</h3>
          <p>{t('noSavedAnalysesHint')}</p>
        </div>
      )}

      {!bibliotecaCarregando && biblioteca && biblioteca.positions.map(pos => (
        <div key={pos.position_id} className="biblioteca-posicao">
          <div className="biblioteca-posicao-header">
            <div className="biblioteca-posicao-info">
              <h3>{pos.position_title}</h3>
              <span className="biblioteca-posicao-count">
                {isEn
                  ? `${pos.analyses.length} ${pos.analyses.length === 1 ? 'analysis saved' : 'analyses saved'}`
                  : `${pos.analyses.length} análise${pos.analyses.length !== 1 ? 's' : ''} salva${pos.analyses.length !== 1 ? 's' : ''}`}
              </span>
            </div>
            <button
              className="btn-reaproveitar"
              onClick={() => reaproveitarPosicao(pos)}
              title={t('reusePositionTitle')}
            >
              {t('reusePosition')}
            </button>
          </div>

          <div className="biblioteca-analises-lista">
            {pos.analyses.map(analise => {
              const rec = labelRecomendacao(analise.recommendation);
              return (
                <button
                  key={analise.id}
                  className="biblioteca-analise-item"
                  onClick={() => abrirAnaliseDetalhe(analise)}
                >
                  <div className="biblioteca-analise-tipo">
                    {analise.analysis_type === 'comparative' ? t('comparativeShort') : t('individualShort')}
                  </div>
                  <div className="biblioteca-analise-candidatos">
                    {analise.candidate_names.map(n => restaurarNomeSimples(n)).join(' · ')}
                  </div>
                  <div className="biblioteca-analise-footer">
                    <span className={`rec-badge rec-badge-sm ${rec.cls}`}>{rec.label}</span>
                    <span className="biblioteca-analise-data">{formatarData(analise.created_at)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  // ===========================================================================
  // BIBLIOTECA — tela de detalhe de uma análise
  // ===========================================================================
  const renderAnaliseDetalhe = () => {
    if (!analiseDetalhe) return null;
    const rec = labelRecomendacao(analiseDetalhe.recommendation);
    const isComparativa = analiseDetalhe.analysis_type === 'comparative';
    const nomePrincipal = restaurarNomeSimples(analiseDetalhe.candidate_names?.[0] || 'candidato');
    const nomesDetalhe = (analiseDetalhe.candidate_names || []).map((n) => restaurarNomeSimples(n));
    const comparativaDetalheTexto = isComparativa
      ? normalizarComparativaComNomes(analiseDetalhe.output_comparative || analiseDetalhe.output_decisory || '', nomesDetalhe)
      : '';
    const narrativaDetalheKey = `individual:${analiseDetalhe.id}`;
    const narrativaPersistidaDetalhe = analiseDetalhe.output_narrative
      ? restaurarAliasesProjecao(analiseDetalhe.output_narrative, [nomePrincipal].filter(Boolean))
      : null;
    const narrativaDetalhe = projecoesNarrativas[narrativaDetalheKey] || narrativaPersistidaDetalhe || null;
    const narrativaDetalheLoading = projecaoCarregandoKey === narrativaDetalheKey;

    return (
      <div className="biblioteca-container content-card">
      <div className="biblioteca-header">
        <div className="biblioteca-header-left">
          <button className="btn-voltar" onClick={() => setTelaAtual('biblioteca')}>
            ← {isEn ? 'Library' : 'Biblioteca'}
          </button>
            <div>
              <h2>{analiseDetalhe.position_title}</h2>
              <p className="detalhe-candidatos">{analiseDetalhe.candidate_names.map(n => restaurarNomeSimples(n)).join(' · ')}</p>
            </div>
          </div>
          <div className="detalhe-meta">
            <span className={`rec-badge ${rec.cls}`}>{rec.label}</span>
            <span className="biblioteca-analise-data">{formatarData(analiseDetalhe.created_at)}</span>
            <button
              className="btn-salvar"
              onClick={() => {
                if (isComparativa) {
                  exportarPdfComparativa({
                    texto: comparativaDetalheTexto,
                    nomes: nomesDetalhe,
                    recomendacao: analiseDetalhe.recommendation,
                    positionTitle: analiseDetalhe.position_title,
                    createdAt: analiseDetalhe.created_at,
                  });
                } else {
                  exportarPdfIndividual(
                    {
                      candidato: nomePrincipal,
                      recomendacao: analiseDetalhe.recommendation,
                      decisoria: analiseDetalhe.output_decisory || '',
                      analitica: analiseDetalhe.output_analytical || '',
                      narrativa: narrativaDetalhe || analiseDetalhe.output_narrative || '',
                      analysisId: analiseDetalhe.id,
                    },
                    {
                      createdAt: analiseDetalhe.created_at,
                      positionTitle: analiseDetalhe.position_title,
                      analysisId: analiseDetalhe.id,
                      candidateName: nomePrincipal,
                      narrativeProjection: narrativaDetalhe || analiseDetalhe.output_narrative || '',
                    }
                  );
                }
              }}
              disabled={exportandoPdf}
            >
              {exportandoPdf ? t('exportingPdf') : t('exportPdf')}
            </button>
          </div>
        </div>

        {erro && <div className="error-box" style={{ marginTop: 12 }}><p>{erro}</p></div>}

        {isComparativa ? (
          <>
            <div className="markdown-content"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(comparativaDetalheTexto) }}
            />
          </>
        ) : (
          <>
            <div className="analise-tabs">
              <button
                className={`tab-btn ${analiseDetalheTab === 'decisoria' ? 'tab-active' : ''}`}
                onClick={() => setAnaliseDetalheTab('decisoria')}
              >
                {t('decisorySynthesis')}
              </button>
              <button
                className={`tab-btn ${analiseDetalheTab === 'analitica' ? 'tab-active' : ''}`}
                onClick={() => setAnaliseDetalheTab('analitica')}
              >
                {t('analyticalSynthesis')}
              </button>
              <button
                className={`tab-btn tab-btn-projecao ${analiseDetalheTab === 'narrativa' ? 'tab-active' : ''}`}
                onClick={() => setAnaliseDetalheTab('narrativa')}
              >
                {t('projectionTab')}
              </button>
            </div>
            {analiseDetalheTab === 'decisoria' ? (
              <div className="markdown-content"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(analiseDetalhe.output_decisory) }}
              />
            ) : analiseDetalheTab === 'analitica' ? (
              <div className="markdown-content"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(analiseDetalhe.output_analytical) }}
              />
            ) : (
              <div className="markdown-content">
                {renderProjectionDisclaimerBox()}
                {narrativaDetalhe ? (
                  <div dangerouslySetInnerHTML={{ __html: renderMarkdown(limparCorpoProjecao(narrativaDetalhe)) }} />
                ) : (
                  <div className="projection-tab-empty">
                    <p>{narrativaDetalheLoading ? t('projectionPreparing') : t('projectionPendingHint')}</p>
                    {!narrativaDetalheLoading && (
                      <button
                        className="btn-salvar"
                        onClick={async () => {
                          const projection = await gerarProjecaoNarrativa({
                            analysisId: analiseDetalhe.id,
                            tipo: 'individual',
                            nomesExibicao: [nomePrincipal].filter(Boolean),
                            silent: false,
                          });
                          if (!projection) return;
                          setAnaliseDetalhe(prev => prev ? { ...prev, output_narrative: projection } : prev);
                          setBiblioteca(prev => {
                            if (!prev?.positions) return prev;
                            return {
                              ...prev,
                              positions: prev.positions.map(pos => ({
                                ...pos,
                                analyses: (pos.analyses || []).map(a => (
                                  a.id === analiseDetalhe.id ? { ...a, output_narrative: projection } : a
                                )),
                              })),
                            };
                          });
                        }}
                        disabled={!analiseDetalhe.id}
                      >
                        {t('generateProjectionNow')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderModalEditarDimensao = () => {
    if (!modalEditarDimensao) return null;
    const categoria = modalEditarDimensao.categoria;
    const dimensionLabel = isEn ? (DIMENSION_LABELS_EN[categoria] || categoria) : categoria;

    return (
      <div className="modal-overlay" onClick={() => setModalEditarDimensao(null)}>
        <div className="modal-box" onClick={e => e.stopPropagation()}>
          <div className="modal-icon">✏️</div>
          <h3>{t('positionEditChoiceTitle')}</h3>
          <p>{t('positionEditChoiceConfirm', { dimension: dimensionLabel })}</p>
          <div className="modal-actions">
            <button className="btn-secondary" onClick={() => setModalEditarDimensao(null)}>
              {t('cancel')}
            </button>
            <button className="btn-primary" onClick={confirmarEdicaoDimensao}>
              {t('confirm')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===========================================================================
  // MODAL DE CONTRADIÇÃO — CAMADA 0B
  // ===========================================================================
  const renderModalContradiction = () => {
    if (!modalContradiction) return null;
    const { contradictions } = modalContradiction;
    const current = contradictions[contradictionIdx];
    if (!current) return null;
    const total = contradictions.length;
    const isTradeoff = current.kind === 'tradeoff';
    const ptName = PT_NAMES[current.id] || current.id;
    const principle = getPrincipleById(current.id);
    const configuredChoice = getSliderChoiceLabel(current.slider_level);
    const inferredChoice = getSliderChoiceLabel(current.inferred_level);
    const configuredSummary = getPoleSummaryByLevel(principle, current.slider_level);
    const inferredSummary = getPoleSummaryByLevel(principle, current.inferred_level);
    const keepButtonClass = isTradeoff ? 'btn-primary' : 'btn-secondary';
    const applyButtonClass = isTradeoff ? 'btn-secondary' : 'btn-primary';

    return (
      <div className="modal-overlay" onClick={() => setModalContradiction(null)}>
        <div className="modal-box modal-box-wide" onClick={e => e.stopPropagation()}>
          <div className="modal-icon">🔍</div>
          {total > 1 && (
            <p className="modal-counter">
              {t('cultureReviewCounter', { current: contradictionIdx + 1, total })}
            </p>
          )}
          <h3>{isTradeoff ? t('tradeoffDetected', { name: ptName }) : t('contradictionDetected', { name: ptName })}</h3>
          <p>{isTradeoff ? getTradeoffDescription(current) : getContradictionDescription(current.description)}</p>
          {isTradeoff && (
            <div className="tradeoff-evidence-grid">
              <div className="tradeoff-evidence-card">
                <span className="contradiction-label">{t('tradeoffCurrentEvidenceTitle')}</span>
                <p className="tradeoff-evidence-text">{getTradeoffEvidence(current.evidence_for_current)}</p>
              </div>
              <div className="tradeoff-evidence-card">
                <span className="contradiction-label">{t('tradeoffAlternativeEvidenceTitle')}</span>
                <p className="tradeoff-evidence-text">{getTradeoffEvidence(current.evidence_for_alternative)}</p>
              </div>
            </div>
          )}
          <div className="contradiction-levels">
            <div className="contradiction-level">
              <span className="contradiction-label">{t('contradictionCurrentChoice')}</span>
              <span className="contradiction-value">{configuredChoice}</span>
              {configuredSummary && <span className="contradiction-detail">{configuredSummary}</span>}
            </div>
            <div className="contradiction-arrow">→</div>
            <div className="contradiction-level">
              <span className="contradiction-label">{t('contradictionSuggestedChoice')}</span>
              <span className="contradiction-value">{inferredChoice}</span>
              {inferredSummary && <span className="contradiction-detail">{inferredSummary}</span>}
            </div>
          </div>
          <p className="modal-hint">{isTradeoff ? t('tradeoffDecisionHint') : t('adjustSliderQuestion')}</p>
          <div className="modal-actions">
            <button
              className={keepButtonClass}
              onClick={() => handleContradictionDecision(false)}
            >
              {t('keepCurrentSlider')}
            </button>
            <button
              className={applyButtonClass}
              onClick={() => handleContradictionDecision(true)}
            >
              {t('applySuggestedChoice')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===========================================================================
  // MODAL DE DECISÃO — INCLUSÃO DE CANDIDATOS NOT RECOMMENDED NA COMPARATIVA
  // ===========================================================================
  const renderModalComparativa = () => {
    if (!modalComparativa) return null;
    const { nenhum, finalistaNome, naoRecomendados, podeExcluir } = modalComparativa;

    return (
      <div className="modal-overlay" onClick={() => setModalComparativa(null)}>
        <div className="modal-box" onClick={e => e.stopPropagation()}>
          <div className="modal-icon">⚠️</div>
          <h3>{isEn ? 'Candidates marked as Not Recommended' : 'Candidatos marcados como Não Recomendado'}</h3>
          {podeExcluir ? (
            <p>
              {isEn
                ? <>The following candidates were marked as <strong>Not Recommended</strong> in the individual analysis: <strong>{naoRecomendados.join(', ')}</strong>. Do you want to include them in the comparative analysis?</>
                : <>Os seguintes candidatos foram marcados como <strong>Não Recomendado</strong> na análise individual: <strong>{naoRecomendados.join(', ')}</strong>. Deseja incluí-los na análise comparativa?</>}
            </p>
          ) : (
            <p>
              {isEn
                ? <>
                    {nenhum
                      ? 'No candidate has an individually hireable category.'
                      : <>Only <strong>{finalistaNome}</strong> has an individually hireable category.</>}
                    {' '}
                    <strong>{naoRecomendados.join(', ')}</strong>{' '}
                    {naoRecomendados.length === 1 ? 'was marked' : 'were marked'} as Not Recommended.
                    {' '}
                    To run comparative analysis now, you need to include these candidates.
                  </>
                : <>
                    {nenhum
                      ? 'Nenhum candidato tem categoria individual contratável.'
                      : <>Apenas <strong>{finalistaNome}</strong> tem categoria individual contratável.</>}
                    {' '}
                    <strong>{naoRecomendados.join(', ')}</strong>{' '}
                    {naoRecomendados.length === 1 ? 'foi marcado' : 'foram marcados'} como Não Recomendado.
                    {' '}
                    Para gerar a comparativa agora, é necessário incluir esses candidatos.
                  </>}
            </p>
          )}
          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => setModalComparativa(null)}
            >
              {t('cancel')}
            </button>
            {podeExcluir && (
              <button
                className="btn-secondary"
                onClick={() => executarComparativa(false)}
              >
                {isEn ? 'Exclude and continue' : 'Não incluir e continuar'}
              </button>
            )}
            <button
              className="btn-primary"
              onClick={() => executarComparativa(true)}
            >
              {isEn ? 'Include and continue' : 'Incluir e continuar'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===========================================================================
  // MODAL DE CONFIRMAÇÃO DE SALVAMENTO
  // ===========================================================================
  const renderModalSalvar = () => {
    if (!modalSalvar) return null;
    const analysisIds = modalSalvar.analysisIds || (modalSalvar.analysisId ? [modalSalvar.analysisId] : []);
    return (
      <div className="modal-overlay" onClick={() => setModalSalvar(null)}>
        <div className="modal-box" onClick={e => e.stopPropagation()}>
          <div className="modal-icon">💾</div>
          <h3>{t('saveAnalysisModalTitle')}</h3>
          <p>
            {t('saveAnalysisModalBody')}
          </p>
          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => setModalSalvar(null)}
              disabled={salvando}
            >
              {t('cancel')}
            </button>
            <button
              className="btn-primary"
              onClick={() => executarSalvarLote(analysisIds, false)}
              disabled={salvando}
            >
              {salvando ? t('saving') : t('confirm')}
            </button>
          </div>
          <button
            className="modal-suprimir"
            onClick={() => executarSalvarLote(analysisIds, true)}
            disabled={salvando}
          >
            {t('confirmHide60Days')}
          </button>
        </div>
      </div>
    );
  };

  const renderModalProjecaoNarrativa = () => {
    if (!modalProjecaoNarrativa) return null;
    return (
      <div className="modal-overlay" onClick={() => setModalProjecaoNarrativa(null)}>
        <div className="modal-box modal-box-wide modal-projecao" onClick={e => e.stopPropagation()}>
          <div className="modal-icon">🔮</div>
          <h3>{modalProjecaoNarrativa.title || t('narrativeProjectionTitle')}</h3>
          <div
            className="markdown-content modal-projecao-content"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(limparCorpoProjecao(modalProjecaoNarrativa.markdown || '')) }}
          />
          <div className="modal-actions">
            <button className="btn-primary" onClick={() => setModalProjecaoNarrativa(null)}>
              {t('confirm')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderProjectionDisclaimerBox = () => (
    <div className="projection-disclaimer-box">
      <div className="projection-disclaimer-title">{t('projectionDisclaimerTitle')}</div>
      <p className="projection-disclaimer-body">{t('projectionDisclaimerBody')}</p>
    </div>
  );

  const renderFaq = () => {
    const allExpanded = faqItems.length > 0 && faqItems.every((item) => faqExpanded[item.id]);

    return (
      <div className="faq-container content-card">
        <div className="users-header">
          <div className="users-header-left">
            <button className="btn-voltar" onClick={() => setTelaAtual('app')}>
              {t('faqBack')}
            </button>
            <div>
              <h2>{t('faqTitle')}</h2>
              <p className="subtitle">{t('faqSubtitle')}</p>
            </div>
          </div>
          <button
            className="btn-secondary"
            type="button"
            onClick={() => setFaqExpanded(buildFaqExpansionMap(faqItems, !allExpanded))}
          >
            {allExpanded ? t('faqCollapseAll') : t('faqExpandAll')}
          </button>
        </div>

        <div className="faq-list">
          {faqItems.map((item, index) => {
            const isOpen = Boolean(faqExpanded[item.id]);
            return (
              <section className={`faq-item ${isOpen ? 'faq-item-open' : ''}`} key={item.id}>
                <button
                  className="faq-question"
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setFaqExpanded((prev) => ({ ...prev, [item.id]: !isOpen }))}
                >
                  <span className="faq-question-index">{index + 1}</span>
                  <span className="faq-question-text">{item.question}</span>
                  <span className="faq-question-toggle" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                </button>

                {isOpen && (
                  <div className="faq-answer">
                    {(item.paragraphs || []).map((paragraph, paragraphIndex) => (
                      <p key={`${item.id}-paragraph-${paragraphIndex}`}>{paragraph}</p>
                    ))}

                    {Array.isArray(item.steps) && item.steps.length > 0 && (
                      <ol className="faq-steps">
                        {item.steps.map((step, stepIndex) => (
                          <li key={`${item.id}-step-${stepIndex}`}>{step}</li>
                        ))}
                      </ol>
                    )}

                    {Array.isArray(item.points) && item.points.length > 0 && (
                      <ul className="faq-points">
                        {item.points.map((point, pointIndex) => (
                          <li key={`${item.id}-point-${pointIndex}`}>{point}</li>
                        ))}
                      </ul>
                    )}

                    {item.action && (
                      <p className="faq-action">
                        <strong>{t('faqWhatToDoPrefix')}</strong> {item.action}
                      </p>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    );
  };

  const renderUserManagement = () => {
    const roleOptions = normalizedUserRole === 'master_admin'
      ? ['master_admin', 'admin_client', 'user_client']
      : ['admin_client', 'user_client'];
    const showEmailInviteForm = INVITES_ENABLED && !isGlobalAdminContext;
    const showNoEmailProvisionForm = !INVITES_ENABLED && isGlobalAdminContext;
    const targetTenantName = isGlobalAdminContext
      ? (globalUsersTenant?.name || '-')
      : (sessao?.company_name || '-');
    const backLabel = isGlobalAdminContext ? t('globalUsersBack') : t('userManagementBack');
    const subtitle = isGlobalAdminContext
      ? t('globalUsersForTenant', { name: targetTenantName })
      : t('userManagementSubtitle');
    const hasTargetTenant = Boolean(usersTargetCompanyId);

    return (
      <div className="users-container content-card">
        <div className="users-header">
          <div className="users-header-left">
            <button
              className="btn-voltar"
              onClick={() => {
                setTelaAtual('app');
                if (isGlobalAdminContext) setGlobalUsersTenant(null);
              }}
            >
              {backLabel}
            </button>
            <div>
              <h2>{t('userManagementTitle')}</h2>
              <p className="subtitle">{subtitle}</p>
            </div>
          </div>
          <button
            className="btn-secondary"
            onClick={() => carregarUsuariosTenant({ companyIdOverride: usersTargetCompanyId })}
            disabled={usersCarregando || !hasTargetTenant}
          >
            {t('userManagementRefresh')}
          </button>
        </div>

        {usersErro && <div className="error-box"><p>{usersErro}</p></div>}
        {usersFeedback && (
          <div className="success-box">
            <p>{usersFeedback}</p>
          </div>
        )}

        {!INVITES_ENABLED && (
          <div className="info-box users-invite-disabled-box">
            <p>{t('userManagementInviteDisabledBanner')}</p>
          </div>
        )}

        {showEmailInviteForm && (
          <div className="users-invite-card">
            <h3>{t('userManagementInviteTitle')}</h3>
            <form onSubmit={handleInviteUser} className="users-invite-form">
              <input
                type="text"
                placeholder={t('userManagementInviteName')}
                value={inviteForm.name}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, name: e.target.value }))}
                disabled={inviteSubmitting}
              />
              <input
                type="email"
                placeholder={t('userManagementInviteEmail')}
                value={inviteForm.email}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                disabled={inviteSubmitting}
                required
              />
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, role: e.target.value }))}
                disabled={inviteSubmitting}
              >
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {getRoleLabel(role)}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-primary" disabled={inviteSubmitting}>
                {inviteSubmitting ? t('userManagementInviteSubmitting') : t('userManagementInviteSubmit')}
              </button>
            </form>
          </div>
        )}

        {showNoEmailProvisionForm && (
          <div className="users-invite-card">
            <h3>{t('userManagementProvisionTitle')}</h3>
            <form onSubmit={handleProvisionTenantUser} className="users-invite-form users-provision-form">
              <input
                type="text"
                placeholder={t('userManagementInviteName')}
                value={provisionForm.name}
                onChange={(e) => setProvisionForm((prev) => ({ ...prev, name: e.target.value }))}
                disabled={provisionSubmitting}
              />
              <input
                type="email"
                placeholder={t('userManagementInviteEmail')}
                value={provisionForm.email}
                onChange={(e) => setProvisionForm((prev) => ({ ...prev, email: e.target.value }))}
                disabled={provisionSubmitting}
                required
              />
              <select
                value={provisionForm.role}
                onChange={(e) => setProvisionForm((prev) => ({ ...prev, role: e.target.value }))}
                disabled={provisionSubmitting}
              >
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {getRoleLabel(role)}
                  </option>
                ))}
              </select>
              <select
                value={provisionForm.status}
                onChange={(e) => setProvisionForm((prev) => ({ ...prev, status: e.target.value }))}
                disabled={provisionSubmitting}
              >
                <option value="invited">{getStatusLabel('invited')}</option>
                <option value="active">{getStatusLabel('active')}</option>
                <option value="inactive">{getStatusLabel('inactive')}</option>
              </select>
              <button type="submit" className="btn-primary" disabled={provisionSubmitting}>
                {provisionSubmitting ? t('userManagementProvisionSubmitting') : t('userManagementProvisionSubmit')}
              </button>
            </form>
          </div>
        )}

        {!hasTargetTenant && (
          <div className="users-empty-state">
            <p>{t('errGlobalUsersTenantMissing')}</p>
          </div>
        )}

        {hasTargetTenant && (
        <div className="users-list-card">
          <h3>{t('userManagementListTitle')}</h3>

          {usersCarregando && (
            <div className="loading-box users-loading-box">
              <div className="spinner-large"></div>
              <p>{t('userManagementLoading')}</p>
            </div>
          )}

          {!usersCarregando && tenantUsers.length === 0 && (
            <div className="users-empty-state">
              <p>{t('userManagementEmpty')}</p>
            </div>
          )}

          {!usersCarregando && tenantUsers.length > 0 && (
            <div className="tenant-users-grid">
              {tenantUsers.map((user) => {
                const normalizedRole = normalizeUserRole(user.role);
                const normalizedStatus = normalizeUserStatus(user.status);
                const draft = userDraftMap[user.id] || {
                  role: normalizedRole,
                  status: normalizedStatus,
                };
                const userIsBusy = Boolean(userActionLoadingMap[user.id]);
                const canEditTargetUser = normalizedUserRole === 'master_admin' || normalizedRole !== 'master_admin';
                const hasChanges = hasUserDraftChanges(user);

                return (
                  <div className="tenant-user-card" key={user.id}>
                    <div className="tenant-user-main">
                      <div className="tenant-user-title-row">
                        <strong>{user.name || user.email || 'User'}</strong>
                        {user.id === userId && (
                          <span className="tenant-user-self">{t('userManagementSelfTag')}</span>
                        )}
                      </div>
                      <span className="tenant-user-email">{user.email}</span>
                    </div>

                    <div className="tenant-user-fields">
                      <label>
                        {t('userManagementRole')}
                        <select
                          value={draft.role}
                          disabled={userIsBusy || !canEditTargetUser}
                          onChange={(e) => updateUserDraftField(user, 'role', e.target.value)}
                        >
                          {(canEditTargetUser ? roleOptions : ['master_admin']).map((role) => (
                            <option key={role} value={role}>
                              {getRoleLabel(role)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        {t('userManagementStatus')}
                        <select
                          value={draft.status}
                          disabled={userIsBusy || !canEditTargetUser}
                          onChange={(e) => updateUserDraftField(user, 'status', e.target.value)}
                        >
                          <option value="invited">{getStatusLabel('invited')}</option>
                          <option value="active">{getStatusLabel('active')}</option>
                          <option value="inactive">{getStatusLabel('inactive')}</option>
                        </select>
                      </label>
                    </div>

                    <div className="tenant-user-footer">
                      <span className={`status-badge ${normalizedStatus === 'active' ? 'status-ok' : normalizedStatus === 'inactive' ? 'status-warn' : 'status-invited'}`}>
                        {getStatusLabel(normalizedStatus)}
                      </span>
                      <div className="tenant-user-actions">
                        {INVITES_ENABLED && !isGlobalAdminContext && (
                          <button
                            className="btn-secondary"
                            type="button"
                            disabled={userIsBusy}
                            onClick={() => handleResendUserAccess(user)}
                          >
                            {t('userManagementResend')}
                          </button>
                        )}
                        <button
                          className="btn-primary"
                          type="button"
                          disabled={userIsBusy || !canEditTargetUser || !hasChanges}
                          onClick={() => handleSaveUserAccess(user)}
                        >
                          {userIsBusy ? t('userManagementSaving') : t('userManagementSave')}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>
    );
  };

  const renderCredits = () => {
    const currentBalance = Number(creditsSummary?.company?.credits_available || 0);
    const usedTotal = Number(creditsSummary?.company?.credits_used || 0);
    const individualCost = Number(creditsSummary?.credit_policy?.individual_cost || 1);
    const comparativeCost = Number(creditsSummary?.credit_policy?.comparative_cost || 2);

    return (
      <div className="credits-container content-card">
        <div className="users-header">
          <div className="users-header-left">
            <button className="btn-voltar" onClick={() => setTelaAtual('app')}>
              {t('creditsBack')}
            </button>
            <div>
              <h2>{t('creditsTitle')}</h2>
              <p className="subtitle">{t('creditsSubtitle')}</p>
            </div>
          </div>
          <button
            className="btn-secondary"
            onClick={() => carregarCreditosTenant()}
            disabled={creditsLoading || creditsAdjustLoading}
          >
            {t('creditsRefresh')}
          </button>
        </div>

        {creditsError && <div className="error-box"><p>{creditsError}</p></div>}
        {creditsFeedback && (
          <div className="success-box">
            <p>{creditsFeedback}</p>
          </div>
        )}

        <div className="credits-summary-grid">
          <div className="credits-summary-card">
            <span className="credits-summary-label">{t('creditsCurrentBalance')}</span>
            <strong className="credits-summary-value">{currentBalance}</strong>
          </div>
          <div className="credits-summary-card">
            <span className="credits-summary-label">{t('creditsUsedTotal')}</span>
            <strong className="credits-summary-value">{usedTotal}</strong>
          </div>
        </div>
        <p className="credits-policy-copy">
          {t('creditsPolicy', { individual: individualCost, comparative: comparativeCost })}
        </p>

        {canAdjustCredits && (
          <div className="credits-adjust-card">
            <h3>{t('creditsAdjustTitle')}</h3>
            <form onSubmit={handleCreditAdjustSubmit} className="credits-adjust-form">
              <label>
                {t('creditsAdjustAmountLabel')}
                <input
                  type="number"
                  step="1"
                  value={creditsAdjustForm.amount_delta}
                  placeholder={t('creditsAdjustAmountPlaceholder')}
                  onChange={(e) => setCreditsAdjustForm((prev) => ({ ...prev, amount_delta: e.target.value }))}
                  disabled={creditsAdjustLoading || creditsLoading}
                />
              </label>
              <label>
                {t('creditsAdjustReasonLabel')}
                <input
                  type="text"
                  value={creditsAdjustForm.reason}
                  placeholder={t('creditsAdjustReasonPlaceholder')}
                  onChange={(e) => setCreditsAdjustForm((prev) => ({ ...prev, reason: e.target.value }))}
                  disabled={creditsAdjustLoading || creditsLoading}
                />
              </label>
              <button className="btn-primary" type="submit" disabled={creditsAdjustLoading || creditsLoading}>
                {creditsAdjustLoading ? t('creditsAdjustSubmitting') : t('creditsAdjustSubmit')}
              </button>
            </form>
          </div>
        )}

        <div className="credits-history-card">
          <h3>{t('creditsHistoryTitle')}</h3>

          {creditsLoading && (
            <div className="loading-box users-loading-box">
              <div className="spinner-large"></div>
              <p>{t('checking')}</p>
            </div>
          )}

          {!creditsLoading && creditsHistory.length === 0 && (
            <div className="users-empty-state">
              <p>{t('creditsHistoryEmpty')}</p>
            </div>
          )}

          {!creditsLoading && creditsHistory.length > 0 && (
            <div className="credits-history-list">
              {creditsHistory.map((entry) => (
                <div className="credits-history-item" key={entry.id}>
                  <div className="credits-history-row">
                    <span>{t('creditsHistoryDate')}</span>
                    <strong>{formatarData(entry.created_at)}</strong>
                  </div>
                  <div className="credits-history-row">
                    <span>{t('creditsHistoryOperation')}</span>
                    <strong>{getCreditOperationLabel(entry.operation)}</strong>
                  </div>
                  <div className="credits-history-row">
                    <span>{t('creditsHistoryAmount')}</span>
                    <strong className={getCreditAmountClass(entry.operation)}>
                      {getCreditSignedAmount({ operation: entry.operation, amount: entry.amount })}
                    </strong>
                  </div>
                  <div className="credits-history-row">
                    <span>{t('creditsHistoryBalanceAfter')}</span>
                    <strong>{Number(entry.balance_after || 0)}</strong>
                  </div>
                  <div className="credits-history-row">
                    <span>{t('creditsHistoryPerformedBy')}</span>
                    <strong>{entry.performed_by_name || entry.performed_by_email || t('creditsUnknownUser')}</strong>
                  </div>
                  <div className="credits-history-row">
                    <span>{t('creditsHistoryAnalysis')}</span>
                    <strong>
                      {entry.analysis_id
                        ? `${getAnalysisTypeLabel(entry.analysis_type)} · ${entry.analysis_id.slice(0, 8)}`
                        : t('creditsUnknownAnalysis')}
                    </strong>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGlobalAdminPanel = () => (
    <div className="etapa-container">
      <div className="etapa-header">
        <h2>{t('globalAdminWelcomeTitle')}</h2>
        <p className="subtitle">{t('globalAdminWelcomeBody')}</p>
      </div>

      {globalTenantsError && <div className="error-box"><p>{globalTenantsError}</p></div>}
      {loginErro && <div className="error-box"><p>{loginErro}</p></div>}
      {globalTenantsFeedback && (
        <div className="success-box">
          <p>{globalTenantsFeedback}</p>
        </div>
      )}

      {!INVITES_ENABLED && (
        <div className="info-box users-invite-disabled-box">
          <p>{t('userManagementInviteDisabledBanner')}</p>
        </div>
      )}

      <div className="users-invite-card global-tenant-create-card">
        <h3>{t('globalTenantsCreateTitle')}</h3>
        <form onSubmit={handleCreateGlobalTenant} className="global-tenant-create-form">
          <label className="tenant-form-field tenant-field-name">
            <span>{t('globalTenantsName')}</span>
            <input
              type="text"
              value={globalTenantForm.name}
              placeholder={t('globalTenantsName')}
              onChange={(e) => handleGlobalTenantFieldChange('name', e.target.value)}
              disabled={globalTenantCreateLoading}
              required
            />
          </label>
          <label className="tenant-form-field tenant-field-language">
            <span>{t('globalTenantsLanguage')}</span>
            <select
              value={globalTenantForm.language}
              onChange={(e) => handleGlobalTenantFieldChange('language', e.target.value)}
              disabled={globalTenantCreateLoading}
            >
              <option value="pt-BR">pt-BR</option>
              <option value="en-US">en-US</option>
            </select>
          </label>
          <div className="tenant-checkbox-group tenant-field-flags">
            <label className="tenant-checkbox-field">
              <input
                type="checkbox"
                checked={Boolean(globalTenantForm.active)}
                onChange={(e) => handleGlobalTenantFieldChange('active', e.target.checked)}
                disabled={globalTenantCreateLoading}
              />
              <span>{t('globalTenantsStatus')}</span>
            </label>
          </div>
          <div className="tenant-token-field tenant-field-token">
            <label className="tenant-form-field tenant-token-input-field">
              <span>{t('globalTenantsToken')}</span>
              <input
                type="text"
                value={globalTenantForm.access_token}
                placeholder={t('globalTenantsToken')}
                onChange={(e) => {
                  setGlobalTenantTokenTouched(true);
                  handleGlobalTenantFieldChange('access_token', e.target.value);
                }}
                disabled={globalTenantCreateLoading}
                required
              />
            </label>
            <button
              type="button"
              className="btn-secondary tenant-generate-token-btn"
              onClick={regenerateGlobalTenantToken}
              disabled={globalTenantCreateLoading}
            >
              {t('globalTenantsGenerateToken')}
            </button>
          </div>
          <button type="submit" className="btn-primary global-tenant-create-submit" disabled={globalTenantCreateLoading}>
            {globalTenantCreateLoading ? t('globalTenantsCreateSubmitting') : t('globalTenantsCreateSubmit')}
          </button>
        </form>
      </div>

      <div className="users-list-card">
        <div className="users-header global-tenant-list-header">
          <div>
            <h3>{t('globalTenantsListTitle')}</h3>
          </div>
          <button
            className="btn-secondary"
            onClick={() => loadGlobalTenants()}
            disabled={globalTenantsLoading}
          >
            {t('globalTenantsRefresh')}
          </button>
        </div>

        {globalTenantsLoading && (
          <div className="loading-box users-loading-box">
            <div className="spinner-large"></div>
            <p>{t('checking')}</p>
          </div>
        )}

        {!globalTenantsLoading && globalTenants.length === 0 && (
          <div className="users-empty-state">
            <p>{t('globalTenantsEmpty')}</p>
          </div>
        )}

        {!globalTenantsLoading && globalTenants.length > 0 && (
          <div className="global-tenant-list">
            {globalTenants.map((tenant) => {
              const draft = globalTenantEditMap[tenant.company_id] || {
                name: tenant.name || '',
                language: tenant.language || 'pt-BR',
                active: tenant.active !== false,
              };
              const isBusy = Boolean(globalTenantEditLoadingMap[tenant.company_id]);
              const hasChanges = hasGlobalTenantChanges(tenant);
              const enteringThisTenant = globalEnteringTenantId === tenant.company_id;

              return (
                <div className="global-tenant-item global-tenant-item-editable" key={tenant.company_id}>
                  <div className="global-tenant-info global-tenant-edit-fields">
                    <label className="tenant-form-field">
                      <span>{t('globalTenantsName')}</span>
                      <input
                        type="text"
                        value={draft.name}
                        onChange={(e) => updateGlobalTenantDraftField(tenant, 'name', e.target.value)}
                        disabled={isBusy}
                      />
                    </label>
                    <div className="global-tenant-meta-row">
                      <label className="tenant-form-field tenant-edit-language-field">
                        <span>{t('globalTenantsLanguage')}</span>
                        <select
                          value={draft.language}
                          onChange={(e) => updateGlobalTenantDraftField(tenant, 'language', e.target.value)}
                          disabled={isBusy}
                        >
                          <option value="pt-BR">pt-BR</option>
                          <option value="en-US">en-US</option>
                        </select>
                      </label>
                      <label className="tenant-checkbox-field">
                        <input
                          type="checkbox"
                          checked={Boolean(draft.active)}
                          onChange={(e) => updateGlobalTenantDraftField(tenant, 'active', e.target.checked)}
                          disabled={isBusy}
                        />
                        <span>{Boolean(draft.active) ? t('globalTenantsActive') : t('globalTenantsInactive')}</span>
                      </label>
                    </div>
                    <div className="global-tenant-meta-texts">
                      <span>{t('globalTenantsToken')}: {tenant.access_token || '-'}</span>
                      <span>{t('globalTenantsCreatedAt')}: {formatarData(tenant.created_at)}</span>
                    </div>
                  </div>

                  <div className="global-tenant-actions">
                    <button
                      className="btn-secondary"
                      type="button"
                      disabled={isBusy}
                      onClick={() => abrirGestaoUsuariosGlobal(tenant)}
                    >
                      {t('globalTenantsManageUsers')}
                    </button>
                    <button
                      className="btn-secondary"
                      type="button"
                      disabled={isBusy || Boolean(globalEnteringTenantId)}
                      onClick={async () => {
                        setGlobalEnteringTenantId(tenant.company_id);
                        try {
                          await handleSelectContext({
                            selectedValue: `company:${tenant.company_id}`,
                            accessToken: authAccessToken,
                            loginEmailValue: contextLoginEmail,
                            silentOnError: false,
                          });
                        } finally {
                          setGlobalEnteringTenantId(null);
                        }
                      }}
                    >
                      {enteringThisTenant ? t('checking') : t('enterTenant')}
                    </button>
                    <button
                      className="btn-primary"
                      type="button"
                      disabled={isBusy || !hasChanges}
                      onClick={() => handleSaveGlobalTenant(tenant)}
                    >
                      {isBusy ? t('globalTenantsSavingEdit') : t('globalTenantsSaveEdit')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ===========================================================================
  // RENDER PRINCIPAL
  // ===========================================================================
  const etapas = [
    { nome: isEn ? 'Principles' : 'Princípios', num: 1 },
    { nome: isEn ? 'Position' : 'Posição', num: 2 },
    { nome: 'Upload', num: 3 },
    { nome: isEn ? 'Analysis' : 'Análise', num: 4 },
  ];

  // Uma etapa está "desbloqueada" (clicável) se já foi concluída — para frente ou para trás.
  const etapaDesbloqueada = (num) => {
    if (num === 1) return ptsSalvos || etapa > 1;
    if (num === 2) return ptsSalvos || positionId !== null || etapa > 2;
    if (num === 3) return positionId !== null && (candidatos.length > 0 || etapa > 3);
    if (num === 4) return etapa === 4;
    return false;
  };

  const navegarParaBarra = (num) => {
    if (num < etapa) {
      // Voltar — usa modo visualização
      navegarParaEtapa(num);
    } else if (num > etapa && etapaDesbloqueada(num)) {
      // Avançar — vai direto sem modo visualização
      setModoVisualizacao(false);
      setEtapa(num);
    }
  };

  const canSwitchContext = Boolean(authAccessToken) && contextOptions.length > 1;

  return (
    <div className="app-container">
      <div className="main-content">
        <header className="app-header">
          <div className="header-left">
            <img src={skillcertLogo} alt="SkillCert" className="header-logo" />
          </div>
          <div className="header-right">
            {!isGlobalAdminContext && (
              <button className="btn-biblioteca" onClick={abrirBiblioteca}>
                {t('library')}
              </button>
            )}
            {!isGlobalAdminContext && (
              <button className="btn-biblioteca" onClick={abrirCreditos}>
                {t('creditsHeaderButton')}
              </button>
            )}
            {!isGlobalAdminContext && (
              <button className="btn-biblioteca" onClick={abrirFaq}>
                {t('helpHeaderButton')}
              </button>
            )}
            {canManageUsersTenantContext && (
              <button className="btn-biblioteca" onClick={abrirGestaoUsuarios}>
                {t('manageUsersHeaderButton')}
              </button>
            )}
            {canSwitchContext && (
              <button className="btn-secondary btn-switch-context" onClick={handleSwitchContext}>
                {t('switchTenant')}
              </button>
            )}
            <button className="user-chip" onClick={() => resetAuthToLogin(false)}>
              <span className="user-chip-avatar">{usuarioAtivo.avatar}</span>
              <span className="user-chip-info">
                <span className="user-chip-nome">{usuarioAtivo.nome}</span>
                {sessao?.company_name && (
                  <span className="user-chip-empresa">{sessao.company_name}</span>
                )}
              </span>
            </button>
          </div>
        </header>

        {telaAtual === 'app' && !isGlobalAdminContext && (
        <div className="progress-bar">
          {etapas.map((step, idx) => {
            const ativo = etapa === step.num;
            const completo = etapa > step.num;
            const clicavel = step.num !== etapa && etapaDesbloqueada(step.num);
            const futuro = step.num > etapa;
            return (
              <div key={step.nome} className="progress-step-container">
                <div
                  className={`progress-step ${ativo ? 'active' : ''} ${completo ? 'complete' : ''} ${clicavel ? 'clicavel' : ''} ${futuro && clicavel ? 'desbloqueado' : ''}`}
                  onClick={() => clicavel && navegarParaBarra(step.num)}
                  title={clicavel
                    ? (futuro
                      ? (isEn ? `Go to ${step.nome}` : `Ir para ${step.nome}`)
                      : (isEn ? `Back to ${step.nome}` : `Voltar para ${step.nome}`))
                    : ''}
                >
                  <div className="step-circle">{completo ? '✓' : step.num}</div>
                  <span className="step-name">{step.nome}</span>
                </div>
                {idx < 3 && <div className={`progress-line ${completo ? 'complete' : ''}`} />}
              </div>
            );
          })}
        </div>
        )}

        {telaAtual === 'app' && !isGlobalAdminContext && (
          <div className="content-card">
            {etapa === 1 && renderEtapa1()}
            {etapa === 2 && renderEtapa2()}
            {etapa === 3 && renderEtapa3()}
            {etapa === 4 && renderEtapa4()}
          </div>
        )}

        {telaAtual === 'app' && isGlobalAdminContext && (
          <div className="content-card">
            {renderGlobalAdminPanel()}
          </div>
        )}

        {!isGlobalAdminContext && telaAtual === 'usuarios' && renderUserManagement()}
        {isGlobalAdminContext && telaAtual === 'global-usuarios' && renderUserManagement()}
        {!isGlobalAdminContext && telaAtual === 'creditos' && renderCredits()}
        {!isGlobalAdminContext && telaAtual === 'faq' && renderFaq()}
        {!isGlobalAdminContext && telaAtual === 'biblioteca' && renderBiblioteca()}
        {!isGlobalAdminContext && telaAtual === 'analise-detalhe' && renderAnaliseDetalhe()}

        <footer className="app-footer">
          <span>{t('footerTitle')}</span>
          {candidatos.length > 0 && <span>{t('footerCandidates', { count: candidatos.length, plural: candidatos.length > 1 ? 's' : '', cargo })}</span>}
        </footer>
      </div>
      {renderModalSalvar()}
      {renderModalProjecaoNarrativa()}
      {renderModalEditarDimensao()}
      {renderModalComparativa()}
      {renderModalContradiction()}
      {modalDeletarCandidato && (
        <div className="modal-overlay" onClick={() => setModalDeletarCandidato(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-icon">🗑️</div>
            <h3>{t('deleteCandidateModalTitle')}</h3>
            {modalDeletarCandidato.temComparativa ? (
              <p>
                {t('deleteCandidateWithComparative', { name: modalDeletarCandidato.nome })}
              </p>
            ) : (
              <p>
                {t('deleteCandidateNoComparative', { name: modalDeletarCandidato.nome })}
              </p>
            )}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalDeletarCandidato(null)}>
                {t('cancel')}
              </button>
              <button className="btn-danger" onClick={confirmarDeletarCandidato}>
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalConfirmarEdicao && (
        <div className="modal-overlay" onClick={() => setModalConfirmarEdicao(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-icon">⚠️</div>
            <h3>{t('editPrinciplesTitle')}</h3>
            {modalConfirmarEdicao.etapaDestino === 1 ? (
              <p>
                {t('editPrinciplesWarning')}
              </p>
            ) : (
              <p>
                {t('editPositionWarning')}
              </p>
            )}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalConfirmarEdicao(null)}>
                {t('cancel')}
              </button>
              <button className="btn-primary" onClick={confirmarEdicao}>
                {t('confirmEdit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
